
import React, { useState, useMemo, useEffect, useRef } from 'react';

// ── Feature flag — set to true to enable MaxPreps export for all users ───────
const ENABLE_MAXPREPS_EXPORT = false;
import { createPortal } from 'react-dom';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, LabelList, LineChart, Line, CartesianGrid, ReferenceLine } from 'recharts';
import { Player, Team, GameEvent, StatRecord, Organization, MatchRecord, UserProfile, GameState, HudlTeamReport, isGameType, AppSettings, DEFAULT_STAT_THRESHOLDS, StatThresholds, STAFF_ROLES } from '../types';
import { Download, Search, Calendar, Filter, HelpCircle, Info, ArrowUpDown, ChevronDown, ChevronUp, Edit3, Save, X, FilePlus, Camera, Upload, Sparkles, Loader2, RefreshCw, Trophy, Plus, ArrowRight, Check, Target, TrendingUp, Activity, BarChart2, AlertTriangle, Database, Trash2, Star, Users } from 'lucide-react';
import { supabase, mapEventFromDB, insertMatchRecord, setMatchOfRecord } from '../services/supabase';
import { formatDate } from '../lib/dates';
import RecordDiffViewer from './RecordDiffViewer';
import { extractStatsFromImage, ExtractedStats, ExtractStatsResult } from '../services/geminiService';

interface StatsProps {
  players: Player[];
  teams: Team[];
  events: GameEvent[];
  matchRecords?: MatchRecord[];
  setMatchRecords?: React.Dispatch<React.SetStateAction<MatchRecord[]>>;
  currentOrg?: Organization | null;
  currentUser?: UserProfile | null;
  setEvents?: React.Dispatch<React.SetStateAction<GameEvent[]>>;
  initialEventId?: string;
  onEditingChange?: (editing: boolean) => void;
  registerLeaveRequest?: (handler: (targetView: number) => void) => void;
  changeView?: (view: number) => void;
  appSettings?: AppSettings;
  activeTeamId?: string;
}

type StatCategory = 'Summary' | 'Attacking' | 'Serving' | 'Blocking' | 'Digging' | 'Setting' | 'Serve Receiving';

interface CalculatedStats {
  playerId: string;
  matchesPlayed: number; // Matches (events) played
  gamesPlayed: number; // Sets played

  // Attacking
  kills: number;
  attackAttempts: number;
  attackErrors: number;

  // Serving
  aces: number;
  serveAttempts: number;
  serveErrors: number;
  rotationPoints: number;
  serverPoints: number;   // Points won while THIS player was the server (for PS%)

  // Blocking
  blockSolo: number;
  blockAssist: number;
  blockErrors: number;

  // Digging
  digs: number;
  digErrors: number;

  // Ball Handling / Setting
  assists: number;
  ballHandlingAttempts: number;
  ballHandlingErrors: number;

  // Receiving
  receptions: number;
  receptionErrors: number;
  receptionSum: number; // Sum of pass ratings
  srPerfect: number;   // Count of perfect passes (passRating 3)
  srGood: number;      // Count of good passes (passRating 2)
  srPoor: number;      // Count of poor passes (passRating 1)
}

interface SRBucket {
  p3: number; // perfect pass
  p2: number; // good pass
  p1: number; // poor pass
  p0: number; // zero rating (received, rated 0)
  errors: number; // reception errors (aces against)
}

interface PlayerChartData {
  srByContact: Partial<Record<'hands' | 'platform', SRBucket>>;
  srByStyle: Partial<Record<'float' | 'topspin', SRBucket>>;
  serveByType: Record<string, { attempts: number; aces: number; errors: number }>;
  attackErrorsByType: Record<string, number>;
}

const ERR_TYPE_ORDER = ['out', 'net', 'blocked', 'lift', 'double', 'other'] as const;
const ERR_TYPE_LABELS: Record<string, string> = { out: 'Out', net: 'Net', blocked: 'Blocked', lift: 'Lift', double: 'Double', other: 'Other' };
const ERR_TYPE_COLORS: Record<string, string> = { out: '#ef4444', net: '#2563eb', blocked: '#10b981', lift: '#f97316', double: '#7c3aed', other: '#94a3b8' };

interface TeamMetrics {
  // Macro
  sideoutPct: number | null;    // null = no phase data (manual entry)
  pointScoringPct: number | null; // null = no break-point data (manual entry)
  // Micro
  serveReceiveAvg: number;
  hittingEfficiency: number;
  killPct: number;
  aceToErrorRatio: number;
  servePct: number | null;

  // Raw counts for num/denom display
  teamReceptions: number;
  teamReceptionSum: number;
  teamKills: number;
  teamErrors: number;
  teamAttempts: number;
  teamAces: number;
  teamServeErrors: number;
  totalServes: number;
  totalPointsOnServe: number;
  effectiveSideoutPoints: number;
  effectiveSideoutOpp: number;
  reportWonOnReceive: number | null;
  reportLostOnReceive: number | null;
  reportWonOnServe: number | null;
  reportLostOnServe: number | null;

  // Rotation
  rotationStats: Record<string, {
    sideoutAttempts: number;
    sideoutsWon: number;
    sideoutPct: number;
    breakPointAttempts: number;
    breakPointsWon: number;
    breakPointPct: number;
    serverPlayerIds?: string[]; // All servers sorted by serve attempts desc
    serverStats?: Record<string, { attempts: number; points: number }>; // per-player serve stats keyed by playerId
  }>;
}

// Portal-based tooltip — renders in document.body so it's never clipped by overflow containers
const ColTooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  return (
    <span
      ref={ref}
      className="inline-flex items-center gap-0.5"
      onMouseEnter={() => {
        if (ref.current) {
          const r = ref.current.getBoundingClientRect();
          setPos({ x: r.left + r.width / 2, y: r.top });
        }
      }}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && createPortal(
        <div
          style={{ position: 'fixed', left: pos.x, top: pos.y - 8, transform: 'translate(-50%, -100%)', zIndex: 9999, pointerEvents: 'none' }}
          className="px-2 py-1 bg-slate-800 text-white text-[11px] font-normal normal-case rounded-md shadow-lg whitespace-nowrap"
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </div>,
        document.body
      )}
    </span>
  );
};

const STAT_DEFINITIONS: Record<string, string> = {
  // Serving
  'ACE/G': 'Aces Per Set: Average aces per set played.',
  'ACE %': 'Ace Percentage: Total aces divided by total service attempts.',
  'SRV %': 'Serve Percentage: Serves kept in play (Attempts − Errors) / Attempts.',
  'S ATT_SERVE': 'Service Attempts: Total times player served.',
  'ACE': 'Aces: Serves that result directly in a point.',
  'SVE_SERVE': 'Service Errors: Missed serves.',
  'PTS': 'Rotation Points: Points scored by team while player was serving.',
  'PS%': 'Point Scoring Percentage: Percentage of total serves that resulted in a point for your team (aces + rally points won while serving) ÷ total serves (aces + errors + in-play). Minimum 8 serves required. Only shown when live game tracking or scoring data is available.',

  // Attacking
  'K/G': 'Kills Per Set: Average kills per set played.',
  'H %': 'Hitting Percentage: (Kills − Errors) / Total Attacks.',
  'K%': 'Kill Percentage: Kills / Total Attacks.',
  'ATT': 'Total Attacks: All attack attempts.',
  'K': 'Kills: Attacks that result in a point.',
  'E': 'Attack Errors: Attacks that go out, into net, etc.',

  // Receiving
  'SRG': 'Serve Receptions / Set: Average successful receptions per set.',
  'R': 'Receptions: Successful passes of a serve.',
  'RE': 'Reception Errors: Failed pass resulting in an ace for the opponent.',
  'AVG': 'Pass Average: Weighted quality of pass (0–3 scale).',
  'SR+': 'Perfect Passes: 3-ball — no setter adjustment needed.',
  'SR~': 'Good Passes: 2-ball — setter has options.',
  'SR-': 'Poor Passes: 1-ball — limited setter options.',
  'SR Rtg': 'Serve Receive Rating: Weighted average pass quality (0–3 scale).',

  // Blocking
  'B/G': 'Blocks Per Set: Total Blocks (Solo + Assist) / Sets.',
  'BLK': 'Total Blocks: Block Solo + Block Assist.',
  'BS': 'Block Solo: Point-scoring block by a single player.',
  'BA': 'Block Assist: Block requiring multiple players.',
  'BE': 'Block Error: Net violation or other error during block.',

  // Setting
  'AST/G': 'Assists Per Set: Average assists per set.',
  'SA_SET': 'Sets Attempted: Total sets attempted.',
  'AST': 'Assists: Sets that lead directly to a kill.',
  'SE_SET': 'Setting Error: Double contact, lift, etc.',

  // Digging
  'D/G': 'Digs Per Set: Average digs per set.',
  'D': 'Digs: Successfully passing an attacked ball.',
  'DE': 'Dig Errors: Attacked ball not kept in play.',

  // General
  'MP': 'Matches Played: Number of game events the player participated in.',
  'SP': 'Sets Played: Total sets played across all matches.',
};

const CATEGORY_KEYS: Record<string, string[]> = {
  'Summary':        ['K', 'H %', 'ACE', 'SRV %', 'PS%', 'BLK', 'D', 'AST', 'AVG'],
  'Attacking':      ['K/G', 'K', 'E', 'ATT', 'H %', 'K%'],
  'Serving':        ['ACE/G', 'ACE', 'SVE_SERVE', 'S ATT_SERVE', 'ACE %', 'SRV %', 'PS%', 'PTS'],
  'Blocking':       ['B/G', 'BLK', 'BS', 'BA', 'BE'],
  'Digging':        ['D/G', 'D', 'DE'],
  'Setting':        ['AST/G', 'AST', 'SE_SET', 'SA_SET'],
  'Serve Receiving':['SRG', 'AVG', 'R', 'RE', 'SR+', 'SR~', 'SR-', 'SR Rtg'],
};

// ─── Draft key ↔ StatType mapping ────────────────────────────────────────────
const DRAFT_KEY_TO_STAT: Record<string, StatRecord['statType']> = {
  kills: 'kill',
  attackErrors: 'attack_error',
  attackAttempts: 'attack_attempt',
  aces: 'ace',
  serveErrors: 'serve_error',
  serveAttempts: 'serve_attempt',
  blockSolo: 'block_solo',
  blockAssist: 'block_assist',
  digs: 'dig',
  digErrors: 'dig_error',
  assists: 'assist',
  ballHandlingErrors: 'ball_handling_error',
  receptions: 'reception',
  receptionErrors: 'reception_error',
};

const STAT_TO_DRAFT_KEY: Partial<Record<StatRecord['statType'], string>> = {
  kill: 'kills',
  attack_error: 'attackErrors',
  attack_attempt: 'attackAttempts',
  ace: 'aces',
  serve_error: 'serveErrors',
  serve_attempt: 'serveAttempts',
  block_solo: 'blockSolo',
  block_assist: 'blockAssist',
  dig: 'digs',
  dig_error: 'digErrors',
  assist: 'assists',
  ball_handling_error: 'ballHandlingErrors',
  reception: 'receptions',
  reception_error: 'receptionErrors',
};

const Stats: React.FC<StatsProps> = ({ players, teams, events, matchRecords = [], setMatchRecords, currentOrg, currentUser, setEvents, initialEventId, onEditingChange, registerLeaveRequest, changeView, appSettings, activeTeamId }) => {
  const T: StatThresholds = { ...DEFAULT_STAT_THRESHOLDS, ...appSettings?.statThresholds };
  const [selectedTeamId, setSelectedTeamId] = useState<string>(() => {
    if (activeTeamId) return activeTeamId;
    const clubTeams = teams.filter(t => t.type !== 'opponent');
    return clubTeams[0]?.id ?? teams[0]?.id ?? '';
  });

  const [selectedSeason, setSelectedSeason] = useState<string>(() => {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      return month >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  });

  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [gameDropdownOpen, setGameDropdownOpen] = useState(false);
  const gameDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedSetNumber, setSelectedSetNumber] = useState<number | null>(null);
  // Derived: single-event operations (records panel, import, edit, delete) only work when exactly 1 game is focused
  const selectedEventId = selectedEventIds.size === 1 ? [...selectedEventIds][0] : 'all';
  const [statsViewMode, setStatsViewMode] = useState<'team' | 'player'>('team');
  const [activeCategory, setActiveCategory] = useState<StatCategory>('Summary');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'live' | 'import' | 'manual'>('all');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerDetailTab, setPlayerDetailTab] = useState<'charts' | 'timeline'>('charts');
  const [timelineStat, setTimelineStat] = useState<string>('kills');
  const [srFilter, setSrFilter] = useState<'all' | 'hands' | 'platform' | 'float' | 'topspin'>('all');

  // ── Unified edit/add mode ──────────────────────────────────────────────────
  const [statsMode, setStatsMode] = useState<'none' | 'add_manual' | 'add_scan' | 'edit'>('none');
  const [showActionModal, setShowActionModal] = useState(false);
  // null = editing event.stats directly; string = editing a match record's gameState.stats
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  // Which record to edit when multiple exist (shown in action modal)
  const [selectedEditRecordId, setSelectedEditRecordId] = useState<string | null>(null);
  const [confirmDeleteRecordId, setConfirmDeleteRecordId] = useState<string | null>(null);
  // Warning modal when editing a live-tracked (GameTime) record
  const [showLiveEditWarning, setShowLiveEditWarning] = useState(false);
  // Team stat timeline modal
  const [teamStatTimeline, setTeamStatTimeline] = useState<null | {
    key: 'passRating' | 'hitEfficiency' | 'servePct' | 'killPct' | 'aceToErrorRatio' | 'sideoutPct' | 'pointScoringPct';
    label: string;
    format: (v: number) => string;
    yTickFormat: (v: number) => string;
    green: number;
    amber: number;
    yDomain: [number, number];
  }>(null);

  // Diff viewer: pair of records to compare
  const [diffViewerRecords, setDiffViewerRecords] = useState<[MatchRecord, MatchRecord] | null>(null);
  const [diffViewerInitialSources, setDiffViewerInitialSources] = useState<Record<string, 'a' | 'b' | 'both'> | undefined>(undefined);
  // undefined = event.stats, string = match_record id
  const [pendingLiveEditId, setPendingLiveEditId] = useState<string | null | undefined>(undefined);

  const [isUploading, setIsUploading] = useState(false);
  const [localStatsDraft, setLocalStatsDraft] = useState<Record<string, Record<string, number>>>({});
  const [passRatingsInput, setPassRatingsInput] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scan review state
  const [scannedStats, setScannedStats] = useState<ExtractedStats[]>([]);
  const [scannedTeamReport, setScannedTeamReport] = useState<HudlTeamReport | null>(null);
  const [scannedSetScores, setScannedSetScores] = useState<Array<{ our: number; their: number }>>([]);
  const [manualSetsWon, setManualSetsWon] = useState<{ our: number; their: number }>({ our: 0, their: 0 });
  const [isReviewingScan, setIsReviewingScan] = useState(false);
  // Manual player override: row index → playerId for unmatched jerseys
  const [manualPlayerMap, setManualPlayerMap] = useState<Record<number, string>>({});

  const [saveSuccessToast, setSaveSuccessToast] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteEventConfirm, setShowDeleteEventConfirm] = useState(false);
  const [showDestructiveMenu, setShowDestructiveMenu] = useState(false);
  const [showCreateGameModal, setShowCreateGameModal] = useState(false);
  const [newGameForm, setNewGameForm] = useState({
    date: new Date().toISOString().split('T')[0],
    opponent: '',
    winner: 'us' as 'us' | 'them',
    scoreUs: 3,
    scoreThem: 0,
    isTournament: false,
    tournamentId: '',
    tournamentName: '',
  });

  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignRecordId, setReassignRecordId] = useState<string | null>(null); // null = event.stats
  const [reassignMode, setReassignMode] = useState<'existing' | 'new'>('existing');
  const [reassignTargetEventId, setReassignTargetEventId] = useState('');
  const [reassignNewOpponent, setReassignNewOpponent] = useState('');
  const [reassignNewDate, setReassignNewDate] = useState('');

  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [keepRotationalStats, setKeepRotationalStats] = useState<boolean | null>(null);
  const [pendingNavView, setPendingNavView] = useState<number | null>(null);
  const [hiddenContribPlayers, setHiddenContribPlayers] = useState<Set<string>>(new Set());
  const [selectedSetterFilter, setSelectedSetterFilter] = useState<string | null>(null);
  const [showAllPassPlayers, setShowAllPassPlayers] = useState(false);
  const [showAllPSPlayers, setShowAllPSPlayers] = useState(false);

  const isCoach = currentOrg?.role === 'head_coach' || currentOrg?.role === 'assistant';
  const isAdmin = currentOrg?.isAdmin === true;
  const canEdit = STAFF_ROLES.includes(currentOrg?.role || '') || isAdmin;
  const isEditing = statsMode !== 'none';

  // Identifies Hudl-style summary/total rows that should never be treated as players
  const isSummaryRow = (item: ExtractedStats) => {
    const num = (item.jerseyNumber ?? '').trim();
    const name = (item.playerName ?? '').trim();
    return /^(totals?|teams?|team totals?|squad)$/i.test(num) ||
           /^(totals?|teams?|team totals?|squad)$/i.test(name);
  };

  // Sync selectedTeamId with the global active team
  useEffect(() => {
    if (activeTeamId && activeTeamId !== selectedTeamId) {
      setSelectedTeamId(activeTeamId);
    }
  }, [activeTeamId]);

  useEffect(() => {
    // Only jump to a specific game if a real event ID was passed (not 'all' or empty)
    if (initialEventId && initialEventId !== 'all') {
      setSelectedEventIds(new Set([initialEventId]));
    }
  }, [initialEventId]);

  useEffect(() => {
    if (!gameDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (gameDropdownRef.current && !gameDropdownRef.current.contains(e.target as Node)) {
        setGameDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [gameDropdownOpen]);

  useEffect(() => { setSelectedSetNumber(null); }, [selectedEventId]);

  const toggleGameSelection = (id: string) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    const draft = localStorage.getItem('sideoutiq_stats_draft');
    if (draft) {
      try { setLocalStatsDraft(JSON.parse(draft)); } catch (e) { console.error("Failed to parse local stats draft", e); }
    }
  }, []);

  useEffect(() => {
    if (Object.keys(localStatsDraft).length > 0) {
      localStorage.setItem('sideoutiq_stats_draft', JSON.stringify(localStatsDraft));
    }
  }, [localStatsDraft]);

  // Notify parent when editing state changes (for navigation guard)
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing]);

  // Register leave-request handler so parent can trigger our leave-confirm modal
  useEffect(() => {
    registerLeaveRequest?.((targetView) => {
      setPendingNavView(targetView);
    });
  }, [registerLeaveRequest]);

  // --- 1. Season Logic ---
  const availableSeasons = useMemo(() => {
    const seasons = new Set<string>();
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const currentSeason = month >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
    seasons.add(currentSeason);
    events.forEach(e => {
        if (!e.date) return;
        const parts = e.date.split('-');
        if (parts.length < 2) return;
        const eYear = parseInt(parts[0]);
        const eMonth = parseInt(parts[1]);
        let seasonStr = eMonth >= 8 ? `${eYear}-${eYear + 1}` : `${eYear - 1}-${eYear}`;
        seasons.add(seasonStr);
    });
    return Array.from(seasons).sort().reverse();
  }, [events]);

  // --- 2. Filter Events (Season + Team) ---
  const filteredEvents = useMemo(() => {
    let matches = events.filter(e => isGameType(e.type));
    if (selectedSeason && selectedSeason !== 'all') {
        const [startYear, endYear] = selectedSeason.split('-').map(Number);
        const startDate = `${startYear}-08-01`;
        const endDate = `${endYear}-07-31`;
        matches = matches.filter(e => e.date >= startDate && e.date <= endDate);
    }
    // Exclude events with no stats data (no IS_RECORD match record with stats/scores and no inline stats)
    matches = matches.filter(e => {
      const officialRecord = matchRecords.find(r => r.eventId === e.id && r.isRecord);
      const recordStats = officialRecord?.gameState.stats;
      const hasSetScores = (officialRecord?.gameState.ourSetsWon ?? 0) > 0 || (officialRecord?.gameState.theirSetsWon ?? 0) > 0;
      return (recordStats?.length ?? 0) > 0 || (e.stats?.length ?? 0) > 0 || hasSetScores;
    });
    return matches.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [events, selectedSeason, matchRecords]);

  // --- Computed helpers for selected event ---
  const selectedEventStats = useMemo(() => {
    if (selectedEventId === 'all') return [];
    const evt = filteredEvents.find(e => e.id === selectedEventId);
    if (!evt) return [];
    const record = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
    return record?.gameState.stats ?? evt.stats ?? [];
  }, [selectedEventId, filteredEvents, matchRecords]);

  const selectedEventHasStats = selectedEventStats.length > 0;

  const selectedEventRecords = useMemo(() =>
    matchRecords.filter(r => r.eventId === selectedEventId),
    [selectedEventId, matchRecords]);

  // Parse set result from event title, e.g. "vs TeamName (W 3-1)" → {ourSets: 3, theirSets: 1, won: true}
  const parseResultFromTitle = (evt: GameEvent): { ourSets: number; theirSets: number; won: boolean } | null => {
    const m = evt.title?.match(/\(([WL])\s+(\d+)-(\d+)\)/i);
    if (!m) return null;
    const won = m[1].toUpperCase() === 'W';
    const a = parseInt(m[2]), b = parseInt(m[3]);
    return { ourSets: won ? a : b, theirSets: won ? b : a, won };
  };

  // Resolve win/loss for any event: match record sets → title fallback
  const resolveEventResult = (evt: GameEvent): 'W' | 'L' | 'T' | null => {
    const record = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
    if (record) {
      const { ourSetsWon, theirSetsWon } = record.gameState;
      if (ourSetsWon > theirSetsWon) return 'W';
      if (theirSetsWon > ourSetsWon) return 'L';
      if (ourSetsWon > 0 && ourSetsWon === theirSetsWon) return 'T';
    }
    // Fallback: parse from event title
    const parsed = parseResultFromTitle(evt);
    if (parsed) return parsed.won ? 'W' : 'L';
    return null;
  };

  // Team W-L record computed from filtered events (season + team filter applied)
  const teamRecord = useMemo(() => {
    let wins = 0, losses = 0, ties = 0;
    filteredEvents.forEach(evt => {
      const result = resolveEventResult(evt);
      if (result === 'W') wins++;
      else if (result === 'L') losses++;
      else if (result === 'T') ties++;
    });
    return { wins, losses, ties };
  }, [filteredEvents, matchRecords]);

  // Individual match score data when a single event is selected
  const selectedMatchScore = useMemo(() => {
    if (selectedEventId === 'all') return null;
    const evt = filteredEvents.find(e => e.id === selectedEventId);
    const record = matchRecords.find(r => r.eventId === selectedEventId && r.isRecord);
    if (!record) return null;
    const { ourSetsWon, theirSetsWon, setHistory } = record.gameState;
    // If sets are tracked, derive the true match result from setHistory by scanning
    // until one team accumulates enough wins to end the match. This corrects stored
    // ourSetsWon/theirSetsWon values that may be inflated by the duplicate-set bug.
    if ((ourSetsWon > 0 || theirSetsWon > 0) && setHistory && setHistory.length > 0) {
      // Scan for first team to reach 3 wins (BO5), then fall back to 2 wins (BO3).
      // Use a `found` flag so that a decisive 5th set (matchEnd == setHistory.length)
      // still commits to threshold=3 instead of falling through to the BO3 pass.
      let matchEnd = setHistory.length;
      for (const threshold of [3, 2]) {
        let w1 = 0, w2 = 0, found = false;
        for (let i = 0; i < setHistory.length; i++) {
          const s = setHistory[i];
          if (s.our > s.their) w1++; else w2++;
          if (w1 >= threshold || w2 >= threshold) { matchEnd = i + 1; found = true; break; }
        }
        if (found) break;
      }
      const trimmedHistory = setHistory.slice(0, matchEnd);
      const correctedOur   = trimmedHistory.filter(s => s.our > s.their).length;
      const correctedTheir = trimmedHistory.filter(s => s.their > s.our).length;
      return { ourSetsWon: correctedOur, theirSetsWon: correctedTheir, setHistory: trimmedHistory };
    }
    if (ourSetsWon > 0 || theirSetsWon > 0) {
      return { ourSetsWon, theirSetsWon, setHistory: [] };
    }
    if (evt) {
      const parsed = parseResultFromTitle(evt);
      if (parsed) return { ourSetsWon: parsed.ourSets, theirSetsWon: parsed.theirSets, setHistory: [] };
    }
    return null;
  }, [selectedEventId, filteredEvents, matchRecords]);

  // --- 3. Aggregate Data ---
  const { playerStats, teamMetrics, advancedMetrics, playerChartData, hasLiveTrackingData, hasServerPointData } = useMemo(() => {
    const statsMap = new Map<string, CalculatedStats>();

    // Team Metrics Aggregators
    let totalServes = 0;
    let totalPointsOnServe = 0; // Break Points (aces + serve-phase wins) — merged post-loop
    let bpPointsOnServe = 0;   // Serve-phase wins from break_point/rotation_point (authoritative)
    let killPointsOnServe = 0; // Serve-phase wins from phase-tagged kills/blocks (fallback only)
    let totalSideoutOpportunities = 0; // Receptions + Reception Errors (exact, when tracked)
    let bpReceiveTerminal = 0;    // Terminal receive-phase events from break_point/rotation_point (authoritative)
    let bpSideoutPoints = 0;    // Points won in 'receive' phase from break_point/rotation_point (authoritative)
    let killReceiveTerminal = 0;  // Terminal receive-phase events from phase-tagged kills/errors (fallback only)
    let killSideoutPoints = 0;    // Points won in 'receive' phase from phase-tagged kills (fallback only)
    // Season-level sideout accumulators built up per-event so legacy and modern games
    // each get the right denominator before being summed.
    let totalEffectiveSideoutOpp = 0;
    let totalEffectiveSideoutPoints = 0;
    // True only when at least one stat carries live-tracking context (phase / break_point).
    // Manual box-score entry never sets these, so the phase-dependent metrics would
    // incorrectly read 0% instead of "not enough data".
    let hasLiveTrackingData = false;
    // True when at least one server_point stat exists — enables correct per-player PS%.
    let hasServerPointData = false;

    // Hudl Match Report aggregators (from teamReport on GameState)
    let reportWonOnServe: number | null = null;
    let reportLostOnServe: number | null = null;
    let reportWonOnReceive: number | null = null;
    let reportLostOnReceive: number | null = null;

    let teamKills = 0;
    let teamErrors = 0;
    let teamAttempts = 0;

    let teamAces = 0;
    let teamServeErrors = 0;

    let teamReceptions = 0;
    let teamReceptionSum = 0;

    // Advanced serve breakdown by type (only populated when !simpleStats)
    const serveByType: Record<string, { attempts: number; aces: number; errors: number }> = {};
    // Attack error type breakdown
    const attackErrorsByType: Record<string, number> = {};
    // Kill detail breakdowns
    const killHitTypeCounts: Record<string, number> = {};
    const killOutcomeCounts: Record<string, number> = {};
    // Advanced reception breakdown
    const recByServeStyle: Record<string, { count: number; ratingSum: number; errors: number }> = {};
    const recByContactType: Record<string, { count: number; ratingSum: number; errors: number }> = {};
    // Intersection: serve style × contact type
    const recByStyleAndContact: Record<string, Record<string, { count: number; ratingSum: number; errors: number }>> = {};

    const rotationData: Record<string, { attempts: number, successes: number, receiveTotal: number, killReceiveAttempts: number, killReceiveSuccesses: number, bpAttempts: number, bpSuccesses: number, effectiveAttempts: number, servers: Record<string, { attempts: number, points: number }> }> = {
       "1": { attempts: 0, successes: 0, receiveTotal: 0, killReceiveAttempts: 0, killReceiveSuccesses: 0, bpAttempts: 0, bpSuccesses: 0, effectiveAttempts: 0, servers: {} },
       "2": { attempts: 0, successes: 0, receiveTotal: 0, killReceiveAttempts: 0, killReceiveSuccesses: 0, bpAttempts: 0, bpSuccesses: 0, effectiveAttempts: 0, servers: {} },
       "3": { attempts: 0, successes: 0, receiveTotal: 0, killReceiveAttempts: 0, killReceiveSuccesses: 0, bpAttempts: 0, bpSuccesses: 0, effectiveAttempts: 0, servers: {} },
       "4": { attempts: 0, successes: 0, receiveTotal: 0, killReceiveAttempts: 0, killReceiveSuccesses: 0, bpAttempts: 0, bpSuccesses: 0, effectiveAttempts: 0, servers: {} },
       "5": { attempts: 0, successes: 0, receiveTotal: 0, killReceiveAttempts: 0, killReceiveSuccesses: 0, bpAttempts: 0, bpSuccesses: 0, effectiveAttempts: 0, servers: {} },
       "6": { attempts: 0, successes: 0, receiveTotal: 0, killReceiveAttempts: 0, killReceiveSuccesses: 0, bpAttempts: 0, bpSuccesses: 0, effectiveAttempts: 0, servers: {} },
    };

    // Per-player chart data (SR breakdowns by contact/style, serve by type)
    const playerChartMap = new Map<string, PlayerChartData>();
    const mkSRBucket = (): SRBucket => ({ p3: 0, p2: 0, p1: 0, p0: 0, errors: 0 });

    // IDs of players on the currently-selected team — used to gate rotation server tracking
    // so players from other teams (or opponents) never appear as servers in the rotation cards.
    const teamPlayerIds = new Set(
        players.filter(p => selectedTeamId === 'all' || p.teamId === selectedTeamId).map(p => p.id)
    );

    // Initialize all players
    players.forEach(p => {
        statsMap.set(p.id, {
            playerId: p.id,
            matchesPlayed: 0,
            gamesPlayed: 0,
            kills: 0, attackAttempts: 0, attackErrors: 0,
            aces: 0, serveAttempts: 0, serveErrors: 0, rotationPoints: 0, serverPoints: 0,
            blockSolo: 0, blockAssist: 0, blockErrors: 0,
            digs: 0, digErrors: 0,
            assists: 0, ballHandlingAttempts: 0, ballHandlingErrors: 0,
            receptions: 0, receptionErrors: 0, receptionSum: 0,
            srPerfect: 0, srGood: 0, srPoor: 0
        });
        playerChartMap.set(p.id, { srByContact: {}, srByStyle: {}, serveByType: {}, attackErrorsByType: {} });
    });

    let eventsToProcess = selectedEventIds.size === 0
       ? filteredEvents
       : filteredEvents.filter(e => selectedEventIds.has(e.id));
    // For a specific team selection, only process events belonging to that team.
    // Events without a teamId fall through and are filtered at the record level below.
    if (selectedTeamId !== 'all') {
      eventsToProcess = eventsToProcess.filter(e => !e.teamId || e.teamId === selectedTeamId);
    }

    eventsToProcess.forEach(evt => {
        // Gather all stats for this event from both event.stats and match_records.
        // When source filter is active, we need to see ALL stats across all records
        // so that e.g. an import saved as a match_record is visible under the Import filter.
        // When no source filter, prefer the official match record (original behavior).
        let statsToProcess: StatRecord[];
        if (sourceFilter !== 'all') {
          // Collect from all sources so the filter can find stats regardless of where they're stored
          const allStats: StatRecord[] = [...(evt.stats ?? [])];
          matchRecords.filter(r => r.eventId === evt.id).forEach(r => {
            if (r.gameState.stats?.length) allStats.push(...r.gameState.stats);
          });
          statsToProcess = allStats;
        } else {
          // Default: prefer the official match record; fall back to inline evt.stats
          const recordStats = matchRecords.find(r => r.eventId === evt.id && r.isRecord)?.gameState.stats;
          statsToProcess = recordStats?.length ? recordStats : (evt.stats ?? []);
        }
        if (!statsToProcess || statsToProcess.length === 0) return;

        // Apply source filter — 'live' includes records with no source (pre-existing live tracking)
        const sourceFiltered = sourceFilter === 'all'
          ? statsToProcess
          : statsToProcess.filter(r => {
              if (sourceFilter === 'live') return !r.source || r.source === 'live';
              return r.source === sourceFilter;
            });
        if (sourceFiltered.length === 0) return;

        // Apply per-set filter when a single match is selected and a specific set is chosen
        const setFiltered = (selectedSetNumber !== null && selectedEventId !== 'all')
          ? sourceFiltered.filter(r => r.setNumber === selectedSetNumber)
          : sourceFiltered;
        if (setFiltered.length === 0) return;

        // If this event's stats include any phase-tagged serve records (new format),
        // skip legacy no-phase serve stats to prevent double-counting the denominator.
        const hasPhaseTaggedServe = setFiltered.some(
          s => s.phase === 'serve' && (s.statType === 'ace' || s.statType === 'serve_attempt' || s.statType === 'serve_error')
        );
        // True when this event has phase-tagged break_point records. When so, phaseless
        // rotation_point records are skipped in bpSuccesses to avoid double-counting
        // (stepper writes a rotation_point AND a break_point[phase=serve] for the same rally).
        const hasPhasedBreakPoints = setFiltered.some(
          s => s.phase !== undefined && (s.statType === 'break_point' || s.statType === 'rotation_point')
        );

        const playerSetsInMatch = new Map<string, Set<number>>();
        // Tracks the last confirmed server per rotation so serve-phase break_point stats
        // (which use the scorer's playerId, not the server's) can still credit the right server.
        const lastServerByRotation: Record<string, string> = {};
        // Per-event sideout counters — used to compute this event's effective sideout
        // denominator independently, then accumulate into the season totals below.
        let evtBpSideoutPoints = 0;
        let evtBpReceiveTerminal = 0;
        let evtKillSideoutPoints = 0;
        let evtSideoutOpportunities = 0;
        // Per-event, per-rotation sideout counters for the same reason.
        const evtRotAttempts: Record<string, number> = {};
        const evtRotSuccesses: Record<string, number> = {};
        const evtRotReceiveTotal: Record<string, number> = {};
        const evtRotKillSuccesses: Record<string, number> = {};

        setFiltered.forEach(record => {
            // Skip stats that belong to a different team's players.
            // playerId='team' records (stepper break_point/rotation_point) are always included.
            if (selectedTeamId !== 'all' && record.playerId !== 'team' && !teamPlayerIds.has(record.playerId)) return;

            // --- Team-level & Rotation stats (always runs, including for substitute players) ---
            // Skip legacy no-phase serve stats when phase-tagged stats exist for this event
            const countServe = !hasPhaseTaggedServe || record.phase === 'serve';
            switch(record.statType) {
                case 'kill': teamKills++; teamAttempts++; { const ht = (record as any).killHitType; if (ht) killHitTypeCounts[ht] = (killHitTypeCounts[ht] ?? 0) + 1; const oc = (record as any).killOutcome; if (oc) killOutcomeCounts[oc] = (killOutcomeCounts[oc] ?? 0) + 1; } break;
                case 'attack_error': teamErrors++; teamAttempts++; { const et = (record as any).attackErrorType; if (et) attackErrorsByType[et] = (attackErrorsByType[et] ?? 0) + 1; } break;
                case 'attack_attempt': teamAttempts++; break;
                case 'ace': if (!countServe) break; teamAces++; totalServes++; totalPointsOnServe++;
                  // Aces count as both a serve attempt and a point won for rotation BP%.
                  // No companion break_point stat is recorded for aces (doing so would double-count them
                  // in the team-level totalPointsOnServe), so we credit both here directly.
                  if (record.rotation && rotationData[record.rotation]) {
                    rotationData[record.rotation].bpAttempts++;
                    rotationData[record.rotation].bpSuccesses++;
                    if (teamPlayerIds.has(record.playerId)) {
                      const sv = rotationData[record.rotation].servers;
                      if (!sv[record.playerId]) sv[record.playerId] = { attempts: 0, points: 0 };
                      sv[record.playerId].attempts++;
                      sv[record.playerId].points++; // ace = point scored
                      lastServerByRotation[record.rotation] = record.playerId;
                    }
                  }
                  if (record.serveType) {
                    if (!serveByType[record.serveType]) serveByType[record.serveType] = { attempts: 0, aces: 0, errors: 0 };
                    serveByType[record.serveType].attempts++;
                    serveByType[record.serveType].aces++;
                  }
                  break;
                case 'serve_error': if (!countServe) break; teamServeErrors++; totalServes++;
                  if (record.rotation && rotationData[record.rotation]) {
                    rotationData[record.rotation].bpAttempts++;
                    if (teamPlayerIds.has(record.playerId)) {
                      const sv = rotationData[record.rotation].servers;
                      if (!sv[record.playerId]) sv[record.playerId] = { attempts: 0, points: 0 };
                      sv[record.playerId].attempts++;
                      lastServerByRotation[record.rotation] = record.playerId;
                    }
                  }
                  if (record.serveType) {
                    if (!serveByType[record.serveType]) serveByType[record.serveType] = { attempts: 0, aces: 0, errors: 0 };
                    serveByType[record.serveType].attempts++;
                    serveByType[record.serveType].errors++;
                  }
                  break;
                case 'serve_attempt': if (!countServe) break; totalServes++;
                  if (record.rotation && rotationData[record.rotation]) {
                    rotationData[record.rotation].bpAttempts++;
                    if (teamPlayerIds.has(record.playerId)) {
                      const sv = rotationData[record.rotation].servers;
                      if (!sv[record.playerId]) sv[record.playerId] = { attempts: 0, points: 0 };
                      sv[record.playerId].attempts++;
                      lastServerByRotation[record.rotation] = record.playerId;
                    }
                  }
                  if (record.serveType) {
                    if (!serveByType[record.serveType]) serveByType[record.serveType] = { attempts: 0, aces: 0, errors: 0 };
                    serveByType[record.serveType].attempts++;
                  }
                  break;
                case 'rotation_point':
                case 'break_point': hasLiveTrackingData = true;
                  if (record.phase === 'receive') {
                    // When a stat button records an attack_error or reception_error while we are
                    // receiving, GameTime writes a break_point for the OPPONENT server (not our
                    // team). That means the opponent scored — it is a sideout FAILURE, not a
                    // success. The stepper uses playerId 'team' for both success and failure
                    // (distinguished by the receive_failure flag). Only treat a receive-phase
                    // break_point as a sideout success when it belongs to our team.
                    const isOurPoint = record.playerId === 'team' || teamPlayerIds.has(record.playerId);
                    if (record.flags?.includes('receive_failure') || !isOurPoint) {
                      // Opponent scored while we were receiving — count denominator only.
                      bpReceiveTerminal++; evtBpReceiveTerminal++;
                      if (record.rotation && rotationData[record.rotation]) {
                        rotationData[record.rotation].attempts++;
                        evtRotAttempts[record.rotation] = (evtRotAttempts[record.rotation] ?? 0) + 1;
                      }
                    } else {
                      // We scored while receiving — sideout success (authoritative).
                      bpSideoutPoints++; evtBpSideoutPoints++;
                      bpReceiveTerminal++; evtBpReceiveTerminal++;
                      if (record.rotation && rotationData[record.rotation]) {
                        rotationData[record.rotation].successes++;
                        rotationData[record.rotation].attempts++;
                        evtRotSuccesses[record.rotation] = (evtRotSuccesses[record.rotation] ?? 0) + 1;
                        evtRotAttempts[record.rotation] = (evtRotAttempts[record.rotation] ?? 0) + 1;
                      }
                    }
                  } else if (record.phase === 'serve' || !hasPhasedBreakPoints) {
                    // Serve phase win, or legacy record from an event without phase data.
                    // Skip phaseless records when phase-tagged break_point data exists to avoid
                    // double-counting rotation_point + break_point[serve] pairs from the same rally.
                    bpPointsOnServe++;
                    if (record.rotation && rotationData[record.rotation]) {
                      rotationData[record.rotation].bpSuccesses++;
                      // Credit the actual server: use record.playerId if they're in the server map,
                      // otherwise fall back to the last confirmed server in this rotation.
                      const sv = rotationData[record.rotation].servers;
                      const creditId = sv[record.playerId] ? record.playerId : lastServerByRotation[record.rotation];
                      if (creditId && sv[creditId]) sv[creditId].points++;
                    }
                  }
                  break;
                case 'server_point': hasServerPointData = true; break;
                case 'reception': totalSideoutOpportunities++; evtSideoutOpportunities++; teamReceptions++;
                  if (record.rotation && rotationData[record.rotation]) {
                    rotationData[record.rotation].receiveTotal++;
                    evtRotReceiveTotal[record.rotation] = (evtRotReceiveTotal[record.rotation] ?? 0) + 1;
                  }
                  if (record.passRating !== undefined) teamReceptionSum += record.passRating;
                  if (record.incomingServeStyle) {
                    if (!recByServeStyle[record.incomingServeStyle]) recByServeStyle[record.incomingServeStyle] = { count: 0, ratingSum: 0, errors: 0 };
                    recByServeStyle[record.incomingServeStyle].count++;
                    if (record.passRating !== undefined) recByServeStyle[record.incomingServeStyle].ratingSum += record.passRating;
                  }
                  if (record.contactType) {
                    if (!recByContactType[record.contactType]) recByContactType[record.contactType] = { count: 0, ratingSum: 0, errors: 0 };
                    recByContactType[record.contactType].count++;
                    if (record.passRating !== undefined) recByContactType[record.contactType].ratingSum += record.passRating;
                  }
                  if (record.incomingServeStyle && record.contactType) {
                    if (!recByStyleAndContact[record.incomingServeStyle]) recByStyleAndContact[record.incomingServeStyle] = {};
                    if (!recByStyleAndContact[record.incomingServeStyle][record.contactType]) recByStyleAndContact[record.incomingServeStyle][record.contactType] = { count: 0, ratingSum: 0, errors: 0 };
                    recByStyleAndContact[record.incomingServeStyle][record.contactType].count++;
                    if (record.passRating !== undefined) recByStyleAndContact[record.incomingServeStyle][record.contactType].ratingSum += record.passRating;
                  }
                  break;
                case 'reception_error': totalSideoutOpportunities++; evtSideoutOpportunities++; teamReceptions++;
                  if (record.rotation && rotationData[record.rotation]) {
                    rotationData[record.rotation].receiveTotal++;
                    evtRotReceiveTotal[record.rotation] = (evtRotReceiveTotal[record.rotation] ?? 0) + 1;
                  }
                  if (record.incomingServeStyle) {
                    if (!recByServeStyle[record.incomingServeStyle]) recByServeStyle[record.incomingServeStyle] = { count: 0, ratingSum: 0, errors: 0 };
                    recByServeStyle[record.incomingServeStyle].count++;
                    recByServeStyle[record.incomingServeStyle].errors++;
                  }
                  if (record.contactType) {
                    if (!recByContactType[record.contactType]) recByContactType[record.contactType] = { count: 0, ratingSum: 0, errors: 0 };
                    recByContactType[record.contactType].count++;
                    recByContactType[record.contactType].errors++;
                  }
                  if (record.incomingServeStyle && record.contactType) {
                    if (!recByStyleAndContact[record.incomingServeStyle]) recByStyleAndContact[record.incomingServeStyle] = {};
                    if (!recByStyleAndContact[record.incomingServeStyle][record.contactType]) recByStyleAndContact[record.incomingServeStyle][record.contactType] = { count: 0, ratingSum: 0, errors: 0 };
                    recByStyleAndContact[record.incomingServeStyle][record.contactType].count++;
                    recByStyleAndContact[record.incomingServeStyle][record.contactType].errors++;
                  }
                  break;
            }

            // Phase-based team metrics (always runs)
            if (record.phase !== undefined) hasLiveTrackingData = true;
            if (record.phase === 'serve') {
                // Track kill/block serve-phase wins separately as a fallback counter.
                // These are NOT added to totalPointsOnServe directly because the companion
                // break_point stat (recorded by GameTime for the same rally) is the authoritative
                // source. If we added to totalPointsOnServe here AND in the break_point case,
                // every serve-phase kill would be double-counted. Instead, bpPointsOnServe wins
                // when break_point data is present; killPointsOnServe is the fallback for legacy
                // games that have phase-tagged kills but no break_point stats.
                if (record.statType === 'kill' || record.statType === 'block_solo' || record.statType === 'block_assist') {
                    killPointsOnServe++;
                }
            }
            if (record.phase === 'receive') {
                if (record.statType === 'kill' || record.statType === 'block_solo' || record.statType === 'block_assist') {
                    // Fallback only — used when no break_point/rotation_point stats exist for this game.
                    killSideoutPoints++; evtKillSideoutPoints++;
                    killReceiveTerminal++;
                    if (record.rotation) {
                        rotationData[record.rotation].killReceiveSuccesses++;
                        rotationData[record.rotation].killReceiveAttempts++;
                        evtRotKillSuccesses[record.rotation] = (evtRotKillSuccesses[record.rotation] ?? 0) + 1;
                    }
                }
                // Receive-phase losses also count as terminal receive events (fallback)
                if (record.statType === 'attack_error' || record.statType === 'reception_error') {
                    killReceiveTerminal++;
                    if (record.rotation) {
                        rotationData[record.rotation].killReceiveAttempts++;
                    }
                }
            }

            // --- Player-level stats (skipped for substitutes not in the roster) ---
            const pStats = statsMap.get(record.playerId);
            if (!pStats) return;
            const pChart = playerChartMap.get(record.playerId);

            // Track Sets Played
            if (record.setNumber) {
                if (!playerSetsInMatch.has(record.playerId)) playerSetsInMatch.set(record.playerId, new Set());
                playerSetsInMatch.get(record.playerId)?.add(record.setNumber);
            }

            // --- Individual Player Stats ---
            switch(record.statType) {
                case 'kill': pStats.kills++; pStats.attackAttempts++; break;
                case 'attack_error': pStats.attackErrors++; pStats.attackAttempts++; if (pChart) { const et = (record as any).attackErrorType; if (et) pChart.attackErrorsByType[et] = (pChart.attackErrorsByType[et] ?? 0) + 1; } break;
                case 'attack_attempt': pStats.attackAttempts++; break;
                case 'ace': if (!countServe) break; pStats.aces++; pStats.serveAttempts++;
                  if (record.serveType && pChart) {
                    if (!pChart.serveByType[record.serveType]) pChart.serveByType[record.serveType] = { attempts: 0, aces: 0, errors: 0 };
                    pChart.serveByType[record.serveType].attempts++;
                    pChart.serveByType[record.serveType].aces++;
                  }
                  break;
                case 'serve_error': if (!countServe) break; pStats.serveErrors++; pStats.serveAttempts++;
                  if (record.serveType && pChart) {
                    if (!pChart.serveByType[record.serveType]) pChart.serveByType[record.serveType] = { attempts: 0, aces: 0, errors: 0 };
                    pChart.serveByType[record.serveType].attempts++;
                    pChart.serveByType[record.serveType].errors++;
                  }
                  break;
                case 'serve_attempt': if (!countServe) break; pStats.serveAttempts++;
                  if (record.serveType && pChart) {
                    if (!pChart.serveByType[record.serveType]) pChart.serveByType[record.serveType] = { attempts: 0, aces: 0, errors: 0 };
                    pChart.serveByType[record.serveType].attempts++;
                  }
                  break;
                case 'rotation_point': pStats.rotationPoints++; break;
                case 'break_point': if (record.phase !== 'receive') pStats.rotationPoints++; break;
                case 'server_point': pStats.serverPoints++; break;
                case 'block_solo': pStats.blockSolo++; break;
                case 'block_assist': pStats.blockAssist++; break;
                case 'block_error': pStats.blockErrors++; break;
                case 'dig': pStats.digs++; break;
                case 'dig_error': pStats.digErrors++; break;
                case 'assist': pStats.assists++; pStats.ballHandlingAttempts++; break;
                case 'ball_handling_error': pStats.ballHandlingErrors++; pStats.ballHandlingAttempts++; break;
                case 'reception':
                    pStats.receptions++;
                    if (record.passRating !== undefined) {
                        pStats.receptionSum += record.passRating;
                        if (record.passRating === 3) pStats.srPerfect++;
                        else if (record.passRating === 2) pStats.srGood++;
                        else if (record.passRating === 1) pStats.srPoor++;
                    }
                    if (record.incomingServeStyle && pChart && (record.incomingServeStyle === 'float' || record.incomingServeStyle === 'topspin')) {
                        if (!pChart.srByStyle[record.incomingServeStyle]) pChart.srByStyle[record.incomingServeStyle] = mkSRBucket();
                        const b = pChart.srByStyle[record.incomingServeStyle]!;
                        if (record.passRating === 3) b.p3++; else if (record.passRating === 2) b.p2++; else if (record.passRating === 1) b.p1++; else b.p0++;
                    }
                    if (record.contactType && pChart && (record.contactType === 'hands' || record.contactType === 'platform')) {
                        if (!pChart.srByContact[record.contactType]) pChart.srByContact[record.contactType] = mkSRBucket();
                        const b = pChart.srByContact[record.contactType]!;
                        if (record.passRating === 3) b.p3++; else if (record.passRating === 2) b.p2++; else if (record.passRating === 1) b.p1++; else b.p0++;
                    }
                    break;
                case 'reception_error':
                    pStats.receptionErrors++;
                    // Important: An error counts as a reception attempt (value 0) for the average
                    pStats.receptions++;
                    if (record.incomingServeStyle && pChart && (record.incomingServeStyle === 'float' || record.incomingServeStyle === 'topspin')) {
                        if (!pChart.srByStyle[record.incomingServeStyle]) pChart.srByStyle[record.incomingServeStyle] = mkSRBucket();
                        pChart.srByStyle[record.incomingServeStyle]!.errors++;
                    }
                    if (record.contactType && pChart && (record.contactType === 'hands' || record.contactType === 'platform')) {
                        if (!pChart.srByContact[record.contactType]) pChart.srByContact[record.contactType] = mkSRBucket();
                        pChart.srByContact[record.contactType]!.errors++;
                    }
                    break;
            }
        });

        playerSetsInMatch.forEach((sets, playerId) => {
            const pStats = statsMap.get(playerId);
            if (pStats) pStats.gamesPlayed += sets.size;
        });

        // For imported/scanned stats that carry no setNumber (e.g. Hudl box-score import),
        // count the whole event as 1 game for any player who had records but wasn't
        // already tracked by set number above.
        const playersWithAnyRecord = new Set<string>();
        sourceFiltered.forEach(record => {
            if (statsMap.has(record.playerId)) playersWithAnyRecord.add(record.playerId);
        });
        playersWithAnyRecord.forEach(playerId => {
            if (!playerSetsInMatch.has(playerId)) {
                const pStats = statsMap.get(playerId);
                if (pStats) pStats.gamesPlayed += 1;
            }
            // Count matches (events) played
            const pStats = statsMap.get(playerId);
            if (pStats) pStats.matchesPlayed += 1;
        });

        // Accumulate per-event sideout denominator independently so that legacy games
        // (stepper-only wins, no failures) get the reception-count floor applied per-game,
        // while well-tracked games use only their stepper terminal count. Mixing them at
        // the aggregate level would mask legacy games' inflated numerators.
        {
          const evtCombinedWins = evtBpSideoutPoints + evtKillSideoutPoints;
          const evtCombinedTerm = evtBpReceiveTerminal + evtKillSideoutPoints;
          const evtHasFailures = evtBpReceiveTerminal > evtBpSideoutPoints;
          let evtEffOpp: number;
          if (evtBpReceiveTerminal > 0) {
            evtEffOpp = evtHasFailures
              ? evtCombinedTerm
              : Math.max(evtCombinedTerm, evtSideoutOpportunities);
          } else if (evtKillSideoutPoints > 0) {
            evtEffOpp = evtSideoutOpportunities > 0 ? evtSideoutOpportunities : evtKillSideoutPoints;
          } else {
            evtEffOpp = evtSideoutOpportunities;
          }
          totalEffectiveSideoutOpp += evtEffOpp;
          totalEffectiveSideoutPoints += evtCombinedWins;
        }
        // Same per-event logic per rotation: compute each event's effective sideout
        // denominator for each rotation independently and accumulate into effectiveAttempts.
        ["1","2","3","4","5","6"].forEach(rot => {
          const evtAtt = evtRotAttempts[rot] ?? 0;
          const evtSuc = evtRotSuccesses[rot] ?? 0;
          const evtRcv = evtRotReceiveTotal[rot] ?? 0;
          const evtKll = evtRotKillSuccesses[rot] ?? 0;
          const evtCombined = evtAtt + evtKll;
          const evtHasFailures = evtAtt > evtSuc;
          let evtEffAtt: number;
          if (evtAtt > 0) {
            evtEffAtt = evtHasFailures ? evtCombined : Math.max(evtCombined, evtRcv);
          } else if (evtKll > 0) {
            evtEffAtt = evtRcv > 0 ? evtRcv : evtKll;
          } else {
            evtEffAtt = evtRcv;
          }
          rotationData[rot].effectiveAttempts += evtEffAtt;
        });

        // Accumulate Hudl Match Report team-level data from the official match record
        const officialRecord = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
        if (officialRecord?.gameState.teamReport) {
            const tr = officialRecord.gameState.teamReport;
            reportWonOnServe    = (reportWonOnServe    ?? 0) + tr.pointsWonOnServe;
            reportLostOnServe   = (reportLostOnServe   ?? 0) + tr.pointsLostOnServe;
            reportWonOnReceive  = (reportWonOnReceive  ?? 0) + tr.pointsWonOnReceive;
            reportLostOnReceive = (reportLostOnReceive ?? 0) + tr.pointsLostOnReceive;
        }
    });

    const computedPlayerStats = Array.from(statsMap.values()).map(s => {
        const p = players.find(pl => pl.id === s.playerId);
        if (!p) return null;
        if (selectedTeamId !== 'all' && p.teamId !== selectedTeamId) return null;
        if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
        return { ...s, player: p };
    }).filter(Boolean) as (CalculatedStats & { player: Player })[];

    // --- Compute Final Team Metrics ---
    // Sideout % and Point Scoring % require phase-tagged or break_point stats
    // (recorded automatically during live GameTime tracking). Manual box-score entry
    // never produces these, so we return null to show "--" instead of a misleading 0%.
    //
    // When receptions/reception_errors WERE tracked, use them as the exact denominator.
    // When they weren't (common when coaches track kills/aces but skip individual passes),
    // fall back to terminal receive-phase events (kills + blocks + attack_errors in receive)
    // which still gives a meaningful sideout% proxy.
    // Prefer phase-tagged terminal events as denominator (receives that ended with a kill,
    // block, attack error, or reception error in the 'receive' phase). Using raw
    // totalSideoutOpportunities (all receptions) inflates the denominator when receptions
    // are tracked without phase data, producing artificially low sideout %.
    // Merge serve-phase win sources into totalPointsOnServe.
    // break_point/rotation_point stats are authoritative (recorded per-rally by GameTime).
    // Phase-tagged kills are a fallback for legacy games that lack break_point stats.
    totalPointsOnServe += bpPointsOnServe > 0 ? bpPointsOnServe : killPointsOnServe;

    // Use per-event-accumulated effective sideout numerator/denominator.
    // Each event contributed its own correctly-floored denominator (see per-event block above),
    // so mixing legacy (stepper-wins-only) and modern (stepper-tracked-failures) games in one
    // season doesn't cause the aggregate stepperHasFailures check to incorrectly suppress the
    // legacy-game reception-count floor.
    const effectiveSideoutPoints = totalEffectiveSideoutPoints;
    const effectiveSideoutOpp = totalEffectiveSideoutOpp;
    const liveSideoutPct = hasLiveTrackingData
      ? (effectiveSideoutOpp > 0 ? effectiveSideoutPoints / effectiveSideoutOpp : null)
      : null;
    // Derive team PS% from the same rotation data the rotation cards use.
    // This guarantees team PS% == weighted mean of per-rotation PS%.
    // The old method (totalPointsOnServe / totalServes) diverges when some games use
    // break_point tracking and others use phase-tagged kills, because the numerator
    // only picks one source while the denominator spans all games.
    const rotBpAttempts = Object.values(rotationData).reduce((s, d) => s + d.bpAttempts, 0);
    const rotBpSuccesses = Object.values(rotationData).reduce((s, d) => s + d.bpSuccesses, 0);
    const livePointScoringPct = hasLiveTrackingData
      ? (rotBpAttempts > 0 ? rotBpSuccesses / rotBpAttempts
        : totalServes > 0 ? totalPointsOnServe / totalServes : null)
      : null;
    // Fall back to Hudl Match Report numbers when live tracking is unavailable
    const reportSideoutPct = (reportWonOnReceive !== null && reportLostOnReceive !== null)
      ? reportWonOnReceive / (reportWonOnReceive + reportLostOnReceive)
      : null;
    const reportPointScoringPct = (reportWonOnServe !== null && reportLostOnServe !== null)
      ? reportWonOnServe / (reportWonOnServe + reportLostOnServe)
      : null;

    // Prefer live tracking for sideout%: the stepper must be used to keep score/rotations
    // in sync, so bpReceiveTerminal always includes receive_failure records for every rally
    // the opponent wins while serving. Live data is therefore the accurate source.
    // Fall back to Hudl report only when no live tracking data exists.
    const sideoutPct      = liveSideoutPct      ?? reportSideoutPct;
    const pointScoringPct = livePointScoringPct ?? reportPointScoringPct;
    const serveReceiveAvg = teamReceptions > 0 ? teamReceptionSum / teamReceptions : 0;
    const hittingEfficiency = teamAttempts > 0 ? (teamKills - teamErrors) / teamAttempts : 0;
    const killPct = teamAttempts > 0 ? teamKills / teamAttempts : 0;
    const aceToErrorRatio = teamServeErrors > 0 ? teamAces / teamServeErrors : teamAces;
    const servePct = totalServes > 0 ? (totalServes - teamServeErrors) / totalServes : null;

    const rotationStats: TeamMetrics['rotationStats'] = {};
    Object.keys(rotationData).forEach(rot => {
        const d = rotationData[rot];
        const serverEntries = Object.entries(d.servers);
        const serverPlayerIds = serverEntries.length > 0
            ? serverEntries.sort((a, b) => b[1].attempts - a[1].attempts).map(([id]) => id)
            : undefined;
        const serverStats = serverEntries.length > 0
            ? Object.fromEntries(serverEntries.map(([id, s]) => [id, { attempts: s.attempts, points: s.points }]))
            : undefined;
        // Numerator: stepper-recorded sideout wins (d.successes) + stat-tap kill/block
        // sideout wins (d.killReceiveSuccesses). These are disjoint sources because
        // receive-phase kills/blocks don't generate a break_point in GameTime.
        const soSuccesses = d.successes + d.killReceiveSuccesses;
        // Denominator: stepper-recorded events (includes errors via auto-recorded
        // break_points and stepper +1 actions) + kill/block successes that aren't in
        // d.attempts. We add only killReceiveSuccesses (kills) — adding all of
        // killReceiveAttempts would re-count error rallies that already increment
        // d.attempts. Falls back to receptions or kill totals when no events exist.
        // Use per-event-accumulated effective attempts as the denominator so that
        // legacy games (stepper-wins-only) have the reception-count floor applied
        // independently before being summed with modern games.
        const soAttempts = d.effectiveAttempts > 0 ? d.effectiveAttempts
          : (d.receiveTotal > 0 ? d.receiveTotal : d.killReceiveAttempts);
        rotationStats[rot] = {
            sideoutAttempts: soAttempts,
            sideoutsWon: soSuccesses,
            sideoutPct: soAttempts > 0 ? soSuccesses / soAttempts : 0,
            breakPointAttempts: d.bpAttempts,
            breakPointsWon: d.bpSuccesses,
            breakPointPct: d.bpAttempts > 0 ? d.bpSuccesses / d.bpAttempts : 0,
            serverPlayerIds,
            serverStats,
        };
    });

    return {
        playerStats: computedPlayerStats,
        teamMetrics: {
            sideoutPct, pointScoringPct, serveReceiveAvg, hittingEfficiency, killPct, aceToErrorRatio, servePct, rotationStats,
            // Raw counts for numerator/denominator display
            teamReceptions, teamReceptionSum, teamKills, teamErrors, teamAttempts, teamAces, teamServeErrors,
            totalServes, totalPointsOnServe, effectiveSideoutPoints, effectiveSideoutOpp,
            reportWonOnReceive, reportLostOnReceive, reportWonOnServe, reportLostOnServe,
        },
        advancedMetrics: { serveByType, recByServeStyle, recByContactType, recByStyleAndContact, attackErrorsByType, killHitTypeCounts, killOutcomeCounts },
        playerChartData: playerChartMap,
        hasLiveTrackingData,
        hasServerPointData,
    };

  }, [players, filteredEvents, selectedTeamId, selectedEventIds, searchQuery, matchRecords, sourceFilter, selectedSetNumber]);

  // --- 4. Sorting & Formatting ---
  const sortedData = useMemo(() => {
      if (!sortConfig) return playerStats;
      return [...playerStats].sort((a, b) => {
          const getValue = (item: any, key: string) => {
              switch(key) {
                  case 'H%': return (item.attackAttempts > 0 ? (item.kills - item.attackErrors) / item.attackAttempts : 0);
                  case 'K%': return (item.attackAttempts > 0 ? item.kills / item.attackAttempts : 0);
                  case 'K/G': return (item.gamesPlayed > 0 ? item.kills / item.gamesPlayed : 0);
                  case 'Ace %': return (item.serveAttempts > 0 ? item.aces / item.serveAttempts : 0);
                  case 'Srv%': return (item.serveAttempts > 0 ? (item.serveAttempts - item.serveErrors) / item.serveAttempts : 0);
                  case 'PS%': return (hasServerPointData && item.serveAttempts > 0 ? (item.aces + item.serverPoints) / item.serveAttempts : 0);
                  case 'A/G': return (item.gamesPlayed > 0 ? item.aces / item.gamesPlayed : 0);
                  case 'AST/G': return (item.gamesPlayed > 0 ? item.assists / item.gamesPlayed : 0);
                  case 'B/G': return (item.gamesPlayed > 0 ? (item.blockSolo + item.blockAssist) / item.gamesPlayed : 0);
                  case 'D/G': return (item.gamesPlayed > 0 ? item.digs / item.gamesPlayed : 0);
                  case 'SRG': return (item.gamesPlayed > 0 ? item.receptions / item.gamesPlayed : 0);
                  case 'AVG': return (item.receptions > 0 ? item.receptionSum / item.receptions : 0);
                  case 'B': return item.blockSolo + item.blockAssist;
                  default: return item[key] || 0;
              }
          };
          const valA = getValue(a, sortConfig.key);
          const valB = getValue(b, sortConfig.key);
          return sortConfig.direction === 'asc' ? (valA < valB ? -1 : 1) : (valA > valB ? -1 : 1);
      });
  }, [playerStats, sortConfig]);

  // Per-game team metrics for the stat timeline chart
  const perGameMetrics = useMemo(() => {
    // Only include events relevant to the selected team (consistent with teamMetrics)
    const teamPlayerIds = new Set(
      players
        .filter(p => selectedTeamId === 'all' || p.teamId === selectedTeamId)
        .map(p => p.id)
    );
    const raw = filteredEvents
      .map(evt => {
        const record = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
        const stats: StatRecord[] = record?.gameState.stats?.length
          ? record.gameState.stats
          : (evt.stats ?? []);
        // Filter stats to the selected team's players only
        const teamStats = selectedTeamId === 'all'
          ? stats
          : stats.filter(s => teamPlayerIds.has(s.playerId));

        let kills = 0, errors = 0, attempts = 0;
        let aces = 0, serveErrors = 0, serves = 0;
        let receptions = 0, receptionSum = 0;
        let sideoutPoints = 0, receiveTerminal = 0, sideoutOpp = 0;
        let bpReceiveTerminal = 0, bpSideoutPoints = 0;
        let pointsOnServe = 0;
        let hasLive = false;

        for (const s of teamStats) {
          switch (s.statType) {
            case 'kill': kills++; attempts++; break;
            case 'attack_error': errors++; attempts++; break;
            case 'attack_attempt': attempts++; break;
            case 'ace': aces++; serves++; break;
            case 'serve_error': serveErrors++; serves++; break;
            case 'serve_attempt': serves++; break;
            case 'reception': receptions++; sideoutOpp++; if (s.passRating != null) receptionSum += s.passRating; break;
            case 'reception_error': receptions++; sideoutOpp++; break;
            case 'break_point':
            case 'rotation_point':
              hasLive = true;
              break;
          }
          if (s.phase !== undefined) hasLive = true;
          if (s.phase === 'serve' && (s.statType === 'kill' || s.statType === 'block_solo' || s.statType === 'block_assist')) pointsOnServe++;
          if (s.phase === 'receive') {
            if (s.statType === 'kill' || s.statType === 'block_solo' || s.statType === 'block_assist') { sideoutPoints++; receiveTerminal++; }
            if (s.statType === 'attack_error' || s.statType === 'reception_error') receiveTerminal++;
          }
        }

        // break_point/rotation_point stats often use playerId='team', which is excluded by the
        // teamStats filter above. Re-scan the unfiltered stats to get the authoritative sideout
        // denominator (every receive rally, including ones the opponent won), matching the main metric.
        for (const s of stats) {
          if (s.statType !== 'break_point' && s.statType !== 'rotation_point') continue;
          hasLive = true;
          if (s.phase === 'receive') {
            const isOurPoint = s.playerId === 'team' || teamPlayerIds.has(s.playerId);
            if (s.flags?.includes('receive_failure') || !isOurPoint) {
              bpReceiveTerminal++;
            } else {
              bpSideoutPoints++;
              bpReceiveTerminal++;
            }
          }
        }

        let soV: number | null = null;
        let psV: number | null = null;
        if (hasLive) {
          // Combine sources to mirror team metrics: stepper-recorded sideouts
          // (bpSideoutPoints) + stat-tap kill/block sideouts (sideoutPoints) for the
          // numerator. Denominator adds bpReceiveTerminal (stepper events + error
          // failures) + sideoutPoints (kills only, to avoid double-counting errors
          // already counted in bpReceiveTerminal via auto-recorded break_points).
          const combinedWins = bpSideoutPoints + sideoutPoints;
          const combinedTerm = bpReceiveTerminal + sideoutPoints;
          // Only fall back to reception count when the stepper has no failures recorded
          // (bpReceiveTerminal == bpSideoutPoints). If failures exist the stepper is
          // authoritative and using reception count would inflate the denominator.
          const perGameStepperHasFailures = bpReceiveTerminal > bpSideoutPoints;
          const denom = combinedTerm > 0
            ? (perGameStepperHasFailures ? combinedTerm : Math.max(combinedTerm, sideoutOpp > 0 ? sideoutOpp : receiveTerminal))
            : (receiveTerminal > 0 ? receiveTerminal : sideoutOpp);
          soV = denom > 0 ? combinedWins / denom : null;
          psV = serves > 0 ? pointsOnServe / serves : null;
        } else {
          const tr = record?.gameState.teamReport;
          if (tr) {
            const soTotal = tr.pointsWonOnReceive + tr.pointsLostOnReceive;
            const psTotal = tr.pointsWonOnServe + tr.pointsLostOnServe;
            soV = soTotal > 0 ? tr.pointsWonOnReceive / soTotal : null;
            psV = psTotal > 0 ? tr.pointsWonOnServe / psTotal : null;
          }
        }

        // Drop this event from the chart if no metrics could be computed for the selected team
        const hasData = attempts > 0 || serves > 0 || receptions > 0 || soV !== null || psV !== null;
        if (!hasData) return null;

        const mm = evt.date.slice(5).replace('-', '/');
        const opp = evt.opponent?.trim() || '';
        const tName = (evt.tournamentName || '').trim();
        let shortLabel: string;
        let fullLabel: string;
        if (evt.type === 'tournament') {
          // Use opponent as the X-axis label so each game is distinguishable
          shortLabel = opp ? `${mm} · ${opp.slice(0, 12)}` : (tName ? `${mm} · ${tName.slice(0, 12)}` : mm);
          fullLabel = [mm, tName || undefined, opp ? `vs ${opp}` : undefined].filter(Boolean).join(' · ');
        } else {
          shortLabel = mm;
          fullLabel = opp ? `${mm} vs ${opp}` : mm;
        }
        return {
          eventId: evt.id,
          shortLabel,
          fullLabel,
          passRating: receptions > 0 ? receptionSum / receptions : null,
          hitEfficiency: attempts > 0 ? (kills - errors) / attempts : null,
          servePct: serves > 0 ? (serves - serveErrors) / serves : null,
          killPct: attempts > 0 ? kills / attempts : null,
          aceToErrorRatio: serveErrors > 0 ? aces / serveErrors : (aces > 0 ? aces : null),
          sideoutPct: soV,
          pointScoringPct: psV,
        };
      })
      .filter(Boolean) as NonNullable<ReturnType<typeof filteredEvents['map']>[number]>[];
    // Fix #2: disambiguate duplicate shortLabels (multiple tournament games on the same day)
    const labelCounts = new Map<string, number>();
    raw.forEach(m => labelCounts.set(m.shortLabel, (labelCounts.get(m.shortLabel) ?? 0) + 1));
    const labelIdx = new Map<string, number>();
    return raw.map(m => {
      if ((labelCounts.get(m.shortLabel) ?? 1) <= 1) return m;
      const n = (labelIdx.get(m.shortLabel) ?? 0) + 1;
      labelIdx.set(m.shortLabel, n);
      return { ...m, shortLabel: `${m.shortLabel} #${n}` };
    });
  }, [filteredEvents, matchRecords, players, selectedTeamId]);

  // Which stat sources actually exist across filtered/selected events
  const availableSources = useMemo(() => {
    const eventsToCheck = selectedEventIds.size > 0
      ? filteredEvents.filter(e => selectedEventIds.has(e.id))
      : filteredEvents;
    const sources = { live: false, import: false, manual: false };
    eventsToCheck.forEach(evt => {
      const allStats = [
        ...(matchRecords.find(r => r.eventId === evt.id && r.isRecord)?.gameState.stats ?? []),
        ...(evt.stats ?? []),
      ];
      allStats.forEach(s => {
        if (!s.source || s.source === 'live') sources.live = true;
        else if (s.source === 'import') sources.import = true;
        else if (s.source === 'manual') sources.manual = true;
      });
    });
    return sources;
  }, [filteredEvents, selectedEventIds, matchRecords]);

  // Sectioned team dropdown — my teams (club) and opponent teams, only those with match data.
  const { myTeamsWithData, opponentTeamsWithData } = useMemo(() => {
    // Player IDs that appear in any filtered event's stats
    const playerIdsWithStats = new Set<string>();
    const eventTeamIds = new Set<string>();
    filteredEvents.forEach(evt => {
      if (evt.teamId) eventTeamIds.add(evt.teamId);
      const stats = matchRecords.find(r => r.eventId === evt.id && r.isRecord)?.gameState.stats ?? evt.stats ?? [];
      stats.forEach(s => playerIdsWithStats.add(s.playerId));
    });

    const myTeamsWithData = teams
      .filter(t => t.type !== 'opponent' && (
        eventTeamIds.has(t.id) ||
        players.some(p => p.teamId === t.id && playerIdsWithStats.has(p.id))
      ))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Opponent teams only appear if they have actual tracked player stats
    const opponentTeamsWithData = teams
      .filter(t => t.type === 'opponent' &&
        players.some(p => p.teamId === t.id && playerIdsWithStats.has(p.id))
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    return { myTeamsWithData, opponentTeamsWithData };
  }, [filteredEvents, matchRecords, teams, players]);

  const selectedTeamHasStats = myTeamsWithData.some(t => t.id === selectedTeamId);

  // Reset source filter if the active source no longer exists in the current event selection
  useEffect(() => {
    if (sourceFilter === 'all') return;
    if (!availableSources[sourceFilter as keyof typeof availableSources]) {
      setSourceFilter('all');
    }
  }, [availableSources, sourceFilter]);

  // Team aggregate row — summed counting stats, max gamesPlayed (= team sets played).
  // Column render functions then naturally produce team-level rates and percentages.
  const teamAggRow = useMemo(() => {
    if (!sortedData.length) return null;
    const agg = sortedData.reduce((acc, row) => {
      acc.kills += row.kills;
      acc.attackErrors += row.attackErrors;
      acc.attackAttempts += row.attackAttempts;
      acc.aces += row.aces;
      acc.serveErrors += row.serveErrors;
      acc.serveAttempts += row.serveAttempts;
      acc.rotationPoints += row.rotationPoints;
      acc.serverPoints += row.serverPoints;
      acc.blockSolo += row.blockSolo;
      acc.blockAssist += row.blockAssist;
      acc.blockErrors += row.blockErrors;
      acc.digs += row.digs;
      acc.digErrors += row.digErrors;
      acc.assists += row.assists;
      acc.ballHandlingAttempts += row.ballHandlingAttempts;
      acc.ballHandlingErrors += row.ballHandlingErrors;
      acc.receptions += row.receptions;
      acc.receptionErrors += row.receptionErrors;
      acc.receptionSum += row.receptionSum;
      acc.srPerfect += row.srPerfect;
      acc.srGood += row.srGood;
      acc.srPoor += row.srPoor;
      acc.gamesPlayed = Math.max(acc.gamesPlayed, row.gamesPlayed);
      acc.matchesPlayed = Math.max(acc.matchesPlayed, row.matchesPlayed);
      return acc;
    }, {
      playerId: '__team__', matchesPlayed: 0, gamesPlayed: 0,
      kills: 0, attackErrors: 0, attackAttempts: 0,
      aces: 0, serveErrors: 0, serveAttempts: 0, rotationPoints: 0, serverPoints: 0,
      blockSolo: 0, blockAssist: 0, blockErrors: 0,
      digs: 0, digErrors: 0,
      assists: 0, ballHandlingAttempts: 0, ballHandlingErrors: 0,
      receptions: 0, receptionErrors: 0, receptionSum: 0,
      srPerfect: 0, srGood: 0, srPoor: 0,
    });
    return agg;
  }, [sortedData]);

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'desc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'desc') direction = 'asc';
    setSortConfig({ key, direction });
  };

  const RH = (short: string, long: string) => (
    <>
      <span className="md:hidden">{short}</span>
      <span className="hidden md:inline">{long}</span>
    </>
  );

  // ─── Helper: convert existing StatRecord[] → draft shape ──────────────────
  const convertStatsToDraft = (stats: StatRecord[]): Record<string, Record<string, number>> => {
    const draft: Record<string, Record<string, number>> = {};
    stats.forEach(s => {
      if (!draft[s.playerId]) draft[s.playerId] = {};

      // Map reception records to SR breakdown fields
      if (s.statType === 'reception') {
        if (s.passRating === 3) {
          draft[s.playerId].srPerfect = (draft[s.playerId].srPerfect || 0) + 1;
        } else if (s.passRating === 2) {
          draft[s.playerId].srGood = (draft[s.playerId].srGood || 0) + 1;
        } else {
          // passRating 1, 0, or undefined → poor
          draft[s.playerId].srPoor = (draft[s.playerId].srPoor || 0) + 1;
        }
        return;
      }
      if (s.statType === 'reception_error') {
        draft[s.playerId].srError = (draft[s.playerId].srError || 0) + 1;
        return;
      }

      const key = STAT_TO_DRAFT_KEY[s.statType];
      if (!key) return;
      draft[s.playerId][key] = (draft[s.playerId][key] || 0) + 1;
    });
    return draft;
  };

  // ─── Helper: extract pass ratings → comma-sep string per player ───────────
  const convertPassRatingsToDraft = (stats: StatRecord[]): Record<string, string> => {
    const ratings: Record<string, number[]> = {};
    stats.forEach(s => {
      if (s.statType === 'reception' && s.passRating !== undefined) {
        if (!ratings[s.playerId]) ratings[s.playerId] = [];
        ratings[s.playerId].push(s.passRating);
      }
    });
    return Object.fromEntries(
      Object.entries(ratings).map(([id, arr]) => [id, arr.join(',')])
    );
  };

  // ─── Helper: parse pass ratings string → array (length = receptionCount) ──
  const parsePassRatingsArray = (input: string, receptionCount: number): (number | undefined)[] => {
    const trimmed = input.trim();
    if (!trimmed) return Array(receptionCount).fill(undefined);
    const parts = trimmed.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
    if (parts.length === 1) {
      // Single value = average → distribute evenly
      return Array(receptionCount).fill(parts[0]);
    }
    // Individual values — pad/trim to receptionCount
    const result: (number | undefined)[] = [];
    for (let i = 0; i < receptionCount; i++) {
      result.push(parts[i] ?? undefined);
    }
    return result;
  };

  // ─── Helper: compute pass avg from input string ────────────────────────────
  const parsePassAvg = (input: string, receptionCount: number): number => {
    const trimmed = input.trim();
    if (!trimmed || receptionCount === 0) return 0;
    const parts = trimmed.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
    if (parts.length === 0) return 0;
    if (parts.length === 1) return parts[0];
    const sum = parts.reduce((acc, v) => acc + v, 0);
    return sum / parts.length;
  };

  // ─── Helper: build StatRecord[] from draft + pass input ───────────────────
  const buildStatRecords = (
    draft: Record<string, Record<string, number>>,
    passInput: Record<string, string>
  ): StatRecord[] => {
    const records: StatRecord[] = [];
    const timestamp = Date.now();

    Object.entries(draft).forEach(([playerId, stats]) => {
      Object.entries(stats).forEach(([key, count]) => {
        if (count <= 0) return;

        // SR breakdown keys → reception records with passRating
        if (key === 'srPerfect') {
          for (let i = 0; i < count; i++) records.push({ playerId, statType: 'reception', passRating: 3, timestamp, source: 'manual' });
          return;
        }
        if (key === 'srGood') {
          for (let i = 0; i < count; i++) records.push({ playerId, statType: 'reception', passRating: 2, timestamp, source: 'manual' });
          return;
        }
        if (key === 'srPoor') {
          for (let i = 0; i < count; i++) records.push({ playerId, statType: 'reception', passRating: 1, timestamp, source: 'manual' });
          return;
        }
        if (key === 'srError') {
          for (let i = 0; i < count; i++) records.push({ playerId, statType: 'reception_error', timestamp, source: 'manual' });
          return;
        }

        // Skip old reception/receptionErrors keys — now derived from SR counts
        if (key === 'receptions' || key === 'receptionErrors') return;

        const statType = DRAFT_KEY_TO_STAT[key];
        if (!statType) return;

        for (let i = 0; i < count; i++) {
          records.push({ playerId, statType, timestamp, source: 'manual' });
        }
      });
    });

    return records;
  };

  // ─── Helper: build minimal GameState for stats-only match_record ──────────
  const buildMinimalGameState = (
    stats: StatRecord[],
    teamReport?: HudlTeamReport | null,
    setHistory?: Array<{ our: number; their: number }>,
    setsWonOverride?: { our: number; their: number },
  ): GameState => {
    const evt = filteredEvents.find(e => e.id === selectedEventId);
    const empty6 = { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '' };
    const emptyOp6 = { op1: '', op2: '', op3: '', op4: '', op5: '', op6: '' };
    const ourSetsWon = setHistory?.length ? setHistory.filter(s => s.our > s.their).length : (setsWonOverride?.our ?? 0);
    const theirSetsWon = setHistory?.length ? setHistory.filter(s => s.their > s.our).length : (setsWonOverride?.their ?? 0);
    return {
      id: selectedEventId === 'all' ? `manual-${Date.now()}` : selectedEventId,
      opponent: evt?.opponent || '',
      ourScore: 0,
      theirScore: 0,
      ourSetsWon,
      theirSetsWon,
      set: (setHistory?.length ?? 0) + 1,
      lineup: empty6,
      numerals: empty6,
      opponentLineup: emptyOp6,
      opponentNumerals: emptyOp6,
      stats,
      substitutionsUsed: { us: 0, them: 0 },
      ...(teamReport ? { teamReport } : {}),
      ...(setHistory?.length ? { setHistory } : {}),
    };
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────

  // Unique tournaments derived from all events (for the tournament picker)
  const existingTournaments = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const e of events) {
      if (e.tournamentId && e.tournamentName && !map.has(e.tournamentId)) {
        map.set(e.tournamentId, { id: e.tournamentId, name: e.tournamentName });
      }
    }
    return Array.from(map.values());
  }, [events]);

  const handleCreateGame = async () => {
    if (!newGameForm.opponent || !newGameForm.date) return;
    if (!setEvents) return;
    const titlePrefix = newGameForm.winner === 'us' ? 'W' : 'L';
    const title = `vs ${newGameForm.opponent} (${titlePrefix} ${newGameForm.scoreUs}-${newGameForm.scoreThem})`;
    const finalTournamentId = newGameForm.isTournament ? (newGameForm.tournamentId || crypto.randomUUID()) : undefined;
    const finalTournamentName = newGameForm.isTournament ? newGameForm.tournamentName : undefined;
    const newEvent: GameEvent = {
      id: `game-${Date.now()}`,
      organizationId: currentOrg?.id || '',
      type: 'game',
      title: title,
      date: newGameForm.date,
      time: 'TBD',
      location: 'TBD',
      opponent: newGameForm.opponent,
      tournamentId: finalTournamentId,
      tournamentName: finalTournamentName,
      stats: []
    };
    setEvents(prev => [...prev, newEvent]);
    setSelectedEventIds(new Set([newEvent.id]));
    setStatsMode('add_manual');
    setLocalStatsDraft({});
    setPassRatingsInput({});
    setScannedSetScores([]);
    // Pre-populate sets won from the form so W/L and set count are correct
    setManualSetsWon(
      newGameForm.winner === 'us'
        ? { our: newGameForm.scoreUs, their: newGameForm.scoreThem }
        : { our: newGameForm.scoreThem, their: newGameForm.scoreUs }
    );
    setShowCreateGameModal(false);
    if (currentOrg?.id !== 'dev-org-id') {
      try {
        await supabase.from('events').insert([{
          organization_id: currentOrg?.id,
          type: 'game',
          title: title,
          date: newGameForm.date,
          time: 'TBD',
          location: 'TBD',
          opponent: newGameForm.opponent,
          tournament_id: finalTournamentId || null,
          tournament_name: finalTournamentName || null,
        }]);
      } catch(e) { console.error("Failed to create game in DB", e); }
    }
  };

  const handleOpenActionModal = () => {
    if (selectedEventId === 'all') {
      if (filteredEvents.length === 0) {
        setShowCreateGameModal(true);
        return;
      }
      // Load aggregated season stats into draft and open edit view directly
      const draft: Record<string, Record<string, number>> = {};
      playerStats.forEach(s => {
        draft[s.playerId] = {
          kills: s.kills,
          attackErrors: s.attackErrors,
          attackAttempts: s.attackAttempts,
          aces: s.aces,
          serveErrors: s.serveErrors,
          serveAttempts: s.serveAttempts,
          blockSolo: s.blockSolo,
          blockAssist: s.blockAssist,
          digs: s.digs,
          digErrors: s.digErrors,
          assists: s.assists,
          ballHandlingErrors: s.ballHandlingErrors,
          srPerfect: s.srPerfect,
          srGood: s.srGood,
          srPoor: s.srPoor,
          srError: s.receptionErrors,
        };
      });
      setLocalStatsDraft(draft);
      setPassRatingsInput({});
      setStatsMode('add_manual');
      return;
    }
    // Pre-select the isRecord record (or first) for edit mode
    const records = matchRecords.filter(r => r.eventId === selectedEventId);
    const defaultRecord = records.find(r => r.isRecord) || records[0] || null;
    setSelectedEditRecordId(defaultRecord?.id ?? null);
    setShowActionModal(true);
  };


  const handleStartEdit = (recordId: string | null) => {
    let statsToLoad: StatRecord[];
    let setHistoryToLoad: Array<{ our: number; their: number }> = [];
    let setsWonToLoad = { our: 0, their: 0 };
    if (recordId) {
      const record = matchRecords.find(r => r.id === recordId);
      statsToLoad = record?.gameState.stats ?? [];
      setHistoryToLoad = record?.gameState.setHistory ?? [];
      setsWonToLoad = { our: record?.gameState.ourSetsWon ?? 0, their: record?.gameState.theirSetsWon ?? 0 };
    } else {
      const evt = filteredEvents.find(e => e.id === selectedEventId);
      statsToLoad = evt?.stats ?? [];
    }
    setEditingRecordId(recordId);
    setLocalStatsDraft(convertStatsToDraft(statsToLoad));
    setPassRatingsInput(convertPassRatingsToDraft(statsToLoad));
    setScannedSetScores(setHistoryToLoad);
    setManualSetsWon(setsWonToLoad);
    setStatsMode('edit');
    setShowActionModal(false);
  };

  const handleStartAddManual = () => {
    setLocalStatsDraft({});
    setPassRatingsInput({});
    setScannedSetScores([]);
    setManualSetsWon({ our: 0, their: 0 });
    setStatsMode('add_manual');
    setShowActionModal(false);
  };

  const handleStatChange = (playerId: string, statKey: string, value: number) => {
    setLocalStatsDraft(prev => ({
      ...prev,
      [playerId]: { ...(prev[playerId] || {}), [statKey]: Math.max(0, value) }
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setShowActionModal(false);

    try {
      let allExtracted: ExtractedStats[] = [];
      let latestTeamReport: HudlTeamReport | null = null;
      let latestSetScores: Array<{ our: number; their: number }> = [];
      for (const file of Array.from(files) as File[]) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const result: ExtractStatsResult = await extractStatsFromImage(base64);
        allExtracted = [...allExtracted, ...(result.players ?? [])];
        if (result.teamReport) latestTeamReport = result.teamReport;
        if (result.setScores?.length) latestSetScores = result.setScores;
      }
      // Consolidate duplicates by jerseyNumber with zero-overwrite protection
      const mergedMap: Record<string, ExtractedStats> = {};
      for (const row of allExtracted) {
        const jn = (row.jerseyNumber ?? '').trim().toLowerCase();
        if (!jn) continue;
        if (!mergedMap[jn]) { mergedMap[jn] = { ...row }; continue; }
        const target = mergedMap[jn];
        for (const key of Object.keys(row) as (keyof ExtractedStats)[]) {
          const newVal = row[key];
          const newStr = String(newVal ?? '').trim();
          if (!newStr || newStr === '-') continue;
          if (key !== 'playerName' && key !== 'jerseyNumber' && !/\d/.test(newStr)) continue;
          const existing = target[key];
          if (typeof existing === 'number' && existing !== 0 && (newVal === 0 || newVal === '0')) continue;
          (target as any)[key] = newVal;
        }
      }
      const consolidated = Object.values(mergedMap);
      if (consolidated.length > 0 || latestTeamReport) {
        setScannedStats(consolidated);
        setScannedTeamReport(latestTeamReport);
        setScannedSetScores(latestSetScores);
        setIsReviewingScan(true);
        setStatsMode('add_scan');
      } else {
        alert('No stats found in the selected file(s). Make sure the files clearly show a stat sheet and try again.');
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      const isNetworkError = error instanceof TypeError && error.message.includes('fetch');
      alert(isNetworkError
        ? 'Could not reach the AI service. Make sure the API server is running (npm run server).'
        : `Failed to extract stats: ${error.message ?? 'Unknown error'}`
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleScannedStatChange = (idx: number, field: keyof ExtractedStats, value: string) => {
    setScannedStats(prev => prev.map((item, i) =>
      i === idx ? { ...item, [field]: field === 'jerseyNumber' || field === 'playerName' ? value : (parseFloat(value) || 0) } : item
    ));
  };

  const handleSaveScannedStats = async () => {
    if (!setEvents && !currentUser) return;
    const timestamp = Date.now();
    const newRecords: StatRecord[] = [];

    scannedStats.forEach((item, idx) => {
      if (isSummaryRow(item)) return; // summary row — never save as player stats
      const jerseyMatched = players.find(p =>
        p.jerseyNumber === item.jerseyNumber ||
        p.jerseyNumber === `#${item.jerseyNumber}` ||
        `#${p.jerseyNumber}` === item.jerseyNumber
      );
      const resolvedPlayer = jerseyMatched ?? players.find(p => p.id === manualPlayerMap[idx]);
      if (!resolvedPlayer) return;

      const pid = resolvedPlayer.id;

      // Hitting
      for (let i = 0; i < (item.kills || 0); i++) newRecords.push({ playerId: pid, statType: 'kill', timestamp, source: 'import' });
      for (let i = 0; i < (item.errors || 0); i++) newRecords.push({ playerId: pid, statType: 'attack_error', timestamp, source: 'import' });
      const nonKillNonError = Math.max(0, (item.attempts || 0) - (item.kills || 0) - (item.errors || 0));
      for (let i = 0; i < nonKillNonError; i++) newRecords.push({ playerId: pid, statType: 'attack_attempt', timestamp, source: 'import' });

      // Serving
      for (let i = 0; i < (item.aces || 0); i++) newRecords.push({ playerId: pid, statType: 'ace', timestamp, source: 'import' });
      for (let i = 0; i < (item.serveErrors || 0); i++) newRecords.push({ playerId: pid, statType: 'serve_error', timestamp, source: 'import' });
      const neutralServes = Math.max(0, (item.servingAttempts || 0) - (item.aces || 0) - (item.serveErrors || 0));
      for (let i = 0; i < neutralServes; i++) newRecords.push({ playerId: pid, statType: 'serve_attempt', timestamp, source: 'import' });

      // Blocking
      for (let i = 0; i < (item.blocks || 0); i++) newRecords.push({ playerId: pid, statType: 'block_solo', timestamp, source: 'import' });
      for (let i = 0; i < (item.blockAssist || 0); i++) newRecords.push({ playerId: pid, statType: 'block_assist', timestamp, source: 'import' });

      // Digs / Setting
      for (let i = 0; i < (item.digs || 0); i++) newRecords.push({ playerId: pid, statType: 'dig', timestamp, source: 'import' });
      for (let i = 0; i < (item.assists || 0); i++) newRecords.push({ playerId: pid, statType: 'assist', timestamp, source: 'import' });

      // Serve Receive — map breakdown to reception records with passRating
      for (let i = 0; i < (item.srPerfect || 0); i++) newRecords.push({ playerId: pid, statType: 'reception', passRating: 3, timestamp, source: 'import' });
      for (let i = 0; i < (item.srGood || 0); i++) newRecords.push({ playerId: pid, statType: 'reception', passRating: 2, timestamp, source: 'import' });
      for (let i = 0; i < (item.srPoor || 0); i++) newRecords.push({ playerId: pid, statType: 'reception', passRating: 1, timestamp, source: 'import' });
      for (let i = 0; i < (item.srError || 0); i++) newRecords.push({ playerId: pid, statType: 'reception_error', timestamp, source: 'import' });

      // Sets played — push sentinel records with setNumber 1..N so gamesPlayed counts correctly.
      // These have no stat effect; the computeStats switch doesn't handle 'set_played'.
      const setsN = item.setsPlayed || 1;
      for (let s = 1; s <= setsN; s++) {
        newRecords.push({ playerId: pid, statType: 'set_played', setNumber: s, timestamp, source: 'import' });
      }
    });

    // If there's nothing at all to save, bail early with a message
    if (newRecords.length === 0 && !scannedTeamReport) {
      alert('No matched players to save. Assign players to jersey numbers or cancel.');
      return;
    }

    const evt = filteredEvents.find(e => e.id === selectedEventId);
    const hasExisting = (evt?.stats && evt.stats.length > 0) || matchRecords.some(r => r.eventId === selectedEventId);
    // Always use a match record when team report data is present, so teamReport persists on game_state
    const needsMatchRecord = hasExisting || scannedTeamReport != null;

    try {
      if (needsMatchRecord && currentUser && currentOrg?.id && currentOrg.id !== 'dev-org-id') {
        // Find the existing official record for this event (if any)
        const officialRecord = matchRecords.find(r => r.eventId === selectedEventId && r.isRecord);

        if (scannedTeamReport && newRecords.length === 0 && officialRecord) {
          // Match Report only + existing official record:
          // UPDATE the record by merging teamReport into its existing gameState.
          // This preserves all previously saved player stats and avoids the
          // unique constraint violation on (event_id, submitted_by).
          const setScoresPayload = scannedSetScores.length > 0 ? scannedSetScores : undefined;
          const mergedGameState: GameState = {
            ...officialRecord.gameState,
            teamReport: scannedTeamReport,
            ...(setScoresPayload ? {
              setHistory: setScoresPayload,
              ourSetsWon: setScoresPayload.filter(s => s.our > s.their).length,
              theirSetsWon: setScoresPayload.filter(s => s.their > s.our).length,
            } : {}),
          };
          const { error: updateError } = await supabase
            .from('match_records')
            .update({ game_state: mergedGameState })
            .eq('id', officialRecord.id);
          if (updateError) throw updateError;
          if (setMatchRecords) {
            setMatchRecords(prev => prev.map(r =>
              r.id === officialRecord.id ? { ...r, gameState: mergedGameState } : r
            ));
          }
        } else {
          // Normal path: first-time import, or player stats upload alongside an existing record.
          // The DB no longer enforces a unique constraint on (event_id, submitted_by), so imports
          // always create a separate record that can coexist with live-tracking records.
          const gameState: GameState = { ...buildMinimalGameState(newRecords, scannedTeamReport, scannedSetScores.length ? scannedSetScores : undefined), originalSource: 'import' };
          const newRecord = await insertMatchRecord(currentOrg.id, selectedEventId, currentUser.id, currentUser.fullName, gameState, filteredEvents.find(e => e.id === selectedEventId)?.teamId ?? (selectedTeamId !== 'all' ? selectedTeamId : undefined));
          if (setMatchRecords) setMatchRecords(prev => [...prev, newRecord]);
        }
      } else if (currentOrg?.id === 'dev-org-id' && scannedTeamReport && setMatchRecords) {
        // Dev/local mode: merge into existing in-memory record if present, else create one
        const officialRecord = matchRecords.find(r => r.eventId === selectedEventId && r.isRecord);
        if (scannedTeamReport && newRecords.length === 0 && officialRecord) {
          const setScoresPayload = scannedSetScores.length > 0 ? scannedSetScores : undefined;
          const mergedGameState: GameState = {
            ...officialRecord.gameState,
            teamReport: scannedTeamReport,
            ...(setScoresPayload ? {
              setHistory: setScoresPayload,
              ourSetsWon: setScoresPayload.filter(s => s.our > s.their).length,
              theirSetsWon: setScoresPayload.filter(s => s.their > s.our).length,
            } : {}),
          };
          setMatchRecords(prev => prev.map(r =>
            r.id === officialRecord.id ? { ...r, gameState: mergedGameState } : r
          ));
        } else {
          const gameState = buildMinimalGameState(newRecords, scannedTeamReport, scannedSetScores.length ? scannedSetScores : undefined);
          const fakeRecord: MatchRecord = {
            id: `local-mr-${Date.now()}`,
            eventId: selectedEventId,
            organizationId: currentOrg.id,
            submittedBy: currentUser?.id || null,
            submittedByName: currentUser?.fullName || 'Local',
            submittedAt: new Date().toISOString(),
            gameState,
            isRecord: !hasExisting,
          };
          setMatchRecords(prev => [...prev, fakeRecord]);
          if (setEvents && newRecords.length > 0) {
            setEvents(prev => prev.map(e =>
              e.id === selectedEventId ? { ...e, stats: [...(e.stats || []), ...newRecords] } : e
            ));
          }
        }
      } else {
        // No teamReport, no existing records — simple append to event.stats
        if (setEvents) {
          setEvents(prev => prev.map(e =>
            e.id === selectedEventId ? { ...e, stats: [...(e.stats || []), ...newRecords] } : e
          ));
        }
        if (currentOrg?.id !== 'dev-org-id') {
          const { data: current } = await supabase.from('events').select('stats').eq('id', selectedEventId).single();
          const existing = current?.stats || [];
          await supabase.from('events').update({ stats: [...existing, ...newRecords] }).eq('id', selectedEventId);
        }
      }

      const toastMsg = newRecords.length === 0 && scannedTeamReport
        ? 'Match Report stats saved successfully.'
        : 'Stats saved successfully.';
      setSaveSuccessToast(toastMsg);
      setTimeout(() => setSaveSuccessToast(null), 3500);
    } catch (error: any) {
      console.error('Save scanned stats failed:', error);
      alert(`Failed to save stats: ${error?.message ?? 'Unknown error'}. Please try again.`);
      return; // Don't clear scan state on failure — let the user retry
    }

    setIsReviewingScan(false);
    setScannedStats([]);
    setScannedTeamReport(null);
    setScannedSetScores([]);
    setManualPlayerMap({});
    setStatsMode('none');
    setLocalStatsDraft({});
    localStorage.removeItem('sideoutiq_stats_draft');
  };

  const handleSaveStats = async (onComplete?: () => void) => {
    if (!setEvents && statsMode !== 'edit') return;
    let targetEventId = selectedEventId;
    if (targetEventId === 'all') {
      const newEventId = `manual-${Date.now()}`;
      const newEvent: GameEvent = {
        id: newEventId,
        organizationId: currentOrg?.id || '',
        type: 'game',
        title: 'Manual Stats Entry',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        location: 'Manual Entry',
        opponent: 'Record',
        stats: []
      };
      if (setEvents) setEvents(prev => [...prev, newEvent]);
      targetEventId = newEventId;
    }

    const newStats = buildStatRecords(localStatsDraft, passRatingsInput);

    const ROTATIONAL_STAT_TYPES = ['rotation_point', 'break_point', 'server_point'];

    if (statsMode === 'edit' && editingRecordId) {
      // Replace stats (and set scores if provided) in the match record
      const hasDetailScores = scannedSetScores.length > 0 && scannedSetScores.some(s => s.our > 0 || s.their > 0);
      const hasManualSets = manualSetsWon.our > 0 || manualSetsWon.their > 0;
      const setScoresPatch = hasDetailScores ? {
        setHistory: scannedSetScores,
        ourSetsWon: scannedSetScores.filter(s => s.our > s.their).length,
        theirSetsWon: scannedSetScores.filter(s => s.their > s.our).length,
      } : hasManualSets ? {
        ourSetsWon: manualSetsWon.our,
        theirSetsWon: manualSetsWon.their,
      } : {};
      const existingRecord = matchRecords.find(r => r.id === editingRecordId);
      const preservedRotational = (keepRotationalStats && existingRecord)
        ? (existingRecord.gameState.stats ?? []).filter(s => ROTATIONAL_STAT_TYPES.includes(s.statType))
        : [];
      const statsToSave = [...newStats, ...preservedRotational];
      // Capture provenance before the live stats are overwritten with source='manual'.
      // Preserve an existing originalSource if one was already stamped; otherwise derive
      // from the existing stats so future edits know this record was originally Live/Import.
      const deriveSource = (stats: StatRecord[]): 'live' | 'import' | 'manual' => {
        if (stats.some(s => s.source === 'import')) return 'import';
        if (stats.some(s => s.source === 'manual')) return 'manual';
        return 'live';
      };
      const originalSource = existingRecord?.gameState.originalSource
        ?? (existingRecord ? deriveSource(existingRecord.gameState.stats ?? []) : undefined);
      const provenancePatch = originalSource ? { originalSource } : {};
      if (setMatchRecords) {
        setMatchRecords(prev => prev.map(r =>
          r.id === editingRecordId
            ? { ...r, gameState: { ...r.gameState, stats: statsToSave, ...setScoresPatch, ...provenancePatch } }
            : r
        ));
      }
      if (currentOrg?.id !== 'dev-org-id') {
        try {
          if (existingRecord) {
            const updatedGameState = { ...existingRecord.gameState, stats: statsToSave, ...setScoresPatch, ...provenancePatch };
            await supabase.from('match_records').update({ game_state: updatedGameState }).eq('id', editingRecordId);
          }
        } catch(e) { console.error("DB Save (edit record) failed", e); }
      }
    } else if (statsMode === 'edit' && editingRecordId === null) {
      // Replace event.stats — and if set/result data were entered, promote into a match record
      // (event.stats has no setHistory/ourSetsWon fields, so these need a match record)
      const hasDetailScores = scannedSetScores.length > 0 && scannedSetScores.some(s => s.our > 0 || s.their > 0);
      const hasManualSets = manualSetsWon.our > 0 || manualSetsWon.their > 0;
      if ((hasDetailScores || hasManualSets) && currentUser && currentOrg?.id) {
        const gameState: GameState = { ...buildMinimalGameState(newStats, undefined, hasDetailScores ? scannedSetScores : undefined, hasManualSets && !hasDetailScores ? manualSetsWon : undefined), originalSource: 'manual' };
        if (currentOrg.id !== 'dev-org-id') {
          try {
            const newRecord = await insertMatchRecord(currentOrg.id, targetEventId, currentUser.id, currentUser.fullName, gameState, filteredEvents.find(e => e.id === targetEventId)?.teamId ?? (selectedTeamId !== 'all' ? selectedTeamId : undefined));
            if (setMatchRecords) setMatchRecords(prev => [...prev, newRecord]);
            // Clear inline event.stats now that a match record holds them
            if (setEvents) setEvents(prev => prev.map(e => e.id === targetEventId ? { ...e, stats: [] } : e));
            await supabase.from('events').update({ stats: [] }).eq('id', targetEventId);
          } catch(e) { console.error("DB Save (promote to record) failed", e); }
        } else {
          // Dev mode
          const fakeRecord: MatchRecord = {
            id: `local-mr-${Date.now()}`,
            eventId: targetEventId,
            organizationId: currentOrg.id,
            submittedBy: currentUser?.id || null,
            submittedByName: currentUser?.fullName || 'Local',
            submittedAt: new Date().toISOString(),
            gameState,
            isRecord: true,
          };
          if (setMatchRecords) setMatchRecords(prev => [...prev, fakeRecord]);
          if (setEvents) setEvents(prev => prev.map(e => e.id === targetEventId ? { ...e, stats: [] } : e));
        }
      } else {
        if (setEvents) {
          setEvents(prev => prev.map(e =>
            e.id === targetEventId ? { ...e, stats: newStats } : e
          ));
        }
        if (currentOrg?.id !== 'dev-org-id') {
          try {
            await supabase.from('events').update({ stats: newStats }).eq('id', targetEventId);
          } catch(e) { console.error("DB Save (edit event stats) failed", e); }
        }
      }
    } else {
      // Add manual — create a match_record if there's any data to store, otherwise append to event.stats
      const evt = events.find(e => e.id === targetEventId);
      const hasExisting = (evt?.stats && evt.stats.length > 0) || matchRecords.some(r => r.eventId === targetEventId);
      const hasDetail = scannedSetScores.length > 0 && scannedSetScores.some(s => s.our > 0 || s.their > 0);
      const hasManual = manualSetsWon.our > 0 || manualSetsWon.their > 0;
      const shouldUseRecord = hasExisting || hasDetail || hasManual || newStats.length > 0;

      if (shouldUseRecord && currentUser && currentOrg?.id && currentOrg.id !== 'dev-org-id') {
        try {
          const gameState: GameState = { ...buildMinimalGameState(newStats, undefined, hasDetail ? scannedSetScores : undefined, hasManual && !hasDetail ? manualSetsWon : undefined), originalSource: 'manual' };
          const newRecord = await insertMatchRecord(currentOrg.id, targetEventId, currentUser.id, currentUser.fullName, gameState, filteredEvents.find(e => e.id === targetEventId)?.teamId ?? (selectedTeamId !== 'all' ? selectedTeamId : undefined));
          if (setMatchRecords) setMatchRecords(prev => [...prev, newRecord]);
        } catch(e) { console.error("DB Save (add manual as record) failed", e); }
      } else {
        if (setEvents) {
          setEvents(prev => prev.map(e =>
            e.id === targetEventId ? { ...e, stats: [...(e.stats || []), ...newStats] } : e
          ));
        }
        if (currentOrg?.id !== 'dev-org-id') {
          try {
            const { data: current } = await supabase.from('events').select('stats').eq('id', targetEventId).single();
            const existing = current?.stats || [];
            await supabase.from('events').update({ stats: [...existing, ...newStats] }).eq('id', targetEventId);
          } catch(e) { console.error("DB Save (add manual) failed", e); }
        }
      }
    }

    setLocalStatsDraft({});
    setPassRatingsInput({});
    localStorage.removeItem('sideoutiq_stats_draft');
    setStatsMode('none');
    setEditingRecordId(null);
    setKeepRotationalStats(null);
    onComplete?.();
  };

  const handleCancelEdit = () => {
    setStatsMode('none');
    setIsReviewingScan(false);
    setScannedStats([]);
    setScannedTeamReport(null);
    setScannedSetScores([]);
    setManualPlayerMap({});
    setLocalStatsDraft({});
    setPassRatingsInput({});
    setEditingRecordId(null);
    setKeepRotationalStats(null);
    localStorage.removeItem('sideoutiq_stats_draft');
  };

  const handleDeleteRecord = async (recordId: string) => {
    const record = matchRecords.find(r => r.id === recordId);
    if (!record) return;
    const wasOfficial = record.isRecord;

    // Remove from local state
    if (setMatchRecords) {
      setMatchRecords(prev => {
        const updated = prev.filter(r => r.id !== recordId);
        // If deleted record was official, promote another record for this event
        if (wasOfficial) {
          const remaining = updated.filter(r => r.eventId === record.eventId);
          if (remaining.length > 0) {
            remaining[0].isRecord = true;
          }
        }
        return updated;
      });
    }

    // Delete from DB
    if (currentOrg?.id !== 'dev-org-id') {
      try {
        await supabase.from('match_records').delete().eq('id', recordId);
        // If it was official, promote the next one
        if (wasOfficial) {
          const remaining = matchRecords.filter(r => r.eventId === record.eventId && r.id !== recordId);
          if (remaining.length > 0) {
            await supabase.from('match_records').update({ is_record: true }).eq('id', remaining[0].id);
          }
        }
      } catch (e) { console.error('Delete record failed:', e); }
    }
  };

  const handleClearEventStats = async () => {
    if (!setEvents || selectedEventId === 'all') return;
    setEvents(prev => prev.map(e => e.id === selectedEventId ? { ...e, stats: [] } : e));
    if (currentOrg?.id !== 'dev-org-id') {
      try {
        await supabase.from('events').update({ stats: [] }).eq('id', selectedEventId);
      } catch (e) { console.error('Clear event stats failed:', e); }
    }
  };

  const handleDeleteEvent = async () => {
    if (!setEvents || selectedEventId === 'all') return;
    setEvents(prev => prev.filter(e => e.id !== selectedEventId));
    if (setMatchRecords) setMatchRecords(prev => prev.filter(r => r.eventId !== selectedEventId));
    setSelectedEventIds(new Set());
    setShowDeleteEventConfirm(false);
    setShowDestructiveMenu(false);
    if (currentOrg?.id !== 'dev-org-id') {
      try {
        await supabase.from('match_records').delete().eq('event_id', selectedEventId);
        await supabase.from('events').delete().eq('id', selectedEventId);
      } catch (e) { console.error('Delete event failed:', e); }
    }
  };

  const handleReassignStats = async () => {
    if (!currentOrg?.id) return;
    let targetEventId = reassignTargetEventId;

    if (reassignMode === 'new') {
      if (!reassignNewOpponent) return;
      const newEventId = crypto.randomUUID();
      const newDate = reassignNewDate || new Date().toISOString().split('T')[0];
      const newEvent: GameEvent = {
        id: newEventId,
        organizationId: currentOrg.id,
        type: 'game',
        title: `vs ${reassignNewOpponent}`,
        date: newDate,
        time: 'TBD',
        location: 'TBD',
        opponent: reassignNewOpponent,
        stats: [],
      };
      if (setEvents) setEvents(prev => [...prev, newEvent]);
      targetEventId = newEventId;
      if (currentOrg.id !== 'dev-org-id') {
        try {
          await supabase.from('events').insert([{
            id: newEventId,
            organization_id: currentOrg.id,
            type: 'game',
            title: `vs ${reassignNewOpponent}`,
            date: newDate,
            time: 'TBD',
            location: 'TBD',
            opponent: reassignNewOpponent,
          }]);
        } catch(e) { console.error('Create event for reassign failed', e); }
      }
    }

    if (!targetEventId) return;

    if (reassignRecordId !== null) {
      // Move a specific match record to target event
      if (setMatchRecords) {
        setMatchRecords(prev => prev.map(r =>
          r.id === reassignRecordId ? { ...r, eventId: targetEventId } : r
        ));
      }
      if (currentOrg.id !== 'dev-org-id') {
        try {
          await supabase.from('match_records').update({ event_id: targetEventId }).eq('id', reassignRecordId);
        } catch(e) { console.error('Reassign match record failed', e); }
      }
    } else {
      // Move event.stats from current event to target event
      const srcEvt = events.find(e => e.id === selectedEventId);
      const statsToMove = srcEvt?.stats ?? [];
      if (setEvents && statsToMove.length > 0) {
        setEvents(prev => prev.map(e => {
          if (e.id === selectedEventId) return { ...e, stats: [] };
          if (e.id === targetEventId) return { ...e, stats: [...(e.stats || []), ...statsToMove] };
          return e;
        }));
      }
      if (currentOrg.id !== 'dev-org-id') {
        try {
          await supabase.from('events').update({ stats: [] }).eq('id', selectedEventId);
          const { data: target } = await supabase.from('events').select('stats').eq('id', targetEventId).single();
          const existing = target?.stats || [];
          await supabase.from('events').update({ stats: [...existing, ...statsToMove] }).eq('id', targetEventId);
        } catch(e) { console.error('Reassign event stats failed', e); }
      }
    }

    setSelectedEventIds(new Set([targetEventId]));
    setShowReassignModal(false);
    setShowActionModal(false);
    setReassignRecordId(null);
    setReassignTargetEventId('');
    setReassignNewOpponent('');
    setReassignNewDate('');
  };

  const handleClearStats = async () => {
    if (!setEvents || selectedEventId === 'all') return;
    // Clear evt.stats
    setEvents(prev => prev.map(e => e.id === selectedEventId ? { ...e, stats: [] } : e));
    // Clear any match records for this event
    if (setMatchRecords) {
      setMatchRecords(prev => prev.map(r =>
        r.eventId === selectedEventId ? { ...r, gameState: { ...r.gameState, stats: [] } } : r
      ));
    }
    if (currentOrg?.id !== 'dev-org-id') {
      try {
        await supabase.from('events').update({ stats: [] }).eq('id', selectedEventId);
        // Clear stats in all match records for this event
        const eventRecords = matchRecords.filter(r => r.eventId === selectedEventId);
        for (const record of eventRecords) {
          const updatedGameState = { ...record.gameState, stats: [] };
          await supabase.from('match_records').update({ game_state: updatedGameState }).eq('id', record.id);
        }
      } catch (e) { console.error('Clear stats failed:', e); }
    }
    setShowClearConfirm(false);
    setShowDestructiveMenu(false);
  };

  // ─── Columns ───────────────────────────────────────────────────────────────
  const getColumns = () => {
      const common = [
          { header: 'Player', key: 'player.name', render: (row: any) => (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 overflow-hidden border border-slate-200">
                {row.player.photoUrl ? <img src={row.player.photoUrl} className="w-full h-full object-cover"/> : row.player.jerseyNumber || '#'}
              </div>
              <div>
                <div className="font-bold text-slate-900">{row.player.name}</div>
                <div className="text-[10px] text-slate-400">{row.player.position}</div>
              </div>
            </div>
          )},
          { header: RH('MP', 'Matches Played'), key: 'matchesPlayed', tooltip: 'Matches (events) played' },
          { header: RH('SP', 'Sets Played'), key: 'gamesPlayed', tooltip: 'Sets Played' }
      ];
      const formatDec = (n: number) => n.toFixed(2);
      const formatPct = (n: number) => (n * 100).toFixed(1) + '%';

      if (!isEditing) {
          switch(activeCategory) {
              case 'Attacking': return [...common,
                { header: RH('K/G', 'Kills/Set'), key: 'K/G', tooltip: STAT_DEFINITIONS['K/G'], sectionStart: true, render: (r: any) => formatDec(r.gamesPlayed ? r.kills / r.gamesPlayed : 0) },
                { header: 'K',   key: 'kills',         tooltip: STAT_DEFINITIONS['K'] },
                { header: 'E',   key: 'attackErrors',  tooltip: STAT_DEFINITIONS['E'] },
                { header: 'ATT', key: 'attackAttempts',tooltip: STAT_DEFINITIONS['ATT'] },
                { header: 'H %', key: 'H%', tooltip: STAT_DEFINITIONS['H %'], render: (r: any) => r.attackAttempts ? ((r.kills - r.attackErrors) / r.attackAttempts * 100).toFixed(1) + '%' : '—', highlight: true },
                { header: 'K%',  key: 'K%', tooltip: STAT_DEFINITIONS['K%'], render: (r: any) => formatPct(r.attackAttempts ? r.kills / r.attackAttempts : 0) }];
              case 'Serving': return [...common,
                { header: RH('ACE/G', 'Aces/Set'), key: 'A/G', tooltip: STAT_DEFINITIONS['ACE/G'], sectionStart: true, render: (r: any) => formatDec(r.gamesPlayed ? r.aces / r.gamesPlayed : 0) },
                { header: 'ACE',   key: 'aces',        tooltip: STAT_DEFINITIONS['ACE'] },
                { header: 'SVE',   key: 'serveErrors', tooltip: STAT_DEFINITIONS['SVE_SERVE'] },
                { header: 'S ATT', key: 'serveAttempts',tooltip: STAT_DEFINITIONS['S ATT_SERVE'] },
                { header: 'ACE %', key: 'Ace %', tooltip: STAT_DEFINITIONS['ACE %'], render: (r: any) => formatPct(r.serveAttempts ? r.aces / r.serveAttempts : 0), highlight: true },
                { header: 'SRV %', key: 'Srv%', tooltip: STAT_DEFINITIONS['SRV %'], render: (r: any) => r.serveAttempts > 0 ? formatPct((r.serveAttempts - r.serveErrors) / r.serveAttempts) : '—', highlight: true },
                { header: <ColTooltip text={STAT_DEFINITIONS['PS%']}><span className="flex items-center gap-0.5">PS% <HelpCircle className="w-3 h-3 opacity-60" /></span></ColTooltip>, key: 'PS%', tooltip: STAT_DEFINITIONS['PS%'], render: (r: any) => hasServerPointData && r.serveAttempts > 0 ? formatPct((r.aces + r.serverPoints) / r.serveAttempts) : '—', highlight: true }];
              case 'Blocking': return [...common,
                { header: RH('BLK/G', 'Blocks/Set'), key: 'B/G', tooltip: STAT_DEFINITIONS['B/G'], sectionStart: true, render: (r: any) => formatDec(r.gamesPlayed ? (r.blockSolo + r.blockAssist) / r.gamesPlayed : 0), highlight: true },
                { header: 'BLK', key: 'B',           tooltip: STAT_DEFINITIONS['BLK'], render: (r: any) => r.blockSolo + r.blockAssist },
                { header: 'BS',  key: 'blockSolo',   tooltip: STAT_DEFINITIONS['BS'] },
                { header: 'BA',  key: 'blockAssist', tooltip: STAT_DEFINITIONS['BA'] },
                { header: 'BE',  key: 'blockErrors', tooltip: STAT_DEFINITIONS['BE'] }];
              case 'Digging': return [...common,
                { header: RH('D/G', 'Digs/Set'), key: 'D/G', tooltip: STAT_DEFINITIONS['D/G'], sectionStart: true, render: (r: any) => formatDec(r.gamesPlayed ? r.digs / r.gamesPlayed : 0), highlight: true },
                { header: 'D',  key: 'digs',     tooltip: STAT_DEFINITIONS['D'] },
                { header: 'DE', key: 'digErrors', tooltip: STAT_DEFINITIONS['DE'] }];
              case 'Setting': return [...common,
                { header: RH('AST/G', 'Asst/Set'), key: 'AST/G', tooltip: STAT_DEFINITIONS['AST/G'], sectionStart: true, render: (r: any) => formatDec(r.gamesPlayed ? r.assists / r.gamesPlayed : 0), highlight: true },
                { header: 'AST', key: 'assists',             tooltip: STAT_DEFINITIONS['AST'] },
                { header: 'SE',  key: 'ballHandlingErrors',  tooltip: STAT_DEFINITIONS['SE_SET'] },
                { header: 'SA',  key: 'ballHandlingAttempts',tooltip: STAT_DEFINITIONS['SA_SET'] }];
              case 'Serve Receiving': return [...common,
                { header: RH('SRG', 'Rec/Set'), key: 'SRG', tooltip: STAT_DEFINITIONS['SRG'], sectionStart: true, render: (r: any) => formatDec(r.gamesPlayed ? r.receptions / r.gamesPlayed : 0), highlight: true },
                { header: 'AVG',    key: 'AVG',             tooltip: STAT_DEFINITIONS['AVG'],    render: (r: any) => formatDec(r.receptions ? r.receptionSum / r.receptions : 0), highlight: true },
                { header: 'R',      key: 'receptions',      tooltip: STAT_DEFINITIONS['R'] },
                { header: 'RE',     key: 'receptionErrors', tooltip: STAT_DEFINITIONS['RE'] },
                { header: 'SR+',    key: 'srPerfect',       tooltip: STAT_DEFINITIONS['SR+'] },
                { header: 'SR~',    key: 'srGood',          tooltip: STAT_DEFINITIONS['SR~'] },
                { header: 'SR-',    key: 'srPoor',          tooltip: STAT_DEFINITIONS['SR-'] }];
              default: return [...common,
                { header: 'K',     key: 'kills',   tooltip: STAT_DEFINITIONS['K'],  sectionStart: true },
                { header: 'H %',   key: 'H%',      tooltip: STAT_DEFINITIONS['H %'], render: (r: any) => r.attackAttempts ? ((r.kills - r.attackErrors) / r.attackAttempts * 100).toFixed(1) + '%' : '—', highlight: true },
                { header: 'ACE',   key: 'aces',    tooltip: STAT_DEFINITIONS['ACE'], sectionStart: true },
                { header: 'SRV %', key: 'Srv%',    tooltip: STAT_DEFINITIONS['SRV %'], render: (r: any) => r.serveAttempts > 0 ? formatPct((r.serveAttempts - r.serveErrors) / r.serveAttempts) : '—', highlight: true },
                { header: <ColTooltip text={STAT_DEFINITIONS['PS%']}><span className="flex items-center gap-0.5">PS% <HelpCircle className="w-3 h-3 opacity-60" /></span></ColTooltip>, key: 'PS%', tooltip: STAT_DEFINITIONS['PS%'], render: (r: any) => hasLiveTrackingData && r.serveAttempts > 0 ? formatPct((r.aces + r.rotationPoints) / r.serveAttempts) : '—', highlight: true },
                { header: 'BLK',   key: 'B',       tooltip: STAT_DEFINITIONS['BLK'], render: (r: any) => r.blockSolo + r.blockAssist, sectionStart: true },
                { header: 'D',     key: 'digs',    tooltip: STAT_DEFINITIONS['D'],  sectionStart: true },
                { header: 'AST',   key: 'assists', tooltip: STAT_DEFINITIONS['AST'], sectionStart: true },
                { header: 'SR AVG', key: 'AVG',    tooltip: STAT_DEFINITIONS['AVG'], render: (r: any) => r.receptions > 0 ? formatDec(r.receptionSum / r.receptions) : '—', highlight: true, sectionStart: true }];
          }
      } else {
          // Edit / Add Mode — expanded columns with computed K% and Pass AVG
          const numInput = (playerId: string, statKey: string) => {
            const val = localStatsDraft[playerId]?.[statKey] || 0;
            return (
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min="0"
                className="w-14 p-1.5 text-center border-2 border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-600 outline-none bg-white text-slate-900 font-bold shadow-sm"
                value={val}
                onChange={(e) => handleStatChange(playerId, statKey, parseInt(e.target.value) || 0)}
              />
            );
          };

          return [
            common[0],
            { header: 'K',   key: 'edit_kills',          tooltip: 'Kills', sectionStart: true, render: (row: any) => numInput(row.playerId, 'kills') },
            { header: 'E',   key: 'edit_attackErrors',   tooltip: 'Attack Errors', render: (row: any) => numInput(row.playerId, 'attackErrors') },
            { header: 'ATT', key: 'edit_attackAttempts', tooltip: 'Total Attacks', render: (row: any) => numInput(row.playerId, 'attackAttempts') },
            { header: 'K%',  key: 'edit_killPct',        tooltip: 'Kill % (computed)', highlight: true, render: (row: any) => {
              const d = localStatsDraft[row.playerId] || {};
              const att = d.attackAttempts || 0;
              const k = d.kills || 0;
              return <span className="font-bold text-blue-700">{att > 0 ? (k / att * 100).toFixed(1) + '%' : '—'}</span>;
            }},
            { header: 'ACE',   key: 'edit_aces',          tooltip: 'Aces', sectionStart: true, render: (row: any) => numInput(row.playerId, 'aces') },
            { header: 'S ATT', key: 'edit_serveAttempts', tooltip: 'Serve Attempts', render: (row: any) => numInput(row.playerId, 'serveAttempts') },
            { header: 'SVE',   key: 'edit_serveErrors',   tooltip: 'Serve Errors', render: (row: any) => numInput(row.playerId, 'serveErrors') },
            { header: 'BS',    key: 'edit_blockSolo',     tooltip: 'Block Solo', sectionStart: true, render: (row: any) => numInput(row.playerId, 'blockSolo') },
            { header: 'BA',    key: 'edit_blockAssist',   tooltip: 'Block Assist', render: (row: any) => numInput(row.playerId, 'blockAssist') },
            { header: 'D',     key: 'edit_digs',          tooltip: 'Digs', sectionStart: true, render: (row: any) => numInput(row.playerId, 'digs') },
            { header: 'DE',    key: 'edit_digErrors',     tooltip: 'Dig Errors', render: (row: any) => numInput(row.playerId, 'digErrors') },
            { header: 'AST',   key: 'edit_assists',       tooltip: 'Assists', sectionStart: true, render: (row: any) => numInput(row.playerId, 'assists') },
            { header: 'SR+', key: 'edit_srPerfect', tooltip: 'Perfect passes (3-ball)', sectionStart: true, render: (row: any) => numInput(row.playerId, 'srPerfect') },
            { header: 'SR~', key: 'edit_srGood', tooltip: 'Good passes (2-ball)', render: (row: any) => numInput(row.playerId, 'srGood') },
            { header: 'SR-', key: 'edit_srPoor', tooltip: 'Poor passes (1-ball)', render: (row: any) => numInput(row.playerId, 'srPoor') },
            { header: 'SR ERR', key: 'edit_srError', tooltip: 'Reception errors', render: (row: any) => numInput(row.playerId, 'srError') },
            { header: 'R', key: 'edit_receptions_computed', tooltip: 'Total receptions (computed: SR+ + SR~ + SR-)', render: (row: any) => {
              const d = localStatsDraft[row.playerId] || {};
              const total = (d.srPerfect || 0) + (d.srGood || 0) + (d.srPoor || 0);
              return <span className="font-medium text-slate-700">{total || '—'}</span>;
            }},
            { header: 'AVG', key: 'edit_passAvg', tooltip: 'Pass average (computed)', highlight: true, render: (row: any) => {
              const d = localStatsDraft[row.playerId] || {};
              const srP = d.srPerfect || 0;
              const srG = d.srGood || 0;
              const srPo = d.srPoor || 0;
              const total = srP + srG + srPo;
              const avg = total > 0 ? (3 * srP + 2 * srG + 1 * srPo) / total : 0;
              return <span className="font-bold text-blue-700">{total > 0 ? avg.toFixed(2) : '—'}</span>;
            }},
          ];
      }
  };

  // ─── Column group headers for section labels ──────────────────────────────
  const SECTION_COLORS: Record<string, string> = {
    Hitting:        'bg-blue-50 text-blue-700 border-blue-200',
    Serving:        'bg-emerald-50 text-emerald-700 border-emerald-200',
    Blocking:       'bg-orange-50 text-orange-700 border-orange-200',
    Dig:            'bg-slate-100 text-slate-600 border-slate-300',
    Assist:         'bg-slate-100 text-slate-600 border-slate-300',
    'Serve Receive':'bg-violet-50 text-violet-700 border-violet-200',
  };
  type GroupHeader = { label: string; colSpan: number };
  const getGroupHeaders = (): GroupHeader[] => {
    const playerCols = isEditing ? 1 : 3; // edit: Player only; view: Player+MP+SP
    const player: GroupHeader = { label: '', colSpan: playerCols };
    if (isEditing) return [player,
      { label: 'Hitting',        colSpan: 4 },
      { label: 'Serving',        colSpan: 3 },
      { label: 'Blocking',       colSpan: 2 },
      { label: 'Dig',            colSpan: 2 },
      { label: 'Assist',         colSpan: 1 },
      { label: 'Serve Receive',  colSpan: 6 },
    ];
    switch (activeCategory) {
      case 'Attacking':      return [player, { label: 'Hitting',       colSpan: 6 }];
      case 'Serving':        return [player, { label: 'Serving',       colSpan: 6 }];
      case 'Blocking':       return [player, { label: 'Blocking',      colSpan: 5 }];
      case 'Digging':        return [player, { label: 'Dig',           colSpan: 3 }];
      case 'Setting':        return [player, { label: 'Assist',        colSpan: 4 }];
      case 'Serve Receiving':return [player, { label: 'Serve Receive', colSpan: 7 }];
      default: return [player, // Summary
        { label: 'Hitting',       colSpan: 2 },
        { label: 'Serving',       colSpan: 3 },
        { label: 'Blocking',      colSpan: 1 },
        { label: 'Dig',           colSpan: 1 },
        { label: 'Assist',        colSpan: 1 },
        { label: 'Serve Receive', colSpan: 1 },
      ];
    }
  };

  // ─── MaxPreps Export ───────────────────────────────────────────────────────
  const exportToMaxPreps = () => {
    const headers = ['Jersey Number', 'Player Name', 'SP', 'K', 'E', 'ATT', 'A', 'ACE', 'SE', 'D', 'BS', 'BA', 'R', 'RE'];
    const rows = sortedData
      .filter(row => row.playerId !== '__team__')
      .map(row => [
        row.player.jerseyNumber,
        row.player.name,
        row.gamesPlayed,
        row.kills,
        row.attackErrors,
        row.attackAttempts,
        row.assists,
        row.aces,
        row.serveErrors,
        row.digs,
        row.blockSolo,
        row.blockAssist,
        row.receptions,
        row.receptionErrors,
      ]);
    const tsv = [headers, ...rows].map(r => r.join('\t')).join('\n');
    const blob = new Blob([tsv], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Derived values for selected event label ───────────────────────────────
  const selectedEventLabel = useMemo(() => {
    if (selectedEventIds.size === 0) return 'All Games';
    if (selectedEventIds.size === 1) {
      const id = [...selectedEventIds][0];
      const evt = filteredEvents.find(e => e.id === id) ?? events.find(e => e.id === id);
      return evt ? `${evt.date} vs ${evt.opponent || 'Opponent'}` : 'Selected Game';
    }
    return `${selectedEventIds.size} games selected`;
  }, [selectedEventIds, filteredEvents, events]);

  return (
    <div className="space-y-6 pb-12 relative">

      {/* ── Live data recovery banner — only shown when cached stats exist ────── */}

      {/* ── Success Toast ─────────────────────────────────────────────────── */}
      {saveSuccessToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-green-700 text-white px-5 py-3 rounded-xl shadow-2xl text-sm font-medium animate-fade-in">
          <Check className="w-4 h-4 shrink-0" />
          {saveSuccessToast}
        </div>
      )}

      {/* ── Scan upload loading overlay ────────────────────────────────────── */}
      {isUploading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-center gap-4">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3 max-w-xs w-full mx-4">
            <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
            <p className="font-bold text-slate-900 text-base">Analyzing Image…</p>
            <p className="text-xs text-slate-500 text-center">SideoutIQ is extracting player stats from your upload. This may take a minute.</p>
          </div>
        </div>
      )}

      {/* ── Create Game Modal ─────────────────────────────────────────────── */}
      {showCreateGameModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Add Game Record</h3>
              <button onClick={() => setShowCreateGameModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Opponent</label>
                <input type="text" placeholder="Team Name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" value={newGameForm.opponent} onChange={e => setNewGameForm(p => ({...p, opponent: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" value={newGameForm.date} onChange={e => setNewGameForm(p => ({...p, date: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Result</label>
                <div className="flex gap-3 items-center">
                  <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" value={newGameForm.winner} onChange={e => setNewGameForm(p => ({...p, winner: e.target.value as 'us' | 'them'}))}>
                    <option value="us">Win</option>
                    <option value="them">Loss</option>
                  </select>
                  <input type="number" min="0" className="w-16 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-center" value={newGameForm.scoreUs} onChange={e => setNewGameForm(p => ({...p, scoreUs: parseInt(e.target.value)||0}))} />
                  <span className="text-slate-500 font-bold">–</span>
                  <input type="number" min="0" className="w-16 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-center" value={newGameForm.scoreThem} onChange={e => setNewGameForm(p => ({...p, scoreThem: parseInt(e.target.value)||0}))} />
                </div>
              </div>
            </div>
              {/* Tournament checkbox */}
              <div className="border-t border-slate-100 pt-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-blue-600"
                    checked={newGameForm.isTournament}
                    onChange={e => setNewGameForm(p => ({ ...p, isTournament: e.target.checked, tournamentId: '', tournamentName: '' }))}
                  />
                  <span className="text-sm font-medium text-slate-700">Part of a tournament</span>
                </label>
                {newGameForm.isTournament && (
                  <div className="space-y-2 pl-6">
                    {existingTournaments.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Select existing tournament</label>
                        <select
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                          value={newGameForm.tournamentId}
                          onChange={e => {
                            const sel = existingTournaments.find(t => t.id === e.target.value);
                            setNewGameForm(p => ({ ...p, tournamentId: e.target.value, tournamentName: sel ? sel.name : p.tournamentName }));
                          }}
                        >
                          <option value="">— New tournament —</option>
                          {existingTournaments.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {!newGameForm.tournamentId && (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Tournament name</label>
                        <input
                          type="text"
                          placeholder="e.g. Regional Championship"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                          value={newGameForm.tournamentName}
                          onChange={e => setNewGameForm(p => ({ ...p, tournamentName: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowCreateGameModal(false)} className="flex-1 py-2 rounded-lg border border-slate-300 text-slate-600 font-medium text-sm hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreateGame} className="flex-1 py-2 rounded-lg bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 shadow-sm">Create & Add Stats</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reassign Stats Modal ──────────────────────────────────────────── */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Reassign Stats</h3>
                <p className="text-sm text-slate-500 mt-0.5">Move these stats to the correct game</p>
              </div>
              <button onClick={() => setShowReassignModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setReassignMode('existing')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${reassignMode === 'existing' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  Scheduled Game
                </button>
                <button
                  onClick={() => setReassignMode('new')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${reassignMode === 'new' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  New Game
                </button>
              </div>

              {reassignMode === 'existing' ? (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Select the correct game</label>
                  <select
                    value={reassignTargetEventId}
                    onChange={e => setReassignTargetEventId(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">— Pick a game —</option>
                    {events
                      .filter(e => isGameType(e.type) && e.id !== selectedEventId)
                      .sort((a, b) => b.date.localeCompare(a.date))
                      .map(e => (
                        <option key={e.id} value={e.id}>{e.date} vs {e.opponent || 'Opponent'}</option>
                      ))
                    }
                  </select>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Opponent name</label>
                    <input
                      type="text"
                      placeholder="e.g. Riverside High"
                      value={reassignNewOpponent}
                      onChange={e => setReassignNewOpponent(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Date</label>
                    <input
                      type="date"
                      value={reassignNewDate}
                      onChange={e => setReassignNewDate(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-400">The stat record will be moved to the selected game and will appear under that game on the Stats page.</p>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowReassignModal(false)}
                  className="flex-1 py-2 rounded-lg border border-slate-300 text-slate-600 font-medium text-sm hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassignStats}
                  disabled={reassignMode === 'existing' ? !reassignTargetEventId : !reassignNewOpponent}
                  className="flex-1 py-2 rounded-lg bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reassign Stats
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Modal ──────────────────────────────────────────────────── */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Game Stats</h3>
                <p className="text-sm text-slate-500 mt-0.5">{selectedEventLabel}</p>
              </div>
              <button onClick={() => { setShowActionModal(false); setConfirmDeleteRecordId(null); }} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">

              {/* ── Existing Records ── */}
              {(() => {
                const evt = filteredEvents.find(e => e.id === selectedEventId);
                const hasEventStats = (evt?.stats?.length ?? 0) > 0;
                const hasAnyRecords = selectedEventRecords.length > 0 || hasEventStats;
                if (!hasAnyRecords) return null;

                // Determine the source label for event.stats
                const eventStatsSource = evt?.stats?.some(s => s.source === 'import') ? 'Import' :
                  evt?.stats?.some(s => s.source === 'manual') ? 'Manual' :
                  evt?.stats?.some(s => s.source === 'live') ? 'SideoutIQ Game Time' : 'Stats';
                const eventStatsCount = evt?.stats?.length ?? 0;

                return (
                  <div className="border border-slate-200 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-slate-100 p-2 rounded-lg"><Database className="w-5 h-5 text-slate-600" /></div>
                      <div>
                        <div className="font-bold text-slate-900 text-sm">Records for this Game</div>
                        <div className="text-xs text-slate-500">{selectedEventRecords.length > 1 ? 'Click the star to set which record is official' : 'Manage stat records'}</div>
                      </div>
                    </div>

                    {/* Event.stats row (inline stats not in a match_record) */}
                    {hasEventStats && (
                      <div className={`flex items-center gap-2 p-2.5 rounded-lg border transition-colors ${selectedEventRecords.length === 0 ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200 bg-slate-50'}`}>
                        {selectedEventRecords.length === 0 ? (
                          <Star className="w-4 h-4 flex-shrink-0 text-yellow-500 fill-yellow-500" />
                        ) : (
                          <div className="w-4 h-4 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-slate-800">{eventStatsSource}</span>
                          <span className="text-xs text-slate-400 ml-2">{eventStatsCount} records</span>
                        </div>
                        {canEdit && <button
                          onClick={() => {
                            if (eventStatsSource === 'Live') {
                              setPendingLiveEditId(null);
                              setShowLiveEditWarning(true);
                            } else {
                              handleStartEdit(null);
                            }
                          }}
                          className="text-xs px-2.5 py-1 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium transition-colors"
                        >
                          Edit
                        </button>}
                        {canEdit && <button
                          onClick={() => { setReassignRecordId(null); setReassignMode('existing'); setReassignTargetEventId(''); setReassignNewOpponent(''); setReassignNewDate(new Date().toISOString().split('T')[0]); setShowReassignModal(true); }}
                          className="text-xs px-2 py-1 rounded-md text-indigo-500 hover:bg-indigo-50 transition-colors shrink-0"
                          title="Reassign to different game"
                        >
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>}
                        {canEdit && (confirmDeleteRecordId === '__event_stats__' ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={async () => { await handleClearEventStats(); setConfirmDeleteRecordId(null); }}
                              className="text-xs px-2 py-1 rounded-md bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                            >Delete</button>
                            <button onClick={() => setConfirmDeleteRecordId(null)} className="text-xs px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 transition-colors">Cancel</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteRecordId('__event_stats__')}
                            className="text-xs px-2 py-1 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Compare button — only when 2+ records exist */}
                    {selectedEventRecords.length >= 2 && (
                      <button
                        onClick={() => { setDiffViewerRecords([selectedEventRecords[0], selectedEventRecords[1]]); setDiffViewerInitialSources(undefined); }}
                        className="w-full py-2 text-sm font-medium rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M8 6H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3"/><path d="M16 6h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-3"/><line x1="12" y1="2" x2="12" y2="22"/></svg>
                        Compare Records
                      </button>
                    )}

                    {/* Match record rows */}
                    {selectedEventRecords.map(rec => {
                      const isMerged = !!rec.gameState.mergeOf;
                      const recSource = isMerged ? 'Merged' :
                        rec.gameState.stats?.some(s => s.source === 'import') ? 'Import' :
                        rec.gameState.stats?.some(s => s.source === 'manual') ? 'Manual' :
                        rec.gameState.stats?.some(s => s.source === 'live') ? 'SideoutIQ Game Time' : 'Record';
                      const recDate = formatDate(new Date(rec.submittedAt));
                      const statCount = rec.gameState.stats?.length ?? 0;

                      return (
                        <div key={rec.id} className={`flex items-center gap-2 p-2.5 rounded-lg border transition-colors ${rec.isRecord ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                          {canEdit ? (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (rec.isRecord) return; // already official
                              try {
                                await setMatchOfRecord(rec.id, rec.eventId);
                                if (setMatchRecords) {
                                  setMatchRecords(prev => prev.map(r =>
                                    r.eventId === rec.eventId
                                      ? { ...r, isRecord: r.id === rec.id }
                                      : r
                                  ));
                                }
                              } catch(err) { console.error("Set record failed", err); }
                            }}
                            className="flex-shrink-0 p-0.5 rounded hover:bg-yellow-100 transition-colors"
                            title={rec.isRecord ? 'Official record' : 'Set as official'}
                          >
                            <Star className={`w-4 h-4 ${rec.isRecord ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300 hover:text-yellow-400'}`} />
                          </button>
                        ) : (
                          <Star className={`w-4 h-4 flex-shrink-0 ${rec.isRecord ? 'text-yellow-500 fill-yellow-500' : 'text-slate-200'}`} />
                        )}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-800">{recSource}</span>
                            <span className="text-xs text-slate-400 ml-1.5">{recDate}</span>
                            <span className="text-xs text-slate-400 ml-1.5">({statCount})</span>
                            {rec.submittedByName && <span className="text-xs text-slate-400 ml-1.5">by {rec.submittedByName}</span>}
                          </div>
                          {rec.isRecord && <span className="text-[10px] font-bold bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full shrink-0">Official</span>}
                          {isMerged && (() => {
                            const mo = rec.gameState.mergeOf!;
                            const srcA = matchRecords.find(r => r.id === mo.recordAId);
                            const srcB = matchRecords.find(r => r.id === mo.recordBId);
                            if (!srcA || !srcB) return null;
                            return (
                              <button
                                onClick={() => { setDiffViewerRecords([srcA, srcB]); setDiffViewerInitialSources(mo.sources); }}
                                className="text-xs px-2.5 py-1 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 font-medium transition-colors shrink-0"
                                title="View how this merge was built"
                              >
                                View Merge
                              </button>
                            );
                          })()}
                          {canEdit && !isMerged && <button
                            onClick={() => {
                              if (recSource === 'Live') {
                                setPendingLiveEditId(rec.id);
                                setShowLiveEditWarning(true);
                              } else {
                                handleStartEdit(rec.id);
                              }
                            }}
                            className="text-xs px-2.5 py-1 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium transition-colors shrink-0"
                          >
                            Edit
                          </button>}
                          {canEdit && <button
                            onClick={() => { setReassignRecordId(rec.id); setReassignMode('existing'); setReassignTargetEventId(''); setReassignNewOpponent(''); setReassignNewDate(new Date().toISOString().split('T')[0]); setShowReassignModal(true); }}
                            className="text-xs px-2 py-1 rounded-md text-indigo-500 hover:bg-indigo-50 transition-colors shrink-0"
                            title="Reassign to different game"
                          >
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>}
                          {canEdit && (confirmDeleteRecordId === rec.id ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={async () => { await handleDeleteRecord(rec.id); setConfirmDeleteRecordId(null); }}
                                className="text-xs px-2 py-1 rounded-md bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                              >Delete</button>
                              <button onClick={() => setConfirmDeleteRecordId(null)} className="text-xs px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 transition-colors">Cancel</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteRecordId(rec.id)}
                              className="text-xs px-2 py-1 rounded-md text-red-500 hover:bg-red-50 transition-colors shrink-0"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Add Manual */}
              {canEdit && <div className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg"><Plus className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Enter Manually</div>
                    <div className="text-xs text-slate-500">{selectedEventHasStats ? 'Add a new separate record for this game' : 'Type in stats from a stat sheet or estimate'}</div>
                  </div>
                </div>
                <button onClick={handleStartAddManual} className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-sm transition-colors">
                  Start Manual Entry
                </button>
              </div>}

              {/* Scan / Import */}
              {canEdit && <div className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-purple-100 p-2 rounded-lg"><Sparkles className="w-5 h-5 text-purple-600" /></div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Scan / Import</div>
                    <div className="text-xs text-slate-500">{selectedEventHasStats ? 'Upload stats as a new separate record' : 'Upload a photo or CSV of a stat sheet — AI will parse it'}</div>
                  </div>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {isUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><Upload className="w-4 h-4" /> Upload File</>}
                </button>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*,application/pdf,.csv" multiple onChange={handleFileUpload} />
              </div>}

            </div>
          </div>
        </div>
      )}

      {/* ── Live Edit Warning Modal ──────────────────────────────────────────── */}
      {showLiveEditWarning && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-start gap-3">
              <div className="bg-amber-100 p-2 rounded-lg shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Editing live-tracked stats</h3>
                <p className="text-sm text-slate-500 mt-1">This game was tracked with GameTime live scoring, which includes advanced rotation and point scoring data. Manually editing these stats will overwrite that data and the rotational breakdown will be lost.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 p-4">
              <button
                onClick={() => {
                  setShowLiveEditWarning(false);
                  handleStartEdit(pendingLiveEditId ?? null);
                  setPendingLiveEditId(undefined);
                }}
                className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-sm transition-colors"
              >
                Confirm Edit (lose rotational stats)
              </button>
              <button
                onClick={() => { setShowLiveEditWarning(false); setPendingLiveEditId(undefined); }}
                className="w-full py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl text-sm hover:bg-slate-50 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header Row ────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Statistics</h2>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <p className="text-slate-500 text-sm">Analyze performance across {selectedSeason === 'all' ? 'All Seasons' : selectedSeason}.</p>
            {(teamRecord.wins > 0 || teamRecord.losses > 0 || teamRecord.ties > 0) && (
              <span className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-3 py-0.5 text-sm font-bold text-slate-700">
                <Trophy className="w-3.5 h-3.5 text-amber-500" />
                {teamRecord.wins}–{teamRecord.losses}{teamRecord.ties > 0 ? `–${teamRecord.ties}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <button
                onClick={handleCancelEdit}
                className="text-slate-500 hover:text-slate-700 font-medium text-sm flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              {!isReviewingScan && (
                <button
                  onClick={() => setShowSaveConfirm(true)}
                  className="bg-green-600 text-white border border-green-700 px-4 py-2 rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 transition-colors text-sm shadow-sm"
                >
                  <Save className="w-4 h-4" /> Save Stats
                </button>
              )}
              {isReviewingScan && (
                <button
                  onClick={handleSaveScannedStats}
                  className="bg-green-600 text-white border border-green-700 px-4 py-2 rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 transition-colors text-sm shadow-sm"
                >
                  <Check className="w-4 h-4" /> Confirm & Save
                </button>
              )}
            </>
          ) : (
            <>
              {ENABLE_MAXPREPS_EXPORT && (
                <button
                  onClick={exportToMaxPreps}
                  disabled={selectedEventId === 'all'}
                  title={selectedEventId === 'all' ? 'Select a specific game to export stats for MaxPreps.' : 'Downloads the formatted import.txt file to upload directly to MaxPreps.'}
                  className={`bg-white border px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm ${
                    selectedEventId === 'all'
                      ? 'border-slate-200 text-slate-400 opacity-50 cursor-not-allowed'
                      : 'border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300'
                  }`}
                >
                  <Download className="w-4 h-4" /> MaxPreps Export
                </button>
              )}
              {isAdmin && selectedEventId !== 'all' && (
                showDeleteEventConfirm ? (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <span className="text-sm text-red-700 font-medium">Permanently delete this game and all its data?</span>
                    <button onClick={handleDeleteEvent} className="bg-red-600 text-white px-3 py-1 rounded-md text-sm font-bold hover:bg-red-700 transition-colors">Yes, delete</button>
                    <button onClick={() => { setShowDeleteEventConfirm(false); setShowDestructiveMenu(true); }} className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors">Back</button>
                  </div>
                ) : showClearConfirm ? (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <span className="text-sm text-red-700 font-medium">Clear all stats for this game?</span>
                    <button
                      onClick={handleClearStats}
                      className="bg-red-600 text-white px-3 py-1 rounded-md text-sm font-bold hover:bg-red-700 transition-colors"
                    >
                      Yes, clear
                    </button>
                    <button
                      onClick={() => { setShowClearConfirm(false); setShowDestructiveMenu(true); }}
                      className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
                    >
                      Back
                    </button>
                  </div>
                ) : showDestructiveMenu ? (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <span className="text-sm text-red-700 font-medium">What would you like to do?</span>
                    <button
                      onClick={() => { setShowDestructiveMenu(false); setShowClearConfirm(true); }}
                      className="bg-white border border-red-300 text-red-600 px-3 py-1 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
                    >
                      Clear Stats
                    </button>
                    <button
                      onClick={() => { setShowDestructiveMenu(false); setShowDeleteEventConfirm(true); }}
                      className="bg-white border border-red-300 text-red-600 px-3 py-1 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
                    >
                      Delete Match
                    </button>
                    <button
                      onClick={() => setShowDestructiveMenu(false)}
                      className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDestructiveMenu(true)}
                    className="bg-white border border-red-200 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-50 flex items-center gap-2 transition-colors text-sm"
                  >
                    <Trash2 className="w-4 h-4" /> Delete Match
                  </button>
                )
              )}
              {isAdmin && (
                <button
                  onClick={handleOpenActionModal}
                  className="bg-blue-600 text-white border border-blue-700 px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 transition-colors text-sm shadow-sm"
                >
                  <Edit3 className="w-4 h-4" /> Add/Edit Stats
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Control Bar ───────────────────────────────────────────────────── */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between z-20">
        <div className="flex flex-col md:flex-row gap-4 w-full xl:w-auto flex-wrap items-start md:items-center">
          <div className="relative w-full md:w-auto">
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <select className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-48 cursor-pointer hover:bg-slate-100 transition-colors" value={selectedTeamId} onChange={(e) => setSelectedTeamId(e.target.value)}>
              {teams
                .filter(t => t.type !== 'opponent')
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
            </select>
          </div>
          <div className="relative w-full md:w-auto">
            <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <select className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-48 cursor-pointer hover:bg-slate-100 transition-colors" value={selectedSeason} onChange={(e) => setSelectedSeason(e.target.value)}>
              <option value="all">All Time</option>
              {availableSeasons.map(season => <option key={season} value={season}>{season} Season</option>)}
            </select>
          </div>
          <div className="relative w-full md:w-auto" ref={gameDropdownRef}>
            <button
              type="button"
              onClick={() => {
                if (isEditing) return;
                setGameDropdownOpen(prev => !prev);
              }}
              className={`flex items-center gap-2 pl-9 pr-3 py-2 border rounded-lg text-sm font-medium text-slate-700 outline-none w-full md:w-64 text-left transition-colors ${isEditing ? 'bg-yellow-50 border-yellow-300 cursor-not-allowed' : 'bg-slate-50 border-slate-200 hover:bg-slate-100 cursor-pointer'}`}
            >
              <FilePlus className="w-4 h-4 absolute left-3 text-slate-400" />
              <span className="flex-1 truncate">{selectedEventLabel || 'All Games (Summary)'}</span>
              {selectedEventIds.size > 1 && (
                <span className="shrink-0 text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{selectedEventIds.size}</span>
              )}
              <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${gameDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {gameDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="max-h-72 overflow-y-auto py-1">
                  {/* All Games option */}
                  <button
                    type="button"
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${selectedEventIds.size === 0 ? 'text-blue-700 font-semibold' : 'text-slate-700'}`}
                    onClick={() => {
                      const hasUnsavedScan = scannedStats.length > 0 || scannedTeamReport !== null;
                      if (hasUnsavedScan && !window.confirm('You have unsaved scanned stats. Discard them?')) return;
                      if (hasUnsavedScan) handleCancelEdit();
                      setSelectedEventIds(new Set());
                      setShowClearConfirm(false);
                      setGameDropdownOpen(false);
                    }}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedEventIds.size === 0 ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                      {selectedEventIds.size === 0 && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    All Games (Summary)
                  </button>
                  {filteredEvents.length > 0 && <div className="border-t border-slate-100 my-1" />}
                  {filteredEvents.map(evt => {
                    const checked = selectedEventIds.has(evt.id);
                    const officialRecord = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
                    const playerStatCount = (officialRecord?.gameState.stats?.length ?? 0) + (evt.stats?.length ?? 0);
                    const hasScoreData = (officialRecord?.gameState.ourSetsWon ?? 0) > 0 || (officialRecord?.gameState.theirSetsWon ?? 0) > 0;
                    const missingPlayerStats = hasScoreData && playerStatCount === 0;
                    return (
                      <button
                        key={evt.id}
                        type="button"
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${checked ? 'text-blue-700' : 'text-slate-700'}`}
                        onClick={() => {
                          const hasUnsavedScan = scannedStats.length > 0 || scannedTeamReport !== null;
                          if (hasUnsavedScan && !window.confirm('You have unsaved scanned stats. Discard them?')) return;
                          if (hasUnsavedScan) handleCancelEdit();
                          toggleGameSelection(evt.id);
                          setShowClearConfirm(false);
                        }}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                          {checked && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span className="truncate flex-1">{evt.date} vs {evt.opponent || 'Opponent'}</span>
                        {missingPlayerStats && (
                          <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-0.5" title="Has score data but no player stats — click to add">
                            <Upload className="w-2.5 h-2.5" /> Stats
                          </span>
                        )}
                        {(() => {
                          const r = resolveEventResult(evt);
                          if (!r) return null;
                          return (
                            <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${r === 'W' ? 'bg-green-100 text-green-700' : r === 'L' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                              {r}
                            </span>
                          );
                        })()}
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-slate-100">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-blue-600 font-semibold hover:bg-blue-50 transition-colors"
                    onClick={() => { setGameDropdownOpen(false); setShowCreateGameModal(true); }}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add New Game Record
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="relative w-full md:w-auto">
            <Database className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value as typeof sourceFilter)}
              className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-44 cursor-pointer hover:bg-slate-100 transition-colors appearance-none"
            >
              <option value="all">All Sources</option>
              <option value="live" disabled={!availableSources.live}>{availableSources.live ? 'SideoutIQ Game Time' : 'SideoutIQ Game Time (none)'}</option>
              <option value="import" disabled={!availableSources.import}>{availableSources.import ? 'Import' : 'Import (none)'}</option>
              <option value="manual" disabled={!availableSources.manual}>{availableSources.manual ? 'Manual Entry' : 'Manual Entry (none)'}</option>
            </select>
          </div>
          {selectedEventId !== 'all' && selectedMatchScore && selectedMatchScore.setHistory.length > 0 && (
            <div className="relative w-full md:w-auto">
              <Activity className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <select
                value={selectedSetNumber ?? 'all'}
                onChange={e => setSelectedSetNumber(e.target.value === 'all' ? null : Number(e.target.value))}
                className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-36 cursor-pointer hover:bg-slate-100 transition-colors appearance-none"
              >
                <option value="all">All Sets</option>
                {selectedMatchScore.setHistory.map((s, i) => (
                  <option key={i + 1} value={i + 1}>
                    Set {i + 1} ({s.our}–{s.their})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {(statsViewMode === 'player' || isEditing) && (
          <div className="relative w-full xl:w-64">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input type="text" placeholder="Search player..." className="pl-9 pr-4 py-2 w-full bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        )}
      </div>

      {/* ── No-stats warning for the selected team ────────────────────────── */}
      {!selectedTeamHasStats && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <span>
            <span className="font-semibold text-amber-800">
              No stats recorded yet for {teams.find(t => t.id === selectedTeamId)?.name ?? 'this team'}.
            </span>
            <span className="text-amber-700"> Stats will appear here after you track a game in GameTime.</span>
          </span>
        </div>
      )}

      {/* ── Match Score Banner (single game selected) ─────────────────────── */}
      {!isEditing && selectedMatchScore && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Us</div>
              <div className={`stat-num md ${selectedMatchScore.ourSetsWon > selectedMatchScore.theirSetsWon ? 'text-green-600' : 'text-slate-400'}`}>
                {selectedMatchScore.ourSetsWon}
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-400">–</div>
            <div className="text-center">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                {filteredEvents.find(e => e.id === selectedEventId)?.opponent || 'Opp'}
              </div>
              <div className={`stat-num md ${selectedMatchScore.theirSetsWon > selectedMatchScore.ourSetsWon ? 'text-red-600' : 'text-slate-400'}`}>
                {selectedMatchScore.theirSetsWon}
              </div>
            </div>
          </div>
          {selectedMatchScore.setHistory.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
              {selectedMatchScore.setHistory.map((s, i) => {
                const setNum = i + 1;
                const isSelected = selectedSetNumber === setNum;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedSetNumber(isSelected ? null : setNum)}
                    className={`flex flex-col items-center rounded-lg px-3 py-2 min-w-[52px] transition-all ${
                      isSelected
                        ? 'bg-blue-50 ring-2 ring-blue-500 ring-offset-1'
                        : 'bg-slate-100 hover:bg-slate-200'
                    }`}
                  >
                    <span className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${isSelected ? 'text-blue-600' : 'text-slate-500'}`}>Set {setNum}</span>
                    <span className={`text-sm font-bold ${s.our > s.their ? 'text-green-600' : s.their > s.our ? 'text-red-600' : 'text-slate-500'}`}>
                      {s.our}–{s.their}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── View Selector ──────────────────────────────────────────────────── */}
      {!isEditing && (
        <div className="flex rounded-xl border border-slate-200 bg-slate-100 p-1 gap-1">
          <button
            onClick={() => setStatsViewMode('team')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${statsViewMode === 'team' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <BarChart2 className="w-4 h-4" />
            Team Performance
          </button>
          <button
            onClick={() => setStatsViewMode('player')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${statsViewMode === 'player' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Users className="w-4 h-4" />
            Player Performance
          </button>
        </div>
      )}

      {/* ── Team Stats (view mode only) ────────────────────────────────────── */}
      {!isEditing && statsViewMode === 'team' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500">
            <BarChart2 className="w-5 h-5 text-white" />
            <h3 className="font-bold text-base text-white tracking-wide">Team Performance</h3>
          </div>
          <div className="p-6 space-y-6">

            {/* Micro Metrics Grid */}
            {(() => {
              const sr = teamMetrics.serveReceiveAvg;
              const he = teamMetrics.hittingEfficiency;
              const sp = teamMetrics.servePct;
              const kp = teamMetrics.killPct;
              const ae = teamMetrics.aceToErrorRatio;
              const { teamReceptions, teamReceptionSum, teamKills, teamErrors, teamAttempts, teamAces, teamServeErrors, totalServes } = teamMetrics;
              const mc = (val: number, g: number, o: number) =>
                val >= g ? { bg: 'bg-green-50 border-green-300', txt: 'text-green-700' }
                : val >= o ? { bg: 'bg-amber-50 border-amber-300', txt: 'text-amber-700' }
                : { bg: 'bg-red-50 border-red-300', txt: 'text-red-600' };
              const srC = mc(sr, T.passRatingGreen, T.passRatingAmber);
              const heC = teamAttempts > 0 ? mc(he, T.hitEfficiencyGreen, T.hitEfficiencyAmber) : { bg: 'bg-slate-50 border-slate-200', txt: 'text-slate-400' };
              const spC = sp !== null ? mc(sp, T.servePctGreen, T.servePctAmber) : { bg: 'bg-slate-50 border-slate-200', txt: 'text-slate-400' };
              const kpC = mc(kp, T.killPctGreen, T.killPctAmber);
              const aeC = (sp === null && ae === 0) ? { bg: 'bg-slate-50 border-slate-200', txt: 'text-slate-400' } : mc(ae, T.aceErrorRatioGreen, T.aceErrorRatioAmber);
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-shadow ${srC.bg}`} onClick={() => setTeamStatTimeline({ key: 'passRating', label: 'Pass Rating', format: v => v.toFixed(2), yTickFormat: v => v.toFixed(1), green: T.passRatingGreen, amber: T.passRatingAmber, yDomain: [0, 3] })}>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Pass Rating</div>
                    <div className={`text-xl font-bold mt-1 ${srC.txt}`}>{sr.toFixed(2)}</div>
                    {teamReceptions > 0 && <div className="text-[10px] text-slate-400 opacity-70">{teamReceptionSum.toFixed(0)} pts / {teamReceptions} rec</div>}
                    <div className="text-[10px] text-slate-400">Target: ≥{T.passRatingGreen}</div>
                  </div>
                  <div className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-shadow ${heC.bg}`} onClick={() => setTeamStatTimeline({ key: 'hitEfficiency', label: 'Hit Efficiency', format: v => `${(v * 100).toFixed(1)}%`, yTickFormat: v => `${Math.round(v * 100)}%`, green: T.hitEfficiencyGreen, amber: T.hitEfficiencyAmber, yDomain: [-0.1, 0.6] })}>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Hit Eff (H%)</div>
                    <div className={`text-xl font-bold mt-1 ${heC.txt}`}>{teamAttempts > 0 ? `${(he * 100).toFixed(1)}%` : '—'}</div>
                    {teamAttempts > 0 && <div className="text-[10px] text-slate-400 opacity-70">{teamKills - teamErrors} / {teamAttempts} att</div>}
                    <div className="text-[10px] text-slate-400">Target: ≥{Math.round(T.hitEfficiencyGreen * 100)}%</div>
                  </div>
                  <div className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-shadow ${spC.bg}`} onClick={() => setTeamStatTimeline({ key: 'servePct', label: 'Serve %', format: v => `${(v * 100).toFixed(1)}%`, yTickFormat: v => `${Math.round(v * 100)}%`, green: T.servePctGreen, amber: T.servePctAmber, yDomain: [0.5, 1] })}>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Srv %</div>
                    <div className={`text-xl font-bold mt-1 ${spC.txt}`}>{sp !== null ? (sp * 100).toFixed(1) + '%' : '—'}</div>
                    {totalServes > 0 && <div className="text-[10px] text-slate-400 opacity-70">{totalServes - teamServeErrors} / {totalServes} srv</div>}
                    <div className="text-[10px] text-slate-400">Target: ≥{Math.round(T.servePctGreen * 100)}%</div>
                  </div>
                  <div className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-shadow ${kpC.bg}`} onClick={() => setTeamStatTimeline({ key: 'killPct', label: 'Kill %', format: v => `${(v * 100).toFixed(1)}%`, yTickFormat: v => `${Math.round(v * 100)}%`, green: T.killPctGreen, amber: T.killPctAmber, yDomain: [0, 0.7] })}>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Kill %</div>
                    <div className={`text-xl font-bold mt-1 ${kpC.txt}`}>{(kp * 100).toFixed(1)}%</div>
                    {teamAttempts > 0 && <div className="text-[10px] text-slate-400 opacity-70">{teamKills} / {teamAttempts} att</div>}
                    <div className="text-[10px] text-slate-400">Target: ≥{Math.round(T.killPctGreen * 100)}%</div>
                  </div>
                  <div className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-shadow ${aeC.bg}`} onClick={() => setTeamStatTimeline({ key: 'aceToErrorRatio', label: 'Aces / Error', format: v => v.toFixed(2), yTickFormat: v => v.toFixed(1), green: T.aceErrorRatioGreen, amber: T.aceErrorRatioAmber, yDomain: [0, 2.5] })}>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Aces / Error</div>
                    <div className={`text-xl font-bold mt-1 ${aeC.txt}`}>{(sp === null && ae === 0) ? '—' : ae.toFixed(2)}</div>
                    {(teamAces > 0 || teamServeErrors > 0) && <div className="text-[10px] text-slate-400 opacity-70">{teamAces} ace / {teamServeErrors} err</div>}
                    <div className="text-[10px] text-slate-400">Target: ≥{T.aceErrorRatioGreen.toFixed(1)}</div>
                  </div>
                </div>
              );
            })()}

            {/* No live-tracking notice */}
            {teamMetrics.sideoutPct === null && teamMetrics.pointScoringPct === null && (
              <div className="flex items-start gap-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                <Info className="w-4 h-4 shrink-0 text-slate-400 mt-0.5" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  <strong className="text-slate-700">Advanced metrics hidden.</strong>{' '}
                  {selectedEventId !== 'all' && selectedEventHasStats
                    ? <>Stats like Sideout % and Point Scoring % require live GameTime tracking OR a "Match Report" upload. Upload a Hudl Match Report PDF to unlock these metrics for this game.</>
                    : <>Stats like Sideout %, Point Scoring %, and Rotation Analysis require play-by-play context from live GameTime tracking or a Hudl Match Report upload.</>
                  }
                </p>
              </div>
            )}

            {/* Macro Metrics */}
            {(() => {
              const soV = teamMetrics.sideoutPct;
              const bpV = teamMetrics.pointScoringPct;
              const { effectiveSideoutPoints, effectiveSideoutOpp, totalPointsOnServe, totalServes: macroServes,
                      reportWonOnReceive, reportLostOnReceive, reportWonOnServe, reportLostOnServe } = teamMetrics;
              const hasMacroData = soV !== null || bpV !== null;
              if (!hasMacroData) return null;
              const mc3 = (v: number | null, g: number, a: number) => ({
                bg:  v === null ? 'bg-white border-slate-200'    : v >= g ? 'bg-green-50 border-green-300'  : v >= a ? 'bg-amber-50 border-amber-300'  : 'bg-red-50 border-red-300',
                bar: v === null ? 'bg-slate-200'                 : v >= g ? 'bg-green-500'                  : v >= a ? 'bg-amber-400'                   : 'bg-red-400',
                val: v === null ? 'text-slate-400'               : v >= g ? 'text-green-800'                : v >= a ? 'text-amber-800'                 : 'text-red-700',
              });
              const so = mc3(soV, T.sideoutPctGreen,    T.sideoutPctAmber);
              const bp = mc3(bpV, T.breakPointPctGreen, T.breakPointPctAmber);
              const soBg = so.bg; const soBar = so.bar; const soVal = so.val;
              const bpBg = bp.bg; const bpBar = bp.bar; const bpVal = bp.val;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between cursor-pointer hover:shadow-md transition-shadow ${soBg}`} onClick={() => setTeamStatTimeline({ key: 'sideoutPct', label: 'Sideout %', format: v => `${(v * 100).toFixed(1)}%`, yTickFormat: v => `${Math.round(v * 100)}%`, green: T.sideoutPctGreen, amber: T.sideoutPctAmber, yDomain: [0, 1] })}>
                    <div>
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Sideout %</div>
                      <div className={`stat-num md ${soVal}`}>
                        {soV === null ? '--' : `${(soV * 100).toFixed(1)}%`}
                      </div>
                      {soV !== null && (
                        <div className="text-[10px] text-slate-400 opacity-70 mt-0.5">
                          {hasLiveTrackingData && effectiveSideoutOpp > 0
                            ? `${effectiveSideoutPoints} / ${effectiveSideoutOpp} opp`
                            : reportWonOnReceive !== null
                              ? `${reportWonOnReceive} / ${(reportWonOnReceive ?? 0) + (reportLostOnReceive ?? 0)} opp`
                              : `${effectiveSideoutPoints} / ${effectiveSideoutOpp} opp`}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`${soBar} h-1.5 rounded-full`} style={{ width: `${Math.min(100, (soV ?? 0) * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">{soV === null ? 'Requires live game tracking' : `Target: ≥${Math.round(T.sideoutPctGreen * 100)}% (rallies won while receiving)`}</p>
                  </div>
                  <div className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between cursor-pointer hover:shadow-md transition-shadow ${bpBg}`} onClick={() => setTeamStatTimeline({ key: 'pointScoringPct', label: 'Point Scoring %', format: v => `${(v * 100).toFixed(1)}%`, yTickFormat: v => `${Math.round(v * 100)}%`, green: T.breakPointPctGreen, amber: T.breakPointPctAmber, yDomain: [0, 1] })}>
                    <div>
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Point Scoring %</div>
                      <div className={`stat-num md ${bpVal}`}>
                        {bpV === null ? '--' : `${(bpV * 100).toFixed(1)}%`}
                      </div>
                      {bpV !== null && (
                        <div className="text-[10px] text-slate-400 opacity-70 mt-0.5">
                          {hasLiveTrackingData && macroServes > 0
                            ? `${totalPointsOnServe} / ${macroServes} srv`
                            : reportWonOnServe !== null
                              ? `${reportWonOnServe} / ${(reportWonOnServe ?? 0) + (reportLostOnServe ?? 0)} srv`
                              : `${totalPointsOnServe} / ${macroServes} srv`}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`${bpBar} h-1.5 rounded-full`} style={{ width: `${Math.min(100, (bpV ?? 0) * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">{bpV === null ? 'Requires live game tracking' : `Target: ≥${Math.round(T.breakPointPctGreen * 100)}% (rallies won while serving)`}</p>
                  </div>
                </div>
              );
            })()}

            {/* Rotation Analysis — sideout + break point rows in one card */}
            {Object.values(teamMetrics.rotationStats).some((r: any) => r.sideoutAttempts > 0) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

                {/* ── Sideout % row ── */}
                <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-sm text-slate-700 flex justify-between items-center">
                    <span>Sideout % By Rotation</span>
                    <span className="text-xs font-normal text-slate-500">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />≥{Math.round(T.sideoutPctGreen * 100)}%
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-400 ml-2 mr-1" />{Math.round(T.sideoutPctAmber * 100)}–{Math.round(T.sideoutPctGreen * 100) - 1}%
                      <span className="inline-block w-2 h-2 rounded-full bg-red-400 ml-2 mr-1" />&lt;{Math.round(T.sideoutPctAmber * 100)}%
                    </span>
                </div>
                <div className="p-4">
                    <div className="grid grid-cols-6 gap-2">
                        {["1", "2", "3", "4", "5", "6"].map((rot) => {
                            const data = teamMetrics.rotationStats[rot];
                            const hasData = data.sideoutAttempts > 0;
                            const pct = data.sideoutPct;
                            const cardCls = !hasData ? 'bg-slate-50 border-slate-200' : pct >= T.sideoutPctGreen ? 'bg-green-50 border-green-300' : pct >= T.sideoutPctAmber ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-200';
                            const valCls  = !hasData ? 'text-slate-400'               : pct >= T.sideoutPctGreen ? 'text-green-700'               : pct >= T.sideoutPctAmber ? 'text-amber-700'               : 'text-red-600';
                            const barCls  = !hasData ? 'bg-slate-200'                 : pct >= T.sideoutPctGreen ? 'bg-green-500'                  : pct >= T.sideoutPctAmber ? 'bg-amber-400'                  : 'bg-red-400';
                            return (
                                <div key={rot} className={`flex flex-col items-center p-3 rounded-lg border ${cardCls}`}>
                                    <div className={`text-base font-black ${valCls}`}>
                                        {hasData ? `${(pct * 100).toFixed(0)}%` : '—'}
                                    </div>
                                    <div className="w-full bg-white/60 rounded-full h-1 my-1.5">
                                        <div className={`${barCls} h-1 rounded-full`} style={{ width: hasData ? `${Math.min(100, pct * 100)}%` : '0%' }} />
                                    </div>
                                    <div className="text-[10px] font-bold text-slate-500">Rot {rot}</div>
                                    <div className="text-[9px] text-slate-400 mt-0.5">
                                        {hasData ? `${data.sideoutsWon}/${data.sideoutAttempts}` : 'no data'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
                        Denominator uses stepper data when available, otherwise serve receives per rotation, then phase-tagged rally endings. Rotations with no data of any kind show —.
                    </p>
                </div>

                {/* ── Served Points by Rotation row — only when serve-rotation data exists ── */}
                {Object.values(teamMetrics.rotationStats).some((r: any) => r.breakPointAttempts > 0) && (
                <>
                <div className="p-4 bg-slate-50 border-t border-b border-slate-200 font-bold text-sm text-slate-700 flex justify-between items-center">
                    <span>Served Points by Rotation</span>
                    <span className="text-xs font-normal text-slate-500">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />≥{Math.round(T.breakPointPctGreen * 100)}%
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 ml-2 mr-1" />{Math.round(T.breakPointPctAmber * 100)}–{Math.round(T.breakPointPctGreen * 100) - 1}%
                        <span className="inline-block w-2 h-2 rounded-full bg-red-400 ml-2 mr-1" />&lt;{Math.round(T.breakPointPctAmber * 100)}%
                    </span>
                </div>
                <div className="p-4">
                    <div className="grid grid-cols-6 gap-2">
                        {["1", "2", "3", "4", "5", "6"].map((rot) => {
                            const data = teamMetrics.rotationStats[rot];
                            const hasData = data.breakPointAttempts > 0;
                            const pct = data.breakPointPct;
                            const cardCls = !hasData ? 'bg-slate-50 border-slate-200' : pct >= T.breakPointPctGreen ? 'bg-green-50 border-green-300' : pct >= T.breakPointPctAmber ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-200';
                            const valCls  = !hasData ? 'text-slate-400'               : pct >= T.breakPointPctGreen ? 'text-green-700'               : pct >= T.breakPointPctAmber ? 'text-amber-700'               : 'text-red-600';
                            const barCls  = !hasData ? 'bg-slate-200'                 : pct >= T.breakPointPctGreen ? 'bg-green-500'                  : pct >= T.breakPointPctAmber ? 'bg-amber-400'                  : 'bg-red-400';
                            const serverRows = (data.serverPlayerIds ?? [])
                                .map(id => {
                                    const name = players.find(p => p.id === id)?.name;
                                    if (!name) return null;
                                    const s = data.serverStats?.[id];
                                    return { name, attempts: s?.attempts ?? 0, points: s?.points ?? 0 };
                                })
                                .filter((r): r is { name: string; attempts: number; points: number } => r !== null);
                            return (
                                <div key={rot} className={`flex flex-col items-center p-3 rounded-lg border ${cardCls}`}>
                                    <div className={`text-base font-black ${valCls}`}>
                                        {hasData ? `${(pct * 100).toFixed(0)}%` : '—'}
                                    </div>
                                    <div className="w-full bg-white/60 rounded-full h-1 my-1.5">
                                        <div className={`${barCls} h-1 rounded-full`} style={{ width: hasData ? `${Math.min(100, pct * 100)}%` : '0%' }} />
                                    </div>
                                    <div className="text-[10px] font-bold text-slate-500">Rot {rot}</div>
                                    {serverRows.length > 0 ? (
                                        <div className="flex flex-col items-center gap-1 mt-1 w-full">
                                            {serverRows.map((row, i) => (
                                                <div key={i} className="w-full">
                                                    <div className="text-[9px] font-medium text-slate-700 truncate text-center" title={row.name}>{row.name}</div>
                                                    <div className="text-[9px] text-slate-400 text-center">{row.points} pts / {row.attempts} srv</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-[9px] text-slate-400 mt-0.5">
                                            {hasData ? `${data.breakPointsWon} pts / ${data.breakPointAttempts} srv` : 'no data'}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
                        Points scored per serve attempt, broken down by rotation. Shows server names and their individual point totals. Only available from live GameTime tracking.
                    </p>
                </div>
                </>
                )}

            </div>
            )}

            {/* ── Reception Analysis ── */}
            {(() => {
              type RecEntry = { count: number; ratingSum: number; errors: number };
              const srEntries = (Object.entries(advancedMetrics.recByServeStyle) as [string, RecEntry][]).filter(([, d]) => d.count > 0).sort((a, b) => b[1].count - a[1].count);
              const ctEntries = (Object.entries(advancedMetrics.recByContactType) as [string, RecEntry][]).filter(([, d]) => d.count > 0).sort((a, b) => b[1].count - a[1].count);
              if (srEntries.length === 0 && ctEntries.length === 0) return null;

              const SERVE_STYLES = ['float', 'topspin'] as const;
              const CONTACT_TYPES = ['hands', 'platform'] as const;
              const styleLabel: Record<string, string> = { topspin: 'Jump', float: 'Float' };
              const contactLabel: Record<string, string> = { hands: 'Hands', platform: 'Platform' };

              const hasMatrix = SERVE_STYLES.some(s => CONTACT_TYPES.some(c => (advancedMetrics.recByStyleAndContact[s]?.[c]?.count ?? 0) > 0));

              const ratingColors = (avg: number | null) => {
                if (avg === null) return { bg: 'bg-slate-50', border: 'border-slate-200', val: 'text-slate-400', bar: 'bg-slate-200' };
                if (avg >= T.passRatingGreen) return { bg: 'bg-green-50', border: 'border-green-200', val: 'text-green-700', bar: 'bg-green-500' };
                if (avg >= T.passRatingAmber) return { bg: 'bg-amber-50', border: 'border-amber-200', val: 'text-amber-700', bar: 'bg-amber-400' };
                return { bg: 'bg-red-50', border: 'border-red-200', val: 'text-red-600', bar: 'bg-red-400' };
              };

              // Merge multiple intersection cells into a weighted combined entry
              const mergeEntries = (...entries: (RecEntry | undefined)[]): RecEntry =>
                entries.reduce<RecEntry>((acc, d) => d
                  ? { count: acc.count + d.count, ratingSum: acc.ratingSum + d.ratingSum, errors: acc.errors + d.errors }
                  : acc,
                  { count: 0, ratingSum: 0, errors: 0 }
                );

              const renderCell = (d: RecEntry | undefined) => {
                if (!d || d.count === 0) return (
                  <div className="flex flex-col items-center justify-center h-full py-3 text-slate-300">
                    <span className="text-lg font-black">—</span>
                    <span className="text-[9px]">no data</span>
                  </div>
                );
                const avg = d.ratingSum / d.count;
                const c = ratingColors(avg);
                return (
                  <div className="flex flex-col items-center gap-1 py-3 px-1">
                    <span className={`text-xl font-black ${c.val}`}>{avg.toFixed(2)}</span>
                    <div className="w-full bg-white/70 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${c.bar}`} style={{ width: `${Math.min(100, (avg / 3) * 100)}%` }} />
                    </div>
                    <span className="text-[9px] text-slate-500 font-medium">{d.count} passes</span>
                    {d.errors > 0 && <span className="text-[9px] text-red-400">{((d.errors / d.count) * 100).toFixed(0)}% err</span>}
                  </div>
                );
              };

              const renderSummaryRow = (label: string, d: RecEntry) => {
                const avg = d.count > 0 ? d.ratingSum / d.count : 0;
                const c = ratingColors(d.count > 0 ? avg : null);
                return (
                  <div key={label} className="flex items-center gap-3">
                    <div className="w-20 text-xs font-bold text-slate-600 shrink-0">{label}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                          <div className={`h-2.5 rounded-full ${c.bar}`} style={{ width: `${Math.min(100, (avg / 3) * 100)}%` }} />
                        </div>
                        <span className={`text-xs font-black w-8 text-right ${c.val}`}>{avg.toFixed(2)}</span>
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5">{d.count} passes · {d.errors > 0 ? `${((d.errors / d.count) * 100).toFixed(0)}% err` : 'no errors'}</div>
                    </div>
                  </div>
                );
              };

              return (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-sm text-slate-700">
                    Reception Analysis
                  </div>
                  <div className="p-4 space-y-5">

                    {/* ── Matrix: serve type × contact type ── */}
                    {hasMatrix && (
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-2.5">Pass Quality by Serve Type × Contact</div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr>
                                <th className="w-20" />
                                {CONTACT_TYPES.filter(c => ctEntries.some(([k]) => k === c)).map(c => (
                                  <th key={c} className="text-center text-[10px] font-bold text-slate-500 uppercase pb-2 px-2">{contactLabel[c]}</th>
                                ))}
                                <th className="text-center text-[10px] font-bold text-slate-400 uppercase pb-2 px-2">All</th>
                              </tr>
                            </thead>
                            <tbody>
                              {SERVE_STYLES.filter(s => srEntries.some(([k]) => k === s)).map(style => {
                                const visibleContacts = CONTACT_TYPES.filter(c => ctEntries.some(([k]) => k === c));
                                // Weighted "All" = intersection cells only (hands + platform for this serve style)
                                const rowTotal = mergeEntries(...visibleContacts.map(c => advancedMetrics.recByStyleAndContact[style]?.[c]));
                                return (
                                  <tr key={style}>
                                    <td className="text-[10px] font-bold text-slate-600 pr-2 py-1 whitespace-nowrap">{styleLabel[style]}</td>
                                    {visibleContacts.map(contact => {
                                      const d = advancedMetrics.recByStyleAndContact[style]?.[contact];
                                      const c = ratingColors(d && d.count > 0 ? d.ratingSum / d.count : null);
                                      return (
                                        <td key={contact} className="px-1 py-1">
                                          <div className={`rounded-lg border ${c.bg} ${c.border}`}>
                                            {renderCell(d)}
                                          </div>
                                        </td>
                                      );
                                    })}
                                    <td className="px-1 py-1">
                                      <div className={`rounded-lg border ${ratingColors(rowTotal.count > 0 ? rowTotal.ratingSum / rowTotal.count : null).bg} ${ratingColors(rowTotal.count > 0 ? rowTotal.ratingSum / rowTotal.count : null).border} opacity-70`}>
                                        {renderCell(rowTotal)}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* Totals row — weighted by N across float + topspin for each contact */}
                              {(() => {
                                const visibleStyles = SERVE_STYLES.filter(s => srEntries.some(([k]) => k === s));
                                const visibleContacts = CONTACT_TYPES.filter(c => ctEntries.some(([k]) => k === c));
                                const overallTotal = mergeEntries(
                                  ...visibleStyles.flatMap(s => visibleContacts.map(c => advancedMetrics.recByStyleAndContact[s]?.[c]))
                                );
                                return (
                                  <tr>
                                    <td className="text-[10px] font-bold text-slate-400 pr-2 py-1">All</td>
                                    {visibleContacts.map(contact => {
                                      const d = mergeEntries(...visibleStyles.map(s => advancedMetrics.recByStyleAndContact[s]?.[contact]));
                                      const c = ratingColors(d.count > 0 ? d.ratingSum / d.count : null);
                                      return (
                                        <td key={contact} className="px-1 py-1">
                                          <div className={`rounded-lg border ${c.bg} ${c.border} opacity-70`}>
                                            {renderCell(d)}
                                          </div>
                                        </td>
                                      );
                                    })}
                                    <td className="px-1 py-1">
                                      <div className={`rounded-lg border ${ratingColors(overallTotal.count > 0 ? overallTotal.ratingSum / overallTotal.count : null).bg} ${ratingColors(overallTotal.count > 0 ? overallTotal.ratingSum / overallTotal.count : null).border} opacity-70`}>
                                        {renderCell(overallTotal)}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* ── Serve type summary (shown when no matrix data yet) ── */}
                    {!hasMatrix && srEntries.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-2.5">Avg Rating by Incoming Serve Type</div>
                        <div className="space-y-2.5">
                          {srEntries.map(([style, d]) => renderSummaryRow(styleLabel[style] ?? style, d))}
                        </div>
                      </div>
                    )}

                    {/* ── Contact type summary (shown when no matrix data yet) ── */}
                    {!hasMatrix && ctEntries.length > 0 && (
                      <div className={!hasMatrix && srEntries.length > 0 ? 'pt-3 border-t border-slate-100' : ''}>
                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-2.5">Avg Rating by Contact Type</div>
                        <div className="space-y-2.5">
                          {ctEntries.map(([ctype, d]) => renderSummaryRow(contactLabel[ctype] ?? ctype, d))}
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              );
            })()}

            {/* ── Attack Donuts Row + Serve Donuts Row ── */}
            {(() => {
              const { teamKills, teamErrors, teamAttempts, teamAces, teamServeErrors, totalServes } = teamMetrics;
              // serve type keys in data: 'float' | 'jump' | 'jump-float'
              const floatRaw = advancedMetrics.serveByType['float'];
              const jumpRaw  = advancedMetrics.serveByType['jump'];
              // "Serves (All)" always uses team-level totals — counts every ace/error/attempt
              // regardless of whether a serve type was tagged (including manually-edited games).
              const combinedServe = { attempts: totalServes, aces: teamAces, errors: teamServeErrors };

              const hasAttack = teamAttempts > 0;
              const hasFloat  = (floatRaw?.attempts ?? 0) > 0;
              const hasJump   = (jumpRaw?.attempts ?? 0) > 0;
              const hasServe  = combinedServe.attempts > 0;

              const mkPie = (items: { name: string; value: number; color: string }[]) =>
                items.filter(d => d.value > 0);

              const EMPTY_PIE = [{ name: 'empty', value: 1, color: '#e2e8f0' }];

              const renderDonut = (
                title: string,
                total: number,
                data: { name: string; value: number; color: string }[],
                totalLabel: string,
                _emptyMsg: string,
              ) => {
                if (total === 0 || data.length === 0) return null;
                return (
                  <div key={title} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 relative">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{title}</div>
                    <div className="flex items-center gap-4">
                      <div className="shrink-0" style={{ width: 88, height: 88 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={data} cx="50%" cy="50%" innerRadius={22} outerRadius={40} paddingAngle={2} dataKey="value" startAngle={90} endAngle={-270}>
                              {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                            <RechartsTooltip formatter={(value: any, name: any) => [value, name]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {data.map(d => (
                          <div key={d.name} className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                            <div className="flex-1 text-[11px] font-medium text-slate-600">{d.name}</div>
                            <div className="text-xs font-bold text-slate-800">{d.value}</div>
                            <div className="text-[10px] text-slate-400 w-7 text-right">{total > 0 ? ((d.value / total) * 100).toFixed(0) : 0}%</div>
                          </div>
                        ))}
                        <div className="pt-1 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                          <span>{totalLabel}</span>
                          <span className="font-bold text-slate-700">{total}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              };

              // ── Row 1: Attacking ──────────────────────────────────────────
              const attackOutcomePie = hasAttack ? mkPie([
                { name: 'Kills',   value: teamKills,  color: '#22c55e' },
                { name: 'Errors',  value: teamErrors, color: '#ef4444' },
                { name: 'In Play', value: teamAttempts - teamKills - teamErrors, color: '#94a3b8' },
              ]) : [];
              const hasErrDetail = ERR_TYPE_ORDER.filter(k => k !== 'other').some(k => (advancedMetrics.attackErrorsByType[k] ?? 0) > 0);
              const attackErrDetailPie = hasErrDetail ? mkPie(
                ERR_TYPE_ORDER.map(k => ({ name: ERR_TYPE_LABELS[k], value: advancedMetrics.attackErrorsByType[k] ?? 0, color: ERR_TYPE_COLORS[k] }))
              ).sort((a, b) => b.value - a.value) : [];
              const typedErrTotal = attackErrDetailPie.reduce((s, d) => s + d.value, 0);

              // ── Row 2: Serving ────────────────────────────────────────────
              const serveCombinedPie = hasServe ? mkPie([
                { name: 'Aces',    value: combinedServe.aces,   color: '#22c55e' },
                { name: 'Errors',  value: combinedServe.errors, color: '#ef4444' },
                { name: 'In Play', value: combinedServe.attempts - combinedServe.aces - combinedServe.errors, color: '#94a3b8' },
              ]) : [];
              const floatPie = hasFloat ? mkPie([
                { name: 'Aces',    value: floatRaw!.aces,   color: '#22c55e' },
                { name: 'Errors',  value: floatRaw!.errors, color: '#ef4444' },
                { name: 'In Play', value: floatRaw!.attempts - floatRaw!.aces - floatRaw!.errors, color: '#94a3b8' },
              ]) : [];
              const jumpPie = hasJump ? mkPie([
                { name: 'Aces',    value: jumpRaw!.aces,   color: '#22c55e' },
                { name: 'Errors',  value: jumpRaw!.errors, color: '#ef4444' },
                { name: 'In Play', value: jumpRaw!.attempts - jumpRaw!.aces - jumpRaw!.errors, color: '#94a3b8' },
              ]) : [];

              const STATS_HIT_TYPE_COLORS: Record<string, string> = { driven: '#22c55e', shot: '#3b82f6', tip: '#f59e0b' };
              const STATS_OUTCOME_COLORS: Record<string, string> = { tool: '#8b5cf6', floor: '#10b981', shank: '#ef4444' };
              const killHitTypePie = mkPie((Object.entries(advancedMetrics.killHitTypeCounts) as [string, number][]).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: STATS_HIT_TYPE_COLORS[k] ?? '#94a3b8' })));
              const killOutcomePie = mkPie((Object.entries(advancedMetrics.killOutcomeCounts) as [string, number][]).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: STATS_OUTCOME_COLORS[k] ?? '#94a3b8' })));
              const killHitTypeTotal = killHitTypePie.reduce((s, d) => s + d.value, 0);
              const killOutcomeTotal = killOutcomePie.reduce((s, d) => s + d.value, 0);

              return (
                <div className="space-y-4">
                  {/* Attacking row */}
                  {(hasAttack || hasErrDetail) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderDonut('Team Attacks', teamAttempts, attackOutcomePie, 'Total Attacks', '')}
                      {renderDonut('Attack Error Breakdown', hasErrDetail ? typedErrTotal : 0, attackErrDetailPie, 'Typed Errors', '')}
                    </div>
                  )}
                  {/* Serving row */}
                  {(hasServe || hasFloat || hasJump) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {renderDonut('Serves (All)', combinedServe.attempts, serveCombinedPie, 'Total Serves', '')}
                      {renderDonut('Float Serves', floatRaw?.attempts ?? 0, floatPie, 'Total Float', '')}
                      {renderDonut('Topspin Serves', jumpRaw?.attempts ?? 0, jumpPie, 'Total Topspin', '')}
                    </div>
                  )}
                  {/* Kill detail row — only shown when data exists */}
                  {(killHitTypePie.length > 0 || killOutcomePie.length > 0) && (
                    <div className={`grid grid-cols-1 ${killHitTypePie.length > 0 && killOutcomePie.length > 0 ? 'md:grid-cols-2' : ''} gap-4`}>
                      {killHitTypePie.length > 0 && renderDonut('Kill Hit Type', killHitTypeTotal, killHitTypePie, 'Tagged Kills', '')}
                      {killOutcomePie.length > 0 && renderDonut('Kill Outcome', killOutcomeTotal, killOutcomePie, 'Tagged Kills', '')}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Team Player Contribution Charts ── */}
            {(() => {
              const PLAYER_COLORS = ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6','#0ea5e9','#f97316','#14b8a6','#ec4899','#6366f1','#84cc16','#fb923c'];

              // ── Gather raw stats per event (mirrors the main useMemo logic) ──
              const eventsToUse = selectedEventId === 'all'
                ? filteredEvents
                : filteredEvents.filter(e => e.id === selectedEventId);
              const statsByEvent: { eventId: string; stats: StatRecord[] }[] = eventsToUse.map(evt => {
                let stats: StatRecord[];
                if (sourceFilter !== 'all') {
                  const all: StatRecord[] = [...(evt.stats ?? [])];
                  matchRecords.filter(r => r.eventId === evt.id).forEach(r => { if (r.gameState.stats?.length) all.push(...r.gameState.stats); });
                  stats = all.filter(r => sourceFilter === 'live' ? (!r.source || r.source === 'live') : r.source === sourceFilter);
                } else {
                  const rec = matchRecords.find(r => r.eventId === evt.id && r.isRecord)?.gameState.stats;
                  stats = rec?.length ? rec : (evt.stats ?? []);
                }
                return { eventId: evt.id, stats };
              });

              // ── Find setter players and which events each had assists in ──
              const teamPlayers = players.filter(p => selectedTeamId === 'all' || p.teamId === selectedTeamId);
              const setterPlayers = teamPlayers.filter(p => p.position === 'Setter');
              const setterEventMap = new Map<string, Set<string>>();
              setterPlayers.forEach(setter => {
                const evtIds = new Set<string>();
                statsByEvent.forEach(({ eventId, stats }) => {
                  if (stats.some(s => s.playerId === setter.id && s.statType === 'assist')) evtIds.add(eventId);
                });
                if (evtIds.size > 0) setterEventMap.set(setter.id, evtIds);
              });
              const settersWithData = setterPlayers.filter(p => setterEventMap.has(p.id));

              // ── Resolve active setter filter ──
              const activeSetterFilter = selectedSetterFilter && setterEventMap.has(selectedSetterFilter) ? selectedSetterFilter : null;
              const relevantEventIds = activeSetterFilter ? setterEventMap.get(activeSetterFilter)! : null;

              // ── Build kill + attack distribution filtered by setter events ──
              const killCountByPlayer: Record<string, number> = {};
              const attackCountByPlayer: Record<string, number> = {};
              statsByEvent.forEach(({ eventId, stats }) => {
                if (relevantEventIds && !relevantEventIds.has(eventId)) return;
                stats.forEach(s => {
                  if (s.statType === 'kill') {
                    killCountByPlayer[s.playerId] = (killCountByPlayer[s.playerId] ?? 0) + 1;
                  }
                  if (s.statType === 'kill' || s.statType === 'attack_attempt' || s.statType === 'attack_error') {
                    attackCountByPlayer[s.playerId] = (attackCountByPlayer[s.playerId] ?? 0) + 1;
                  }
                });
              });
              const buildChartData = (countMap: Record<string, number>) =>
                Object.entries(countMap)
                  .map(([playerId, value]) => { const p = players.find(x => x.id === playerId); return p ? { name: p.name, value, color: '' } : null; })
                  .filter((d): d is { name: string; value: number; color: string } => d !== null)
                  .sort((a, b) => b.value - a.value)
                  .map((d, i) => ({ ...d, color: PLAYER_COLORS[i % PLAYER_COLORS.length] }));

              const allKillData = buildChartData(killCountByPlayer);
              const allAttackData = buildChartData(attackCountByPlayer);

              if (allKillData.length === 0 && allAttackData.length === 0) return null;

              const killData = allKillData.filter(d => !hiddenContribPlayers.has(d.name));
              const attackData = allAttackData.filter(d => !hiddenContribPlayers.has(d.name));
              const totalKills = killData.reduce((s, d) => s + d.value, 0);
              const totalAttacks = attackData.reduce((s, d) => s + d.value, 0);

              const togglePlayer = (name: string) => setHiddenContribPlayers(prev => {
                const next = new Set(prev);
                next.has(name) ? next.delete(name) : next.add(name);
                return next;
              });

              const renderPieLegend = (allData: typeof allKillData, totalRaw: number) =>
                allData.map(d => {
                  const hidden = hiddenContribPlayers.has(d.name);
                  const pct = ((d.value / totalRaw) * 100).toFixed(0);
                  return (
                    <button key={d.name} onClick={() => togglePlayer(d.name)} className={`flex items-center gap-1.5 text-[11px] rounded px-1 py-0.5 text-left transition-opacity hover:bg-slate-50 ${hidden ? 'opacity-40' : ''}`}>
                      <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                      <span className={`text-slate-600 truncate ${hidden ? 'line-through' : ''}`}>{d.name}</span>
                      {!hidden && <span className="ml-auto font-bold text-slate-700 whitespace-nowrap">{d.value} <span className="text-slate-400 font-normal">({pct}%)</span></span>}
                    </button>
                  );
                });

              return (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Header with shared setter filter */}
                  <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-sm text-slate-700 flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" /> Attack &amp; Kill Distribution by Setter
                      </div>
                      {settersWithData.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Setter</span>
                          <div className="flex flex-wrap gap-1">
                            <button onClick={() => setSelectedSetterFilter(null)} className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${!activeSetterFilter ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>All</button>
                            {settersWithData.map(setter => (
                              <button key={setter.id} onClick={() => setSelectedSetterFilter(activeSetterFilter === setter.id ? null : setter.id)} className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${activeSetterFilter === setter.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                {setter.name}{setter.jerseyNumber ? ` #${setter.jerseyNumber}` : ''}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {activeSetterFilter && (
                      <p className="text-[10px] text-slate-400 mt-1">Showing events where {settersWithData.find(s => s.id === activeSetterFilter)?.name} had assists</p>
                    )}
                  </div>
                  <div className={`p-5 grid grid-cols-1 ${allKillData.length > 0 && allAttackData.length > 0 ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`}>
                    {allKillData.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-slate-700">Kill Distribution</span>
                          <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">Total: {totalKills}</span>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={killData.length > 0 ? killData : [{ name: '', value: 1, color: '#e2e8f0' }]} cx="50%" cy="50%" outerRadius={75} dataKey="value" strokeWidth={2} stroke="white">
                              {(killData.length > 0 ? killData : [{ name: '', value: 1, color: '#e2e8f0' }]).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                            {killData.length > 0 && <RechartsTooltip formatter={(v, name) => [`${v} kills (${((Number(v) / totalKills) * 100).toFixed(0)}%)`, name as string]} />}
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-2 gap-1">{renderPieLegend(allKillData, allKillData.reduce((s, x) => s + x.value, 0))}</div>
                      </div>
                    )}
                    {allAttackData.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div><span className="text-sm font-bold text-slate-700">Attack Distribution</span><span className="text-[10px] text-slate-400 ml-1.5">who was set</span></div>
                          <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5 shrink-0">Total: {totalAttacks}</span>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={attackData.length > 0 ? attackData : [{ name: '', value: 1, color: '#e2e8f0' }]} cx="50%" cy="50%" outerRadius={75} dataKey="value" strokeWidth={2} stroke="white">
                              {(attackData.length > 0 ? attackData : [{ name: '', value: 1, color: '#e2e8f0' }]).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                            {attackData.length > 0 && <RechartsTooltip formatter={(v, name) => [`${v} atk (${((Number(v) / totalAttacks) * 100).toFixed(0)}%)`, name as string]} />}
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-2 gap-1">{renderPieLegend(allAttackData, allAttackData.reduce((s, x) => s + x.value, 0))}</div>
                      </div>
                    )}
                  </div>
                  {hiddenContribPlayers.size > 0 && (
                    <div className="px-5 pb-4 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">{hiddenContribPlayers.size} player{hiddenContribPlayers.size > 1 ? 's' : ''} hidden — click to restore</span>
                      <button onClick={() => setHiddenContribPlayers(new Set())} className="text-[11px] text-blue-500 hover:text-blue-700 font-medium">Show all</button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Point Scoring % Chart ── */}
            {hasServerPointData && (() => {
              const PS_MIN_SERVES = 8;
              const psData = sortedData
                .filter(r => r.serveAttempts >= PS_MIN_SERVES)
                .map(r => ({
                  name: r.player.name,
                  pct: Math.round(((r.aces + r.serverPoints) / r.serveAttempts) * 1000) / 10,
                  serveAttempts: r.serveAttempts,
                  pointsOnServe: r.aces + r.serverPoints,
                }))
                .sort((a, b) => b.pct - a.pct);

              if (psData.length === 0) return null;

              const top8 = psData.slice(0, 8);
              const displayData = showAllPSPlayers ? psData : top8;
              const barColor = (pct: number) => pct >= 40 ? '#22c55e' : pct >= 25 ? '#f59e0b' : '#ef4444';

              return (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="font-bold text-sm text-slate-700 flex items-center gap-2">
                      <Target className="w-4 h-4 text-violet-500" />
                      <span>Point Scoring %</span>
                      <ColTooltip text={STAT_DEFINITIONS['PS%']}>
                        <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                      </ColTooltip>
                      <span className="text-[10px] font-normal text-slate-400">points won on serve ÷ total serves (aces + errors + in-play) · min {PS_MIN_SERVES} serves · top 8</span>
                    </div>
                    {psData.length > 8 && (
                      <button onClick={() => setShowAllPSPlayers(v => !v)} className="text-[11px] text-blue-500 hover:text-blue-700 font-medium">
                        {showAllPSPlayers ? 'Show top 8' : `Show all ${psData.length}`}
                      </button>
                    )}
                  </div>
                  <div className="p-5">
                    <ResponsiveContainer width="100%" height={displayData.length * 36 + 16}>
                      <BarChart data={displayData} layout="vertical" margin={{ left: 8, right: 56, top: 4, bottom: 4 }}>
                        <XAxis type="number" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                        <RechartsTooltip formatter={(v: number, _n: string, props: any) => [`${v.toFixed(1)}% (${props.payload.pointsOnServe}pts / ${props.payload.serveAttempts} srv)`, 'Point Scoring %']} cursor={{ fill: '#f1f5f9' }} />
                        <Bar dataKey="pct" radius={[0, 4, 4, 0]} maxBarSize={22}>
                          {displayData.map((entry, i) => <Cell key={i} fill={barColor(entry.pct)} />)}
                          <LabelList dataKey="pct" position="right" formatter={(v: number) => `${v.toFixed(1)}%`} style={{ fontSize: 11, fill: '#475569', fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                      {[['#22c55e','≥ 40% Excellent'],['#f59e0b','25–39% Average'],['#ef4444','< 25% Below avg']].map(([color, label]) => (
                        <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Avg Pass Quality Chart ── */}
            {(() => {
              const passData = sortedData
                .filter(r => r.receptions > 0)
                .map(r => ({
                  name: r.player.name,
                  avg: r.receptions > 0 ? Math.round((r.receptionSum / r.receptions) * 100) / 100 : 0,
                  receptions: r.receptions,
                }))
                .sort((a, b) => b.avg - a.avg);

              if (passData.length === 0) return null;

              // Default: top 5 by reception count, displayed sorted by avg desc
              const top5ByAttempts = [...passData]
                .sort((a, b) => b.receptions - a.receptions)
                .slice(0, 5)
                .sort((a, b) => b.avg - a.avg);

              const displayData = showAllPassPlayers ? passData : top5ByAttempts;
              const barColor = (avg: number) => avg >= T.passRatingGreen ? '#22c55e' : avg >= T.passRatingAmber ? '#f59e0b' : '#ef4444';

              return (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="font-bold text-sm text-slate-700 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-green-500" /> Avg Pass Quality
                      <span className="text-[10px] font-normal text-slate-400">0–3 scale · highest first</span>
                    </div>
                    {passData.length > 5 && (
                      <button onClick={() => setShowAllPassPlayers(v => !v)} className="text-[11px] text-blue-500 hover:text-blue-700 font-medium">
                        {showAllPassPlayers ? 'Show top 5' : `Show all ${passData.length}`}
                      </button>
                    )}
                  </div>
                  <div className="p-5">
                    <ResponsiveContainer width="100%" height={displayData.length * 36 + 16}>
                      <BarChart data={displayData} layout="vertical" margin={{ left: 8, right: 48, top: 4, bottom: 4 }}>
                        <XAxis type="number" domain={[0, 3]} ticks={[0, 1, 2, 3]} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                        <RechartsTooltip formatter={(v: number, _n: string, props: any) => [`${v.toFixed(2)} avg (${props.payload.receptions} rec)`, 'Pass Quality']} cursor={{ fill: '#f1f5f9' }} />
                        <Bar dataKey="avg" radius={[0, 4, 4, 0]} maxBarSize={22}>
                          {displayData.map((entry, i) => <Cell key={i} fill={barColor(entry.avg)} />)}
                          <LabelList dataKey="avg" position="right" formatter={(v: number) => v.toFixed(2)} style={{ fontSize: 11, fill: '#475569', fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                      {[['#22c55e',`≥ ${T.passRatingGreen} Excellent`],['#f59e0b',`${T.passRatingAmber}–${T.passRatingGreen} Average`],['#ef4444',`< ${T.passRatingAmber} Poor`]].map(([color, label]) => (
                        <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Hitting % by Player ── */}
            {(() => {
              const hitters = playerStats
                .filter(s => s.attackAttempts > 0)
                .map(s => {
                  const hitPct = (s.kills - s.attackErrors) / s.attackAttempts;
                  const color = hitPct >= 0.3 ? '#22c55e' : hitPct >= 0.15 ? '#f59e0b' : hitPct >= 0 ? '#94a3b8' : '#ef4444';
                  const nameParts = (s as any).player?.name?.split(' ') ?? [];
                  const label = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ((s as any).player?.name ?? 'Unknown');
                  return { name: label, hitPct: Math.round(hitPct * 1000) / 10, kills: s.kills, errors: s.attackErrors, attempts: s.attackAttempts, color };
                })
                .sort((a, b) => b.hitPct - a.hitPct)
                .slice(0, 6);
              if (hitters.length === 0) return null;
              return (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-sm text-slate-700 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" /> Hitting % by Player
                    <span className="text-[10px] font-normal text-slate-400">top attackers · (K−E)/ATT</span>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={hitters.length * 40 + 16}>
                      <BarChart data={hitters} layout="vertical" margin={{ top: 4, right: 140, bottom: 4, left: 4 }}>
                        <XAxis type="number" domain={[-30, 60]} hide />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12, fill: '#475569' }} axisLine={false} tickLine={false} />
                        <ReferenceLine x={0} stroke="#e2e8f0" strokeWidth={1.5} />
                        <Bar dataKey="hitPct" radius={[0, 4, 4, 0]} maxBarSize={24} minPointSize={2}>
                          {hitters.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          <LabelList
                            dataKey="hitPct"
                            position="right"
                            content={(props: any) => {
                              const { x, y, width, height, index } = props;
                              const d = hitters[index as number];
                              const xPos = Math.max((x as number), (x as number) + (width as number)) + 8;
                              const yMid = (y as number) + (height as number) / 2;
                              return (
                                <text x={xPos} y={yMid} fontSize={11} dominantBaseline="middle">
                                  <tspan fontWeight="700" fill="#1e293b">{d.hitPct > 0 ? '+' : ''}{d.hitPct}%</tspan>
                                  <tspan dx={6} fill="#94a3b8">{d.kills}K {d.errors}E {d.attempts}att</tspan>
                                </text>
                              );
                            }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-2 pt-3 border-t border-slate-100">
                      {[['#22c55e','≥ 30% Elite'],['#f59e0b','15–29% Average'],['#94a3b8','0–14% Below Avg'],['#ef4444','< 0% Negative']].map(([color, label]) => (
                        <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

          </div>
        </div>
      )}

      {/* ── Player Performance heading ────────────────────────────────────── */}
      {!isEditing && statsViewMode === 'player' && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-slate-800 text-white px-5 py-2.5 rounded-xl shrink-0">
            <Users className="w-5 h-5" />
            <h3 className="font-bold text-base tracking-wide">Player Performance</h3>
          </div>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
      )}

      {/* ── Category Tabs (view mode only) ────────────────────────────────── */}
      {!isEditing && statsViewMode === 'player' && (
        <div className="flex overflow-x-auto gap-1 border-b border-slate-200 pb-1">
            {(['Summary', 'Attacking', 'Serving', 'Blocking', 'Digging', 'Setting', 'Serve Receiving'] as StatCategory[]).map(cat => (
                <button key={cat} onClick={() => { setActiveCategory(cat); setShowDefinitions(false); }} className={`px-4 py-2 text-sm font-bold whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${activeCategory === cat ? 'border-blue-600 text-blue-600 bg-blue-50/50' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>{cat}</button>
            ))}
        </div>
      )}

      {/* ── Mode Banner ───────────────────────────────────────────────────── */}
      {isEditing && (
        <div className={`border-l-4 p-4 rounded-r-lg text-sm flex items-start gap-3 relative pr-10 ${statsMode === 'edit' ? 'bg-amber-50 border-amber-400 text-amber-800' : 'bg-blue-50 border-blue-400 text-blue-800'}`}>
          <Edit3 className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">
              {statsMode === 'edit' ? 'Editing Stats' : statsMode === 'add_scan' ? 'Review Scanned Stats' : 'Adding Stats Manually'}
            </p>
            <p>
              {statsMode === 'edit'
                ? <>You are editing stats for <strong>{selectedEventLabel}</strong>. Changes will replace the existing data on Save.</>
                : statsMode === 'add_scan'
                ? <>Review the stats extracted from your upload for <strong>{selectedEventLabel}</strong>. Edit any values, then confirm.</>
                : selectedEventId === 'all'
                ? <>Season-long totals loaded from all games. Edit any values and save to create a new manual game record with these totals.</>
                : <>Entering stats for <strong>{selectedEventLabel}</strong>. Enter the <strong>total count</strong> for each stat. Pass quality: enter individual values like "3,1,2,1" or a single average like "2.3".</>
              }
            </p>
          </div>
          <button onClick={handleCancelEdit} className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/10"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Empty State ───────────────────────────────────────────────────── */}
      {filteredEvents.length === 0 && !isEditing && (
        <div className="flex flex-col items-center justify-center p-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 text-center">
          <div className="bg-white p-4 rounded-full shadow-sm mb-4"><Trophy className="w-8 h-8 text-slate-400" /></div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No Games Recorded</h3>
          <p className="text-slate-500 max-w-sm mb-6">You haven't added any games to your schedule yet. Create a game record to start tracking player statistics.</p>
          <button onClick={() => setShowCreateGameModal(true)} className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 flex items-center gap-2 shadow-lg shadow-blue-600/20 transition-all"><Plus className="w-5 h-5" /> Add First Game</button>
        </div>
      )}

      {/* ── Scan Review Panel ─────────────────────────────────────────────── */}
      {isReviewingScan && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-purple-50 border-b border-purple-200 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="font-bold text-purple-800 text-sm">AI-Extracted Stats — Review & Edit</span>
              {scannedTeamReport && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  <BarChart2 className="w-3 h-3" /> Match Report data included
                </span>
              )}
            </div>
            {(() => { const n = scannedStats.filter(s => !isSummaryRow(s)).length; return <span className="text-xs text-purple-600">{n} player{n !== 1 ? 's' : ''} found</span>; })()}
          </div>
          {scannedTeamReport && (() => {
            const bpTotal = scannedTeamReport.pointsWonOnServe + scannedTeamReport.pointsLostOnServe;
            const soTotal = scannedTeamReport.pointsWonOnReceive + scannedTeamReport.pointsLostOnReceive;
            const bpV = bpTotal > 0 ? scannedTeamReport.pointsWonOnServe / bpTotal : 0;
            const soV = soTotal > 0 ? scannedTeamReport.pointsWonOnReceive / soTotal : 0;
            const bpBg  = bpV >= 0.35 ? 'bg-green-50 border-green-300' : bpV >= 0.28 ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300';
            const bpBar = bpV >= 0.35 ? 'bg-green-500' : bpV >= 0.28 ? 'bg-amber-400' : 'bg-red-400';
            const bpVal = bpV >= 0.35 ? 'text-green-800' : bpV >= 0.28 ? 'text-amber-800' : 'text-red-700';
            const soBg  = soV >= 0.60 ? 'bg-green-50 border-green-300' : soV >= 0.50 ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300';
            const soBar = soV >= 0.60 ? 'bg-green-500' : soV >= 0.50 ? 'bg-amber-400' : 'bg-red-400';
            const soVal = soV >= 0.60 ? 'text-green-800' : soV >= 0.50 ? 'text-amber-800' : 'text-red-700';
            return (
              <div className="p-4 bg-blue-50/50 border-b border-blue-200">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <BarChart2 className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="font-bold text-blue-800 text-sm">Match Report — Preview What You're Saving</span>
                  <span className="text-[11px] text-blue-400">
                    (serve: {scannedTeamReport.pointsWonOnServe}W / {scannedTeamReport.pointsLostOnServe}L · receive: {scannedTeamReport.pointsWonOnReceive}W / {scannedTeamReport.pointsLostOnReceive}L)
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                  <div className={`p-4 rounded-xl border shadow-sm flex flex-col justify-between ${bpBg}`}>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Point Scoring %</div>
                      <div className={`stat-num sm ${bpVal}`}>{(bpV * 100).toFixed(1)}%</div>
                    </div>
                    <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`${bpBar} h-1.5 rounded-full`} style={{ width: `${Math.min(100, bpV * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Target: ≥35%</p>
                  </div>
                  <div className={`p-4 rounded-xl border shadow-sm flex flex-col justify-between ${soBg}`}>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Sideout %</div>
                      <div className={`stat-num sm ${soVal}`}>{(soV * 100).toFixed(1)}%</div>
                    </div>
                    <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`${soBar} h-1.5 rounded-full`} style={{ width: `${Math.min(100, soV * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Target: ≥60%</p>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Set Scores section */}
          <div className="p-4 border-b border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Trophy className="w-4 h-4 text-amber-500 shrink-0" />
              <span className="font-bold text-slate-700 text-sm">Set Scores</span>
              <span className="text-xs text-slate-400">(AI-extracted or enter manually)</span>
              <button
                type="button"
                onClick={() => setScannedSetScores(prev => [...prev, { our: 0, their: 0 }])}
                className="ml-auto text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
              >
                + Add Set
              </button>
            </div>
            {scannedSetScores.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No set scores extracted. Add them manually to save with this import.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {scannedSetScores.map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                    <span className="text-[10px] font-bold text-slate-400 uppercase w-10">Set {i + 1}</span>
                    <input
                      type="text" inputMode="numeric" pattern="[0-9]*"
                      value={s.our || ''}
                      onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setScannedSetScores(prev => prev.map((x, j) => j === i ? { ...x, our: v ? parseInt(v) : 0 } : x)); }}
                      placeholder="0"
                      className="w-12 text-center text-sm font-bold border border-slate-200 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <span className="text-slate-400 font-bold text-xs">–</span>
                    <input
                      type="text" inputMode="numeric" pattern="[0-9]*"
                      value={s.their || ''}
                      onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setScannedSetScores(prev => prev.map((x, j) => j === i ? { ...x, their: v ? parseInt(v) : 0 } : x)); }}
                      placeholder="0"
                      className="w-12 text-center text-sm font-bold border border-slate-200 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <button
                      type="button"
                      onClick={() => setScannedSetScores(prev => prev.filter((_, j) => j !== i))}
                      className="text-slate-300 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left min-w-[1200px]">
              <thead className="bg-slate-50 text-slate-500 font-medium uppercase text-[10px]">
                {/* Section group headers */}
                <tr className="border-b border-slate-200">
                  <th colSpan={3} className="px-2 py-1 sticky left-0 bg-slate-50 z-10" />
                  <th colSpan={4} className="px-2 py-1 text-center text-[9px] font-bold tracking-widest text-blue-600 bg-blue-50/60 border-l-2 border-slate-400/60">Hitting</th>
                  <th colSpan={3} className="px-2 py-1 text-center text-[9px] font-bold tracking-widest text-emerald-600 bg-emerald-50/60 border-l-2 border-slate-400/60">Serving</th>
                  <th colSpan={2} className="px-2 py-1 text-center text-[9px] font-bold tracking-widest text-orange-600 bg-orange-50/60 border-l-2 border-slate-400/60">Blocking</th>
                  <th colSpan={2} className="px-2 py-1 text-center text-[9px] font-bold tracking-widest text-slate-500 bg-slate-100 border-l-2 border-slate-400/60">Dig &amp; Assist</th>
                  <th colSpan={5} className="px-2 py-1 text-center text-[9px] font-bold tracking-widest text-violet-600 bg-violet-50/60 border-l-2 border-slate-400/60">Serve Receive</th>
                </tr>
                {/* Column labels */}
                <tr>
                  <th className="px-3 py-2 text-center sticky left-0 bg-slate-50 z-10">#</th>
                  <th className="px-3 py-2 text-center min-w-[160px]">Player</th>
                  <th className="px-2 py-2 text-center bg-slate-100 text-slate-600" title="Sets Played — drives K/G, ACE/G, etc.">S</th>
                  {/* Hitting */}
                  <th className="px-2 py-2 text-center bg-blue-50/40 text-blue-700 border-l-2 border-slate-400/60">K</th>
                  <th className="px-2 py-2 text-center bg-blue-50/40 text-blue-700">E</th>
                  <th className="px-2 py-2 text-center bg-blue-50/40 text-blue-700">ATT</th>
                  <th className="px-2 py-2 text-center bg-blue-50/60 text-blue-800">H %</th>
                  {/* Serving */}
                  <th className="px-2 py-2 text-center bg-emerald-50/40 text-emerald-700 border-l-2 border-slate-400/60">ACE</th>
                  <th className="px-2 py-2 text-center bg-emerald-50/40 text-emerald-700">SVE</th>
                  <th className="px-2 py-2 text-center bg-emerald-50/40 text-emerald-700">S ATT</th>
                  {/* Blocking */}
                  <th className="px-2 py-2 text-center bg-orange-50/40 text-orange-700 border-l-2 border-slate-400/60">BS</th>
                  <th className="px-2 py-2 text-center bg-orange-50/40 text-orange-700">BA</th>
                  {/* Dig & Assist */}
                  <th className="px-2 py-2 text-center border-l-2 border-slate-400/60">D</th>
                  <th className="px-2 py-2 text-center">AST</th>
                  {/* Serve Receive */}
                  <th className="px-2 py-2 text-center bg-violet-50/40 text-violet-700 border-l-2 border-slate-400/60">SR+</th>
                  <th className="px-2 py-2 text-center bg-violet-50/40 text-violet-700">SR~</th>
                  <th className="px-2 py-2 text-center bg-violet-50/40 text-violet-700">SR-</th>
                  <th className="px-2 py-2 text-center bg-violet-50/40 text-violet-700">SR ERR</th>
                  <th className="px-2 py-2 text-center bg-violet-50/60 text-violet-800">SR Rtg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {scannedStats.map((item, idx) => {
                  const isTotal = isSummaryRow(item);

                  // Computed validation for H%
                  const att = item.attempts || 0;
                  const computedH = att > 0 ? (item.kills - item.errors) / att : null;
                  const hPctNum = item.hittingPct != null ? Number(item.hittingPct) : null;
                  const hPctMatch = computedH !== null && hPctNum !== null && !isNaN(hPctNum)
                    ? Math.abs(computedH - hPctNum) < 0.005
                    : null;

                  // Computed validation for SR Rtg
                  const srTotal = (item.srPerfect || 0) + (item.srGood || 0) + (item.srPoor || 0) + (item.srError || 0);
                  const computedSrRtg = srTotal > 0
                    ? (3 * (item.srPerfect || 0) + 2 * (item.srGood || 0) + 1 * (item.srPoor || 0)) / srTotal
                    : null;
                  const srRtgMatch = computedSrRtg !== null && item.srRating != null
                    ? Math.abs(computedSrRtg - item.srRating) < 0.05
                    : null;

                  // ── TOTAL ROW — read-only summary, never saved ──
                  if (isTotal) {
                    const tv = (field: keyof ExtractedStats) => {
                      const v = item[field] as number | undefined;
                      return <span className="font-bold text-slate-700 text-xs">{v ?? '—'}</span>;
                    };
                    return (
                      <tr key={idx} className="bg-slate-100 border-t-2 border-slate-300">
                        <td className="px-3 py-2 text-center sticky left-0 bg-slate-100">
                          <span className="text-[10px] font-black text-slate-500 uppercase tracking-wide">Total</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-[10px] font-bold text-slate-400 italic">team totals — not imported</span>
                        </td>
                        <td className="px-2 py-2 text-center bg-slate-200/60">{tv('setsPlayed')}</td>
                        <td className="px-2 py-2 text-center bg-blue-100/40 border-l-2 border-slate-400/60">{tv('kills')}</td>
                        <td className="px-2 py-2 text-center bg-blue-100/40">{tv('errors')}</td>
                        <td className="px-2 py-2 text-center bg-blue-100/40">{tv('attempts')}</td>
                        <td className="px-2 py-2 text-center bg-blue-100/50">
                          {computedH !== null ? <span className="font-bold text-slate-600 text-xs">{computedH >= 0 ? '+' : ''}{computedH.toFixed(3)}</span> : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                        <td className="px-2 py-2 text-center bg-emerald-100/40 border-l-2 border-slate-400/60">{tv('aces')}</td>
                        <td className="px-2 py-2 text-center bg-emerald-100/40">{tv('serveErrors')}</td>
                        <td className="px-2 py-2 text-center bg-emerald-100/40">{tv('servingAttempts')}</td>
                        <td className="px-2 py-2 text-center bg-orange-100/40 border-l-2 border-slate-400/60">{tv('blocks')}</td>
                        <td className="px-2 py-2 text-center bg-orange-100/40">{tv('blockAssist')}</td>
                        <td className="px-2 py-2 text-center border-l-2 border-slate-400/60">{tv('digs')}</td>
                        <td className="px-2 py-2 text-center">{tv('assists')}</td>
                        <td className="px-2 py-2 text-center bg-violet-100/40 border-l-2 border-slate-400/60">{tv('srPerfect')}</td>
                        <td className="px-2 py-2 text-center bg-violet-100/40">{tv('srGood')}</td>
                        <td className="px-2 py-2 text-center bg-violet-100/40">{tv('srPoor')}</td>
                        <td className="px-2 py-2 text-center bg-violet-100/40">{tv('srError')}</td>
                        <td className="px-2 py-2 text-center bg-violet-100/50">
                          {computedSrRtg !== null ? <span className="font-bold text-slate-600 text-xs">{computedSrRtg.toFixed(2)}</span> : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  }

                  // ── PLAYER ROW ──
                  const matchedPlayer = players.find(p =>
                    p.jerseyNumber === item.jerseyNumber ||
                    p.jerseyNumber === `#${item.jerseyNumber}` ||
                    `#${p.jerseyNumber}` === item.jerseyNumber
                  );
                  const manualId = manualPlayerMap[idx];
                  const manualPlayer = manualId ? players.find(p => p.id === manualId) : undefined;

                  const numInput = (field: keyof ExtractedStats) => (
                    <input
                      type="number"
                      min="0"
                      className="w-12 p-1 text-center border border-slate-300 rounded text-xs"
                      value={(item[field] as number) || 0}
                      onChange={e => handleScannedStatChange(idx, field, e.target.value)}
                    />
                  );

                  return (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-center sticky left-0 bg-white">
                        <input type="text" className="w-12 p-1 text-center border border-slate-300 rounded text-xs" value={item.jerseyNumber} onChange={e => handleScannedStatChange(idx, 'jerseyNumber', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        {matchedPlayer ? (
                          <span className="font-medium text-slate-800 text-xs">{matchedPlayer.name}</span>
                        ) : manualPlayer ? (
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-slate-800 text-xs">{manualPlayer.name}</span>
                            <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded font-bold">manual</span>
                            <button
                              className="text-[9px] text-slate-400 hover:text-slate-600 underline"
                              onClick={() => setManualPlayerMap(prev => { const n = { ...prev }; delete n[idx]; return n; })}
                            >change</button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <span className="text-amber-600 flex items-center gap-1 text-[10px]">
                              <AlertTriangle className="w-3 h-3 shrink-0" /> #{item.jerseyNumber} not found
                            </span>
                            <select
                              className="text-xs border border-amber-300 rounded px-1 py-0.5 bg-white text-slate-700 max-w-[140px]"
                              value=""
                              onChange={e => { if (e.target.value) setManualPlayerMap(prev => ({ ...prev, [idx]: e.target.value })); }}
                            >
                              <option value="">— assign player —</option>
                              {players.map(p => (
                                <option key={p.id} value={p.id}>#{p.jerseyNumber} {p.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </td>
                      {/* Sets Played */}
                      <td className="px-2 py-2 text-center bg-slate-100/60">{numInput('setsPlayed')}</td>
                      {/* Hitting */}
                      <td className="px-2 py-2 text-center bg-blue-50/20 border-l-2 border-slate-400/60">{numInput('kills')}</td>
                      <td className="px-2 py-2 text-center bg-blue-50/20">{numInput('errors')}</td>
                      <td className="px-2 py-2 text-center bg-blue-50/20">{numInput('attempts')}</td>
                      <td className="px-2 py-2 text-center bg-blue-50/30">
                        {computedH !== null ? (
                          <span className={`text-xs font-bold ${hPctMatch === true ? 'text-green-600' : hPctMatch === false ? 'text-amber-600' : 'text-slate-400'}`}>
                            {computedH >= 0 ? '+' : ''}{computedH.toFixed(3)}
                            {hPctMatch === false && <span title={`Sheet: ${hPctNum?.toFixed(3)}`}> ⚠</span>}
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      {/* Serving */}
                      <td className="px-2 py-2 text-center bg-emerald-50/20 border-l-2 border-slate-400/60">{numInput('aces')}</td>
                      <td className="px-2 py-2 text-center bg-emerald-50/20">{numInput('serveErrors')}</td>
                      <td className="px-2 py-2 text-center bg-emerald-50/20">{numInput('servingAttempts')}</td>
                      {/* Blocking */}
                      <td className="px-2 py-2 text-center bg-orange-50/20 border-l-2 border-slate-400/60">{numInput('blocks')}</td>
                      <td className="px-2 py-2 text-center bg-orange-50/20">{numInput('blockAssist')}</td>
                      {/* Dig & Assist */}
                      <td className="px-2 py-2 text-center border-l-2 border-slate-400/60">{numInput('digs')}</td>
                      <td className="px-2 py-2 text-center">{numInput('assists')}</td>
                      {/* Serve Receive */}
                      <td className="px-2 py-2 text-center bg-violet-50/20 border-l-2 border-slate-400/60">{numInput('srPerfect')}</td>
                      <td className="px-2 py-2 text-center bg-violet-50/20">{numInput('srGood')}</td>
                      <td className="px-2 py-2 text-center bg-violet-50/20">{numInput('srPoor')}</td>
                      <td className="px-2 py-2 text-center bg-violet-50/20">{numInput('srError')}</td>
                      <td className="px-2 py-2 text-center bg-violet-50/30">
                        {computedSrRtg !== null ? (
                          <span className={`text-xs font-bold ${srRtgMatch === true ? 'text-green-600' : srRtgMatch === false ? 'text-amber-600' : 'text-slate-400'}`}>
                            {computedSrRtg.toFixed(2)}
                            {srRtgMatch === false && <span title={`Sheet: ${item.srRating?.toFixed(2)}`}> ⚠</span>}
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            {scannedStats.filter(s => !isSummaryRow(s)).length === 0 && scannedTeamReport
              ? 'No player rows detected — only Match Report data (Sideout % and Point Scoring %) will be saved. This is normal for a Match Report PDF upload.'
              : 'Rows with no matched or manually selected player will be skipped. H% and SR Rtg columns validate against sheet values (green = match, amber = mismatch).'
            }
          </div>
        </div>
      )}

      {/* ── Serve Receiving Summary Bar ───────────────────────────────────── */}
      {!isEditing && statsViewMode === 'player' && activeCategory === 'Serve Receiving' && filteredEvents.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-4 mb-1">
          <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
            <div>
              <div className="text-[10px] font-bold text-violet-500 uppercase tracking-wide">Team Pass Avg (AVG)</div>
              <div className="stat-num sm text-violet-800">{teamMetrics.serveReceiveAvg > 0 ? teamMetrics.serveReceiveAvg.toFixed(2) : '—'}</div>
              <div className="text-[10px] text-violet-400">Target: 2.0+ / 3.0 scale</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Stats Table (edit mode or view mode) ─────────────────────────── */}
      {(statsViewMode === 'player' || isEditing) && (filteredEvents.length > 0 || isEditing) && !isReviewingScan && (
        <div className="bg-white rounded-xl rounded-tl-none shadow-sm border border-slate-200 overflow-hidden">
          {/* Match Result — shown in manual entry and edit modes */}
          {isEditing && (() => {
            const hasDetailScores = scannedSetScores.length > 0 && scannedSetScores.some(s => s.our > 0 || s.their > 0);
            // When detail scores exist, derive sets won from them
            const effectiveOur = hasDetailScores ? scannedSetScores.filter(s => s.our > s.their).length : manualSetsWon.our;
            const effectiveTheir = hasDetailScores ? scannedSetScores.filter(s => s.their > s.our).length : manualSetsWon.their;
            const resultLabel = effectiveOur > effectiveTheir ? 'W' : effectiveOur < effectiveTheir ? 'L' : effectiveOur > 0 ? 'T' : null;
            const resultCls = resultLabel === 'W' ? 'bg-green-100 text-green-700 border-green-200' : resultLabel === 'L' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-slate-100 text-slate-500 border-slate-200';
            return (
            <div className="p-4 border-b border-slate-200 bg-slate-50/50 space-y-3">
              {/* Sets Won — always shown */}
              <div className="flex items-center gap-2 flex-wrap">
                <Trophy className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="font-bold text-slate-700 text-sm">Sets Won</span>
                <span className="text-xs text-slate-400">optional — determines W/L</span>
                {resultLabel && (
                  <span className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-bold border ${resultCls}`}>
                    {resultLabel} {effectiveOur}–{effectiveTheir}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-slate-500 w-8">Us</span>
                  <input
                    type="text" inputMode="numeric" pattern="[0-9]*"
                    value={hasDetailScores ? effectiveOur : (manualSetsWon.our || '')}
                    onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 1); setManualSetsWon(prev => ({ ...prev, our: v ? parseInt(v) : 0 })); }}
                    placeholder="0"
                    readOnly={hasDetailScores}
                    className={`w-12 text-center text-sm font-bold border border-slate-200 rounded px-1 py-1 outline-none focus:ring-1 focus:ring-blue-400 ${hasDetailScores ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
                  />
                </div>
                <span className="text-slate-400 font-bold text-xs">–</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-slate-500 w-10">Them</span>
                  <input
                    type="text" inputMode="numeric" pattern="[0-9]*"
                    value={hasDetailScores ? effectiveTheir : (manualSetsWon.their || '')}
                    onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 1); setManualSetsWon(prev => ({ ...prev, their: v ? parseInt(v) : 0 })); }}
                    placeholder="0"
                    readOnly={hasDetailScores}
                    className={`w-12 text-center text-sm font-bold border border-slate-200 rounded px-1 py-1 outline-none focus:ring-1 focus:ring-blue-400 ${hasDetailScores ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              {/* Set Score Details — expandable */}
              <div>
                {scannedSetScores.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      const totalSets = (effectiveOur || 0) + (effectiveTheir || 0);
                      const count = totalSets > 0 ? totalSets : 3;
                      setScannedSetScores(Array.from({ length: count }, () => ({ our: 0, their: 0 })));
                    }}
                    className="w-full py-2.5 border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-sm rounded-lg transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Set Scores
                  </button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-slate-500">Set Scores</span>
                      <button
                        type="button"
                        onClick={() => setScannedSetScores(prev => [...prev, { our: 0, their: 0 }])}
                        className="ml-auto text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                      >
                        + Add Set
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {scannedSetScores.map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                          <span className="text-[10px] font-bold text-slate-400 uppercase w-10">Set {i + 1}</span>
                          <input
                            type="text" inputMode="numeric" pattern="[0-9]*"
                            value={s.our || ''}
                            onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setScannedSetScores(prev => prev.map((x, j) => j === i ? { ...x, our: v ? parseInt(v) : 0 } : x)); }}
                            placeholder="0"
                            className="w-12 text-center text-sm font-bold border border-slate-200 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                          />
                          <span className="text-slate-400 font-bold text-xs">–</span>
                          <input
                            type="text" inputMode="numeric" pattern="[0-9]*"
                            value={s.their || ''}
                            onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setScannedSetScores(prev => prev.map((x, j) => j === i ? { ...x, their: v ? parseInt(v) : 0 } : x)); }}
                            placeholder="0"
                            className="w-12 text-center text-sm font-bold border border-slate-200 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                          />
                          <button
                            type="button"
                            onClick={() => setScannedSetScores(prev => prev.filter((_, j) => j !== i))}
                            className="text-slate-300 hover:text-red-400 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            );
          })()}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 font-medium uppercase text-xs">
                {/* Section group headers */}
                <tr className="border-b border-slate-200">
                  {getGroupHeaders().map((g, i) => (
                    g.label ? (
                      <th key={g.label} colSpan={g.colSpan}
                        className={`px-2 py-1 text-center text-[9px] font-bold tracking-widest border-l-2 border-slate-400/60 ${SECTION_COLORS[g.label] || 'bg-slate-100 text-slate-500'}`}
                      >{g.label}</th>
                    ) : (
                      <th key={`empty-${i}`} colSpan={g.colSpan}
                        className={`px-2 py-1 ${i === 0 && !isEditing ? 'sticky left-0 bg-slate-50 z-10' : ''}`}
                      />
                    )
                  ))}
                </tr>
                <tr>
                  {getColumns().map((col: any) => (
                    <th key={col.key} className={`px-4 py-3 ${col.sectionStart ? 'border-l-2 border-slate-400/60' : ''} ${col.highlight ? 'bg-blue-50/50 text-blue-700' : ''} ${col.key === 'player.name' ? 'sticky left-0 bg-slate-50 z-10 w-48 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]' : 'text-center'}`}>
                      <button
                        onClick={() => (!isEditing && col.key !== 'player.name') ? requestSort(col.key) : undefined}
                        className={`flex items-center gap-1 hover:text-slate-800 ${col.key !== 'player.name' ? 'justify-center w-full' : ''}`}
                        disabled={isEditing}
                      >
                        {col.tooltip && col.key !== 'player.name' ? (
                          <ColTooltip text={col.tooltip}>{col.header}</ColTooltip>
                        ) : col.header}
                        {!isEditing && col.key !== 'player.name' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedData.length === 0 ? (
                  <tr><td colSpan={getColumns().length} className="px-4 py-12 text-center text-slate-400 italic">No data found for this period. Start recording games to see stats!</td></tr>
                ) : (
                  <>
                    {sortedData.map((row) => {
                      const isSelected = selectedPlayerId === row.playerId;
                      const chartData = isSelected ? playerChartData.get(row.playerId) : undefined;

                      // ── Inline detail panel (rendered as a table row) ──────────────
                      const inlineDetail = isSelected && !isEditing && chartData ? (() => {
                        const player = row.player;

                        const getSRBucket = (): SRBucket | null => {
                          if (srFilter === 'hands') return chartData.srByContact.hands ?? null;
                          if (srFilter === 'platform') return chartData.srByContact.platform ?? null;
                          if (srFilter === 'float') return chartData.srByStyle.float ?? null;
                          if (srFilter === 'topspin') return chartData.srByStyle.topspin ?? null;
                          return { p3: row.srPerfect, p2: row.srGood, p1: row.srPoor, p0: 0, errors: row.receptionErrors };
                        };

                        const srBucket = getSRBucket();
                        const srTotal = srBucket ? srBucket.p3 + srBucket.p2 + srBucket.p1 + srBucket.p0 + srBucket.errors : 0;
                        const srPieData = srBucket && srTotal > 0 ? [
                          { name: 'Perfect (3)', value: srBucket.p3, color: '#22c55e' },
                          { name: 'Good (2)', value: srBucket.p2, color: '#3b82f6' },
                          { name: 'Poor (1)', value: srBucket.p1, color: '#f59e0b' },
                          { name: 'Error (0)', value: srBucket.errors, color: '#ef4444' },
                        ].filter(d => d.value > 0) : [];

                        const inPlay = Math.max(0, row.attackAttempts - row.kills - row.attackErrors);
                        const hittingTotal = row.attackAttempts;
                        const hittingPieData = hittingTotal > 0 ? [
                          { name: 'Kills', value: row.kills, color: '#22c55e' },
                          { name: 'Errors', value: row.attackErrors, color: '#ef4444' },
                          { name: 'In Play', value: inPlay, color: '#94a3b8' },
                        ].filter(d => d.value > 0) : [];

                        type ServeEntry = { attempts: number; aces: number; errors: number };
                        const serveTypeLabel: Record<string, string> = { jump: 'Jump', float: 'Float', 'jump-float': 'Jump-Float' };
                        const serveColors = ['#8b5cf6', '#0ea5e9', '#6366f1'];
                        const serveEntries = (Object.entries(chartData.serveByType) as [string, ServeEntry][]).filter(([, d]) => d.attempts > 0);
                        const serveTotalAtt = serveEntries.reduce((s, [, d]) => s + d.attempts, 0);
                        const servePieData = serveEntries.map(([type, d], i) => ({
                          name: serveTypeLabel[type] ?? type,
                          value: d.attempts,
                          color: serveColors[i % serveColors.length],
                          raw: d,
                        }));

                        const SR_FILTERS: { key: typeof srFilter; label: string }[] = [
                          { key: 'all', label: 'All' },
                          { key: 'hands', label: 'Hands' },
                          { key: 'platform', label: 'Platform' },
                          { key: 'float', label: 'Float' },
                          { key: 'topspin', label: 'Topspin' },
                        ];

                        const hasAnyData = srTotal > 0 || hittingTotal > 0 || serveTotalAtt > 0 || row.serveAttempts > 0;

                        // ── Per-event timeline data ──
                        type EventPoint = {
                          label: string; fullLabel: string; date: string;
                          kills: number; attackAttempts: number; attackErrors: number; hitPct: number | null;
                          aces: number; serveAttempts: number; serveErrors: number;
                          digs: number; assists: number; blocks: number;
                          receptions: number; receptionErrors: number; avgPass: number | null;
                        };
                        const timelineData: EventPoint[] = filteredEvents
                          .map(evt => {
                            let stats: StatRecord[];
                            if (sourceFilter !== 'all') {
                              const all: StatRecord[] = [...(evt.stats ?? [])];
                              matchRecords.filter(r => r.eventId === evt.id).forEach(r => { if (r.gameState.stats?.length) all.push(...r.gameState.stats); });
                              stats = all.filter(s => sourceFilter === 'live' ? (!s.source || s.source === 'live') : s.source === sourceFilter);
                            } else {
                              const rec = matchRecords.find(r => r.eventId === evt.id && r.isRecord)?.gameState.stats;
                              stats = rec?.length ? rec : (evt.stats ?? []);
                            }
                            const ps = stats.filter(s => s.playerId === row.playerId);
                            if (ps.length === 0) return null;
                            let kills = 0, attackAttempts = 0, attackErrors = 0;
                            let aces = 0, serveAttempts = 0, serveErrors = 0;
                            let digs = 0, assists = 0, blocks = 0;
                            let receptions = 0, receptionErrors = 0, receptionSum = 0;
                            ps.forEach(s => {
                              if (s.statType === 'kill') { kills++; attackAttempts++; }
                              else if (s.statType === 'attack_attempt') attackAttempts++;
                              else if (s.statType === 'attack_error') { attackAttempts++; attackErrors++; }
                              else if (s.statType === 'ace') { aces++; serveAttempts++; }
                              else if (s.statType === 'serve_attempt') serveAttempts++;
                              else if (s.statType === 'serve_error') { serveAttempts++; serveErrors++; }
                              else if (s.statType === 'dig') digs++;
                              else if (s.statType === 'assist') assists++;
                              else if (s.statType === 'block_solo') blocks++;
                              else if (s.statType === 'block_assist') blocks += 0.5;
                              else if (s.statType === 'reception') { receptions++; if (s.passRating !== undefined) receptionSum += s.passRating; }
                              else if (s.statType === 'reception_error') { receptions++; receptionErrors++; }
                            });
                            const mm = evt.date.slice(5).replace('-', '/');
                            const opp = evt.opponent?.trim() ?? '';
                            const label = evt.type === 'tournament'
                              ? (opp ? `${mm} · ${opp.substring(0, 10)}` : mm)
                              : mm;
                            const fullLabel = opp ? `${mm} vs ${opp}` : mm;
                            return { label, fullLabel, date: evt.date, kills, attackAttempts, attackErrors, hitPct: attackAttempts > 0 ? (kills - attackErrors) / attackAttempts : null, aces, serveAttempts, serveErrors, digs, assists, blocks, receptions, receptionErrors, avgPass: receptions > 0 ? Math.round((receptionSum / receptions) * 100) / 100 : null };
                          })
                          .filter((d): d is EventPoint => d !== null)
                          .sort((a, b) => a.date.localeCompare(b.date));

                        type TimelineStatDef = { key: keyof EventPoint; label: string; color: string; isDecimal?: boolean };
                        const TIMELINE_STATS: TimelineStatDef[] = ([
                          { key: 'kills', label: 'Kills', color: '#22c55e' },
                          { key: 'attackAttempts', label: 'Attacks', color: '#3b82f6' },
                          { key: 'hitPct', label: 'Hit %', color: '#6366f1', isDecimal: true },
                          { key: 'aces', label: 'Aces', color: '#8b5cf6' },
                          { key: 'serveAttempts', label: 'Serves', color: '#0ea5e9' },
                          { key: 'digs', label: 'Digs', color: '#f59e0b' },
                          { key: 'assists', label: 'Assists', color: '#14b8a6' },
                          { key: 'blocks', label: 'Blocks', color: '#ef4444' },
                          { key: 'receptions', label: 'Receptions', color: '#ec4899' },
                          { key: 'avgPass', label: 'Pass Avg', color: '#10b981', isDecimal: true },
                        ] as TimelineStatDef[]).filter(s => timelineData.some(d => d[s.key] !== null && (d[s.key] as number) > 0));

                        const activeStatMeta = TIMELINE_STATS.find(s => s.key === timelineStat) ?? TIMELINE_STATS[0];
                        const effectiveStatKey = activeStatMeta?.key ?? 'kills';

                        const serveInPct = row.serveAttempts > 0 ? (((row.serveAttempts - row.serveErrors) / row.serveAttempts) * 100).toFixed(0) : '—';
                        const aceRatePct = row.serveAttempts > 0 ? ((row.aces / row.serveAttempts) * 100).toFixed(0) : '—';
                        const basicServePieData = [
                          { name: 'Ace', value: row.aces, color: '#22c55e' },
                          { name: 'Error', value: row.serveErrors, color: '#ef4444' },
                          { name: 'In Play', value: Math.max(0, (row.serveAttempts - row.serveErrors) - row.aces), color: '#94a3b8' },
                        ].filter(d => d.value > 0);

                        return (
                          <tr key={`${row.playerId}-detail`} className="bg-gradient-to-r from-blue-50/60 to-indigo-50/40">
                            <td colSpan={getColumns().length} className="px-4 py-4">
                              <div className="rounded-xl border border-blue-200 bg-white shadow-sm overflow-hidden">
                                {/* Header */}
                                <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {player.photoUrl ? (
                                        <img src={player.photoUrl} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm" />
                                      ) : (
                                        <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-black">{player.name.charAt(0)}</div>
                                      )}
                                      <div>
                                        <div className="font-bold text-slate-900 text-sm">{player.name}</div>
                                        <div className="text-xs text-slate-500">{player.position}{player.jerseyNumber ? ` · #${player.jerseyNumber}` : ''} · {row.matchesPlayed}MP / {row.gamesPlayed}SP</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-1">
                                        {(['charts', 'timeline'] as const).map(tab => (
                                          <button key={tab} onClick={e => { e.stopPropagation(); setPlayerDetailTab(tab); }} className={`px-3 py-1 rounded-md text-xs font-bold transition-colors capitalize ${playerDetailTab === tab ? 'bg-blue-600 text-white' : 'bg-white/60 text-slate-500 hover:bg-white hover:text-slate-700'}`}>{tab === 'charts' ? 'Charts' : 'Timeline'}</button>
                                        ))}
                                      </div>
                                      <button onClick={e => { e.stopPropagation(); setSelectedPlayerId(null); }} className="p-1.5 rounded-lg hover:bg-blue-100 text-slate-400 hover:text-slate-600 transition-colors" title="Close">
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {playerDetailTab === 'timeline' && (
                                  <div className="p-5 space-y-5" onClick={e => e.stopPropagation()}>
                                    <div>
                                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Cumulative Totals</div>
                                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                        {[
                                          { label: 'Kills', value: row.kills, color: '#22c55e', timelineKey: 'kills' },
                                          { label: 'Attacks', value: row.attackAttempts, color: '#3b82f6', timelineKey: 'attackAttempts' },
                                          { label: 'Hit %', value: row.attackAttempts > 0 ? ((row.kills - row.attackErrors) / row.attackAttempts * 100).toFixed(1) + '%' : '—', color: '#6366f1', timelineKey: 'hitPct' },
                                          { label: 'Aces', value: row.aces, color: '#8b5cf6', timelineKey: 'aces' },
                                          { label: 'Serves', value: row.serveAttempts, color: '#0ea5e9', timelineKey: 'serveAttempts' },
                                          { label: 'Digs', value: row.digs, color: '#f59e0b', timelineKey: 'digs' },
                                          { label: 'Assists', value: row.assists, color: '#14b8a6', timelineKey: 'assists' },
                                          { label: 'Blocks', value: row.blockSolo + row.blockAssist, color: '#ef4444', timelineKey: 'blocks' },
                                          { label: 'Receptions', value: row.receptions, color: '#ec4899', timelineKey: 'receptions' },
                                          { label: 'Rcv Errors', value: row.receptionErrors, color: '#f43f5e', timelineKey: null },
                                          { label: 'Pass Avg', value: row.receptions > 0 ? (row.receptionSum / row.receptions).toFixed(2) : '—', color: '#10b981', timelineKey: 'avgPass' },
                                          { label: 'Matches', value: row.matchesPlayed, color: '#64748b', timelineKey: null },
                                        ].map(s => {
                                          const isSelectable = s.timelineKey !== null && timelineData.length >= 2 && TIMELINE_STATS.some(t => String(t.key) === s.timelineKey);
                                          const isActive = isSelectable && timelineStat === s.timelineKey;
                                          return isSelectable ? (
                                            <button
                                              key={s.label}
                                              type="button"
                                              onClick={() => { setTimelineStat(s.timelineKey!); }}
                                              className="bg-slate-50 rounded-lg p-2 text-center border transition-all hover:shadow-sm"
                                              style={isActive ? { borderColor: s.color, boxShadow: `0 0 0 2px ${s.color}33` } : { borderColor: '#f1f5f9' }}
                                            >
                                              <div className="text-base font-black" style={{ color: s.color }}>{s.value}</div>
                                              <div className="text-[10px] leading-tight mt-0.5 font-semibold" style={{ color: isActive ? s.color : '#94a3b8' }}>{s.label}</div>
                                            </button>
                                          ) : (
                                            <div key={s.label} className="bg-slate-50 rounded-lg p-2 text-center border border-slate-100">
                                              <div className="text-base font-black" style={{ color: s.color }}>{s.value}</div>
                                              <div className="text-[10px] text-slate-400 leading-tight mt-0.5">{s.label}</div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    {timelineData.length < 2 ? (
                                      <div className="flex items-center justify-center h-28 text-xs text-slate-400 italic bg-slate-50 rounded-lg">
                                        {timelineData.length === 0 ? 'No per-game data available.' : 'Need at least 2 games to show a timeline.'}
                                      </div>
                                    ) : (
                                      <div className="space-y-3">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Trend Over Time ({timelineData.length} games)</div>
                                        <div className="flex flex-wrap gap-1">
                                          {TIMELINE_STATS.map(s => (
                                            <button key={String(s.key)} onClick={() => setTimelineStat(String(s.key))} className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${timelineStat === String(s.key) ? 'text-white border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`} style={timelineStat === String(s.key) ? { background: s.color, borderColor: s.color } : {}}>
                                              {s.label}
                                            </button>
                                          ))}
                                        </div>
                                        <ResponsiveContainer width="100%" height={200}>
                                          <LineChart data={timelineData} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={0} angle={timelineData.length > 6 ? -35 : 0} textAnchor={timelineData.length > 6 ? 'end' : 'middle'} height={timelineData.length > 6 ? 40 : 20} />
                                            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={32} tickFormatter={activeStatMeta?.isDecimal ? (v: number) => v.toFixed(2) : undefined} />
                                            <RechartsTooltip
                                              content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0].payload as EventPoint;
                                                // Use payload[0].value (bound to the Line's dataKey by Recharts)
                                                // rather than re-deriving from the closure — avoids stale capture.
                                                const v = payload[0].value as number;
                                                const meta = TIMELINE_STATS.find(s => String(s.key) === payload[0].dataKey);
                                                return (
                                                  <div style={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', padding: '6px 10px' }}>
                                                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{d.fullLabel}</div>
                                                    <div style={{ color: meta?.color ?? '#3b82f6' }}>{meta?.label ?? payload[0].dataKey}: {meta?.isDecimal ? v.toFixed(3) : v}</div>
                                                  </div>
                                                );
                                              }}
                                            />
                                            <Line type="monotone" dataKey={String(effectiveStatKey)} stroke={activeStatMeta?.color ?? '#3b82f6'} strokeWidth={2} dot={{ r: 4, fill: activeStatMeta?.color ?? '#3b82f6', strokeWidth: 0 }} activeDot={{ r: 6 }} connectNulls />
                                          </LineChart>
                                        </ResponsiveContainer>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {playerDetailTab === 'charts' && !hasAnyData ? (
                                  <div className="px-5 py-8 text-center text-slate-400 text-sm italic">No detailed chart data available for this player.</div>
                                ) : playerDetailTab === 'charts' && (
                                  <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 divide-y md:divide-y-0 md:divide-x divide-slate-100" onClick={e => e.stopPropagation()}>

                                    {/* ── Serve Receive ── */}
                                    <div className="space-y-2 pb-3 md:pb-0 md:pr-3">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-blue-500" /> Serve Receive</h4>
                                        {srTotal > 0 && <span className="text-xs font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">N={srTotal}</span>}
                                      </div>
                                      <div className="flex flex-wrap gap-1">
                                        {SR_FILTERS.map(f => {
                                          const hasFiltData = f.key === 'all'
                                            ? (row.srPerfect + row.srGood + row.srPoor + row.receptionErrors) > 0
                                            : f.key === 'hands' ? !!chartData.srByContact.hands
                                            : f.key === 'platform' ? !!chartData.srByContact.platform
                                            : f.key === 'float' ? !!chartData.srByStyle.float
                                            : !!chartData.srByStyle.topspin;
                                          if (!hasFiltData && f.key !== 'all') return null;
                                          return (
                                            <button key={f.key} onClick={() => setSrFilter(f.key)}
                                              className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${srFilter === f.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-600'}`}>
                                              {f.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {srTotal === 0 ? (
                                        <div className="flex items-center justify-center h-24 text-xs text-slate-400 italic">No reception data</div>
                                      ) : (
                                        <>
                                          <ResponsiveContainer width="100%" height={130}>
                                            <PieChart>
                                              <Pie data={srPieData} cx="50%" cy="50%" outerRadius={55} dataKey="value" strokeWidth={2} stroke="white">
                                                {srPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                              </Pie>
                                              <RechartsTooltip formatter={(v, name) => [`${v} (${((Number(v) / srTotal) * 100).toFixed(0)}%)`, name as string]} />
                                            </PieChart>
                                          </ResponsiveContainer>
                                          <div className="grid grid-cols-2 gap-1.5">
                                            {srPieData.map(d => (
                                              <div key={d.name} className="flex items-center gap-1.5 text-[11px]">
                                                <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                                                <span className="text-slate-500 truncate">{d.name}</span>
                                                <span className="ml-auto font-bold text-slate-700">{d.value}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </>
                                      )}
                                    </div>

                                    {/* ── Serving ── */}
                                    <div className="space-y-2 pt-3 pb-3 md:pt-0 md:pb-0 md:px-3">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-violet-500" /> Serving</h4>
                                        {row.serveAttempts > 0 && <span className="text-xs font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">N={row.serveAttempts}</span>}
                                      </div>
                                      {row.serveAttempts === 0 ? (
                                        <div className="flex items-center justify-center h-24 text-xs text-slate-400 italic">No serving data</div>
                                      ) : serveTotalAtt === 0 ? (
                                        /* Basic serve stats — no live type breakdown available */
                                        <div className="space-y-2 pt-1">
                                          <div className="text-center">
                                            <span className="stat-num md text-blue-600">{serveInPct === '—' ? '—' : serveInPct + '%'}</span>
                                            <span className="text-xs text-slate-400 ml-1.5">Serve In</span>
                                          </div>
                                          <ResponsiveContainer width="100%" height={120}>
                                            <PieChart>
                                              <Pie data={basicServePieData} cx="50%" cy="50%" outerRadius={50} dataKey="value" strokeWidth={2} stroke="white">
                                                {basicServePieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                              </Pie>
                                              <RechartsTooltip formatter={(v, name) => [`${v} (${((Number(v) / row.serveAttempts) * 100).toFixed(0)}%)`, name as string]} />
                                            </PieChart>
                                          </ResponsiveContainer>
                                          <div className="grid grid-cols-3 gap-1.5">
                                            {basicServePieData.map(d => (
                                              <div key={d.name} className="flex flex-col items-center gap-0.5 text-[11px]">
                                                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
                                                <span className="text-slate-500 font-medium">{d.name}</span>
                                                <span className="font-bold text-slate-700">{d.value} <span className="font-normal text-slate-400">({((d.value / row.serveAttempts) * 100).toFixed(0)}%)</span></span>
                                              </div>
                                            ))}
                                          </div>
                                          <div className="text-[10px] text-slate-400 italic text-center">Serve-type breakdown requires live tracking</div>
                                        </div>
                                      ) : (
                                        <>
                                          <ResponsiveContainer width="100%" height={130}>
                                            <PieChart>
                                              <Pie data={servePieData} cx="50%" cy="50%" outerRadius={55} dataKey="value" strokeWidth={2} stroke="white">
                                                {servePieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                              </Pie>
                                              <RechartsTooltip formatter={(v, name) => [`${v} att (${((Number(v) / serveTotalAtt) * 100).toFixed(0)}%)`, name as string]} />
                                            </PieChart>
                                          </ResponsiveContainer>
                                          <div className="space-y-2">
                                            {servePieData.map(d => {
                                              const acePct = d.raw.attempts > 0 ? ((d.raw.aces / d.raw.attempts) * 100).toFixed(0) : '0';
                                              const srvPct = d.raw.attempts > 0 ? (((d.raw.attempts - d.raw.errors) / d.raw.attempts) * 100).toFixed(0) : '0';
                                              return (
                                                <div key={d.name} className="flex items-center gap-2 text-[11px]">
                                                  <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                                                  <span className="text-slate-600 w-16 shrink-0 font-medium">{d.name}</span>
                                                  <span className="text-slate-400">{d.raw.attempts}</span>
                                                  <span className="ml-auto font-bold text-emerald-600">{acePct}% ACE</span>
                                                  <span className="font-bold text-blue-600">{srvPct}% IN</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </>
                                      )}
                                    </div>

                                    {/* ── Hitting ── */}
                                    <div className="space-y-2 pt-3 md:pt-0 md:pl-3">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-green-500" /> Hitting</h4>
                                        {hittingTotal > 0 && <span className="text-xs font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">N={hittingTotal}</span>}
                                      </div>
                                      {hittingTotal === 0 ? (
                                        <div className="flex items-center justify-center h-24 text-xs text-slate-400 italic">No attack data</div>
                                      ) : (
                                        <>
                                          <ResponsiveContainer width="100%" height={130}>
                                            <PieChart>
                                              <Pie data={hittingPieData} cx="50%" cy="50%" outerRadius={55} dataKey="value" strokeWidth={2} stroke="white">
                                                {hittingPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                              </Pie>
                                              <RechartsTooltip formatter={(v, name) => [`${v} (${((Number(v) / hittingTotal) * 100).toFixed(0)}%)`, name as string]} />
                                            </PieChart>
                                          </ResponsiveContainer>
                                          <div className="grid grid-cols-3 gap-2 text-center">
                                            {[
                                              { label: 'Kills', value: row.kills, color: '#22c55e' },
                                              { label: 'Errors', value: row.attackErrors, color: '#ef4444' },
                                              { label: 'In Play', value: inPlay, color: '#94a3b8' },
                                            ].map(d => (
                                              <div key={d.label}>
                                                <div className="text-xl font-black" style={{ color: d.color }}>{d.value}</div>
                                                <div className="text-[10px] text-slate-500">{d.label}</div>
                                                <div className="text-[10px] font-bold text-slate-400">{((d.value / hittingTotal) * 100).toFixed(0)}%</div>
                                              </div>
                                            ))}
                                          </div>
                                          {/* Error type breakdown — only shown when detail data exists */}
                                          {(() => {
                                            const errByType = chartData.attackErrorsByType;
                                            const hasDetail = ERR_TYPE_ORDER.filter(k => k !== 'other').some(k => (errByType[k] ?? 0) > 0);
                                            if (!hasDetail) return null;
                                            const errPieData = ERR_TYPE_ORDER
                                              .map(k => ({ name: ERR_TYPE_LABELS[k], value: errByType[k] ?? 0, color: ERR_TYPE_COLORS[k] }))
                                              .filter(d => d.value > 0)
                                              .sort((a, b) => b.value - a.value);
                                            const errTotal = errPieData.reduce((s, d) => s + d.value, 0);
                                            return (
                                              <div className="pt-2 border-t border-slate-100">
                                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Error Breakdown <span className="font-normal normal-case">(N={errTotal})</span></div>
                                                <ResponsiveContainer width="100%" height={100}>
                                                  <PieChart>
                                                    <Pie data={errPieData} cx="50%" cy="50%" outerRadius={42} dataKey="value" strokeWidth={2} stroke="white">
                                                      {errPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                                    </Pie>
                                                    <RechartsTooltip formatter={(v, name) => [`${v} (${((Number(v) / errTotal) * 100).toFixed(0)}%)`, name as string]} />
                                                  </PieChart>
                                                </ResponsiveContainer>
                                                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                                  {errPieData.map(d => (
                                                    <div key={d.name} className="flex items-center gap-1.5 text-[10px]">
                                                      <div className="w-2 h-2 rounded-sm shrink-0" style={{ background: d.color }} />
                                                      <span className="text-slate-500 truncate">{d.name}</span>
                                                      <span className="ml-auto font-bold text-slate-700">{d.value}</span>
                                                      <span className="text-slate-400">{((d.value / errTotal) * 100).toFixed(0)}%</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </>
                                      )}
                                    </div>

                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })() : null;

                      return (
                        <React.Fragment key={row.playerId}>
                          <tr
                            className={`hover:bg-blue-50/40 transition-colors group cursor-pointer ${isSelected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : ''}`}
                            onClick={() => { setSelectedPlayerId(prev => { const next = prev === row.playerId ? null : row.playerId; if (next !== prev) { setPlayerDetailTab('charts'); setSrFilter('all'); } return next; }); }}
                          >
                            {getColumns().map((col: any) => {
                              const val = col.render ? col.render(row) : (row as any)[col.key];
                              return (
                                <td key={col.key} className={`px-4 py-3 ${col.sectionStart ? 'border-l-2 border-slate-300/70' : ''} ${col.key === 'player.name' ? `sticky left-0 border-r border-slate-100 z-10 ${isSelected ? 'bg-blue-50' : 'bg-white group-hover:bg-blue-50/40'}` : 'text-center text-slate-600'} ${col.highlight ? 'font-bold text-slate-900 bg-blue-50/10' : ''}`}>
                                  {val}
                                </td>
                              );
                            })}
                          </tr>
                          {inlineDetail}
                        </React.Fragment>
                      );
                    })}
                    {!isEditing && teamAggRow && (
                      <tr className="border-t-2 border-slate-400 bg-slate-800">
                        {getColumns().map((col: any) => {
                          if (col.key === 'player.name') {
                            return (
                              <td key={col.key} className="px-4 py-2.5 sticky left-0 bg-slate-800 z-10 border-r border-slate-600">
                                <span className="text-[11px] font-black uppercase tracking-widest text-white/70">Team</span>
                              </td>
                            );
                          }
                          const val = col.render ? col.render(teamAggRow) : (teamAggRow as any)[col.key];
                          return (
                            <td key={col.key} className={`px-4 py-2.5 text-center text-white font-bold text-sm ${col.sectionStart ? 'border-l-2 border-slate-500' : ''} ${col.highlight ? 'bg-slate-700' : ''}`}>
                              {val ?? '—'}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Stat Legend ────────────────────────────────────────────────────── */}
      {!isEditing && statsViewMode === 'player' && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-400 overflow-hidden">
          {/* Compact inline key — always visible */}
          <div className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-200">
            <span className="font-bold text-slate-500 shrink-0 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Stat Key:</span>
            {[
              ['K','Kills'], ['E','Attack Errors'], ['ATT','Total Attacks'], ['H %','Hitting %'],
              ['ACE','Aces'], ['SVE','Serve Errors'], ['S ATT','Serve Attempts'],
              ['BLK','Blocks'], ['BS','Block Solo'], ['BA','Block Assist'],
              ['D','Digs'], ['AST','Assists'],
              ['SR+','Perfect Pass'], ['SR~','Good Pass'], ['SR-','Poor Pass'], ['SR Rtg','Pass Rating (0–3)'],
            ].map(([abbr, full]) => (
              <span key={abbr} className="whitespace-nowrap">
                <span className="font-bold text-slate-500">{abbr}</span>
                <span className="text-slate-300 mx-0.5">·</span>
                <span>{full}</span>
              </span>
            ))}
          </div>
          {/* Expandable full definitions for active category */}
          <div className="px-4 py-2">
            <button onClick={() => setShowDefinitions(!showDefinitions)} className="flex items-center gap-1.5 font-semibold text-slate-500 hover:text-slate-700 transition-colors">
              <span>Full definitions — {activeCategory}</span>
              {showDefinitions ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showDefinitions && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5 mt-2 pt-2 border-t border-slate-200 animate-fade-in-down">
                {(CATEGORY_KEYS[activeCategory] || []).map(key => {
                  const displayKey = key.replace('_SERVE', '').replace('_SET', '');
                  return (
                    <div key={key} className="flex gap-2">
                      <span className="font-bold w-14 shrink-0 text-slate-500">{displayKey}</span>
                      <span className="text-slate-400">{STAT_DEFINITIONS[key]}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── Save Confirmation Modal ────────────────────────────────────────── */}
      {showSaveConfirm && (() => {
        const editingRecord = statsMode === 'edit' && editingRecordId
          ? matchRecords.find(r => r.id === editingRecordId)
          : null;
        const rotationalStatTypes = ['rotation_point', 'break_point', 'server_point'];
        const existingRotational = editingRecord
          ? (editingRecord.gameState.stats ?? []).filter(s => rotationalStatTypes.includes(s.statType))
          : [];
        const hasRotational = existingRotational.length > 0;
        const canSave = !hasRotational || keepRotationalStats !== null;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="p-5 border-b border-slate-200">
                <h3 className="text-base font-bold text-slate-900">Save Stats?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {statsMode === 'edit'
                    ? 'This will permanently replace the existing stats. This action cannot be undone.'
                    : 'This will save these stats permanently. This action cannot be undone.'}
                </p>
              </div>
              {hasRotational && (
                <div className="p-4 border-b border-slate-200 bg-amber-50">
                  <p className="text-sm font-semibold text-amber-800 mb-1">⚠️ Rotational stats detected</p>
                  <p className="text-xs text-amber-700 mb-3">
                    This game has rotation, break point, and serve point data recorded by Game Time. Manual edits may make those stats inaccurate.
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setKeepRotationalStats(true)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${keepRotationalStats === true ? 'bg-blue-600 border-blue-600 text-white font-semibold' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                    >
                      <span className="font-medium">Keep rotational stats</span>
                      <span className={`block text-xs mt-0.5 ${keepRotationalStats === true ? 'text-blue-100' : 'text-slate-400'}`}>Rotation % and sideout data remain, but may be off</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setKeepRotationalStats(false)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${keepRotationalStats === false ? 'bg-red-600 border-red-600 text-white font-semibold' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                    >
                      <span className="font-medium">Remove rotational stats</span>
                      <span className={`block text-xs mt-0.5 ${keepRotationalStats === false ? 'text-red-100' : 'text-slate-400'}`}>Rotation %, sideout, and break point data will be cleared</span>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-3 p-4">
                <button
                  onClick={() => { setShowSaveConfirm(false); setKeepRotationalStats(null); }}
                  className="flex-1 py-2 rounded-lg border border-slate-200 text-slate-600 font-medium text-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={!canSave}
                  onClick={() => { setShowSaveConfirm(false); handleSaveStats(); }}
                  className={`flex-1 py-2 rounded-lg font-bold text-sm transition-colors ${canSave ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                >
                  Save Stats
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Leave Without Saving Confirmation Modal ───────────────────────── */}
      {pendingNavView !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900">Unsaved Changes</h3>
              <p className="text-sm text-slate-500 mt-1">
                You have stat edits that haven't been saved yet. What would you like to do?
              </p>
            </div>
            <div className="flex flex-col gap-2 p-4">
              <button
                onClick={() => {
                  const target = pendingNavView;
                  setPendingNavView(null);
                  handleSaveStats(() => changeView?.(target));
                }}
                className="w-full py-2.5 rounded-lg bg-green-600 text-white font-bold text-sm hover:bg-green-700 transition-colors"
              >
                Save & Leave
              </button>
              <button
                onClick={() => {
                  const target = pendingNavView;
                  setPendingNavView(null);
                  handleCancelEdit();
                  changeView?.(target);
                }}
                className="w-full py-2.5 rounded-lg border border-red-200 text-red-600 font-medium text-sm hover:bg-red-50 transition-colors"
              >
                Discard Changes & Leave
              </button>
              <button
                onClick={() => setPendingNavView(null)}
                className="w-full py-2.5 rounded-lg text-slate-500 font-medium text-sm hover:bg-slate-100 transition-colors"
              >
                Stay & Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Stat Timeline Modal */}
      {teamStatTimeline && (() => {
        const tl = teamStatTimeline;
        const chartData = perGameMetrics
          .filter(d => d[tl.key] !== null)
          .map(d => ({ label: d.shortLabel, fullLabel: d.fullLabel, value: d[tl.key] as number }));
        const allVals = chartData.map(d => d.value);
        const dataMin = allVals.length ? Math.min(...allVals) : 0;
        const dataMax = allVals.length ? Math.max(...allVals) : 1;
        const domainMin = Math.min(tl.yDomain[0], dataMin - (dataMax - dataMin) * 0.15);
        const domainMax = Math.max(tl.yDomain[1], dataMax + (dataMax - dataMin) * 0.15);
        const dotColor = (v: number) =>
          v >= tl.green ? '#16a34a' : v >= tl.amber ? '#d97706' : '#dc2626';
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setTeamStatTimeline(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  {tl.label} — Season Trend
                </h2>
                <button onClick={() => setTeamStatTimeline(null)} className="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5">
                {chartData.length < 2 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <Activity className="w-8 h-8 mb-2" />
                    <p className="text-sm">Not enough data yet — play more games to see the trend.</p>
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                        <YAxis
                          domain={[domainMin, domainMax]}
                          tickFormatter={tl.yTickFormat}
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          width={48}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload as { fullLabel: string; value: number };
                            return (
                              <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                <div className="font-semibold text-slate-700">{d.fullLabel}</div>
                                <div className="font-bold mt-0.5" style={{ color: dotColor(d.value) }}>{tl.format(d.value)}</div>
                              </div>
                            );
                          }}
                        />
                        <ReferenceLine y={tl.green} stroke="#16a34a" strokeDasharray="4 3" strokeWidth={1.5}
                          label={{ value: `Target ${tl.format(tl.green)}`, position: 'insideTopRight', fontSize: 10, fill: '#16a34a' }} />
                        <ReferenceLine y={tl.amber} stroke="#d97706" strokeDasharray="4 3" strokeWidth={1.5}
                          label={{ value: tl.format(tl.amber), position: 'insideBottomRight', fontSize: 10, fill: '#d97706' }} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={(props: any) => {
                            const { cx, cy, payload } = props;
                            return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={5} fill={dotColor(payload.value)} stroke="#fff" strokeWidth={2} />;
                          }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 justify-end">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-600 inline-block" />Above target</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block" />Near target</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" />Below target</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Record Diff Viewer */}
      {diffViewerRecords && (
        <RecordDiffViewer
          recordA={diffViewerRecords[0]}
          recordB={diffViewerRecords[1]}
          players={players}
          currentOrgId={currentOrg?.id ?? ''}
          currentUserId={currentUser?.id ?? ''}
          currentUserName={currentUser?.name ?? ''}
          initialSources={diffViewerInitialSources}
          onSaveMerged={async (merged) => {
            if (!selectedEventId || selectedEventId === 'all') return;
            try {
              const mergeDate = formatDate(new Date(), { month: 'numeric', day: 'numeric', year: 'numeric' });
              const newRecord = await insertMatchRecord(currentOrg?.id ?? '', selectedEventId, currentUser?.id ?? '', `Merged · ${mergeDate}`, merged, filteredEvents.find(e => e.id === selectedEventId)?.teamId ?? (selectedTeamId !== 'all' ? selectedTeamId : undefined));
              if (newRecord && setMatchRecords) {
                setMatchRecords(prev => [...prev, newRecord]);
              }
              setDiffViewerRecords(null);
              setDiffViewerInitialSources(undefined);
            } catch (err) {
              console.error('Failed to save merged record', err);
            }
          }}
          onClose={() => { setDiffViewerRecords(null); setDiffViewerInitialSources(undefined); }}
        />
      )}
    </div>
  );
};

export default Stats;
